<template>
  <div
    class="chatContentContainer"
    :class="{
      emailComposerMode: composeMode === 'email',
      emailMarkdownMode: composeMode === 'email' && emailEditorMode === 'markdown'
    }"
  >
    <div class="left">
      <div class="conversationListHeader">
        <strong>{{ conversationListTitle }}</strong>
        <button type="button" title="新建群聊" @click="openCreateGroupPanel">
          <i class="el-icon-plus"></i>
          群聊
        </button>
      </div>
      <div class="conversationList">
        <div
          v-for="item in visibleChatList"
          :key="item._id.$id"
          class="conversationItem"
          @click="startChat(item)"
          :class="{ selected: currentConversationId === item._id.$id }"
        >
          <div class="name">
            <i v-if="isGroupConversation(item)" class="el-icon-user-solid"></i>
            {{ getConversationName(item) }}
            <span
              v-if="!isGroupConversation(item)"
              class="conversationPresenceText"
              :class="presenceTextClass(conversationPeerId(item))"
            >
              {{ conversationPresenceLabel(item) }}
            </span>
          </div>
          <div class="currentMessage">
            {{ item.currentMessage }}
          </div>
          <div class="updatetime">{{ formateTimeLabel(item.update_time) }}</div>
          <div class="statuscircle" v-if="item[`no_read_${user_id}`]"></div>
          <button
            type="button"
            class="conversationPinButton"
            :class="{ pinned: isConversationPinned(item) }"
            :title="isConversationPinned(item) ? '取消置顶会话' : '置顶会话'"
            @click.stop="toggleConversationPinned(item)"
          >
            <i class="el-icon-s-flag"></i>
          </button>
        </div>
        <div v-if="!visibleChatList.length" class="conversationListEmpty">
          <i class="el-icon-user-solid"></i>
          <strong>{{ conversationListEmptyTitle }}</strong>
          <span>{{ conversationListEmptyDescription }}</span>
        </div>
      </div>
    </div>
    <div class="right">
      <div class="rightInfo">
        <span class="conversationIdentity">
          <span class="conversationTitle">
            <button
              v-if="activePeerId"
              type="button"
              class="conversationTitleButton"
              title="查看人员档案"
              @click="openActivePeerProfile"
            >
              {{ getConversationName(currentConversation) }}
            </button>
            <span v-else>{{ getConversationName(currentConversation) }}</span>
            <span
              v-if="showPeerPresence"
              class="peerPresence"
              :class="peerPresenceClass"
              :title="peerPresenceTitle"
            >
              <i></i>{{ peerPresenceLabel }}
            </span>
            <button
              v-else-if="isCurrentGroup"
              type="button"
              class="groupMemberCount"
              :title="groupPresenceTitle"
              @click="openGroupMembersPanel"
            >
              {{ groupMemberCount }} 位成员 · {{ groupOnlineCount }} 在线
              <i class="el-icon-arrow-right"></i>
            </button>
            <button
              type="button"
              class="socketState"
              :class="socketState"
              :title="socketState === 'authRequired' ? '点击重新登录' : socketStateLabel"
              @click="socketState === 'authRequired' && openReauthDialog()"
            >
              <i></i>{{ socketStateLabel }}
            </button>
          </span>
          <span
            v-if="activePeerEmail"
            class="conversationEmail"
            :title="activePeerEmail"
          >
            {{ activePeerEmail }}
          </span>
          <span
            v-else-if="isCurrentGroup && groupMemberSummary"
            class="conversationEmail"
            :title="groupMemberSummary"
          >
            {{ groupMemberSummary }}
          </span>
        </span>
        <span class="headerActions">
          <button
            type="button"
            class="conversationTodoButton"
            :disabled="!currentConversationId"
            title="查看本会话创建的相关任务"
            @click="openConversationTodos"
          >
            <i class="el-icon-s-claim"></i>
            相关任务
          </button>
          <button
            type="button"
            class="conversationFileButton"
            :disabled="!currentConversationId"
            title="查看当前会话发送过的文件"
            @click="openConversationFiles"
          >
            <i class="el-icon-folder-opened"></i>
            文件列表
          </button>
          <button
            type="button"
            class="historySearchToggleButton"
            :disabled="!currentConversationId"
            title="搜索当前会话聊天记录"
            @click="openConversationHistorySearch"
          >
            <i class="el-icon-search"></i>
            搜索记录
          </button>
          <button
            v-if="isCurrentGroup"
            type="button"
            class="groupInviteButton"
            title="添加群成员"
            @click="openInviteGroupPanel"
          >
            <i class="el-icon-user-solid"></i>
            添加成员
          </button>
        </span>
      </div>
      <div class="messageContentArea">
        <div class="rightContent" ref="messageFlow" @scroll="onMessageFlowScroll">
        <div v-if="messageListLoading && !messageList.length" class="messageHistoryLoading">
          正在加载消息...
        </div>
        <div v-if="olderHistoryLoading" class="olderHistoryLoading">
          <i class="el-icon-loading"></i>
          加载更早消息...
        </div>
        <div
          v-for="(item, index) in messageList"
          :key="messageKey(item, index)"
          ref="messageItems"
          class="contentItem"
          :class="{
            textRight: item.user_id === user_id,
            isMultiSelecting: multiSelectMode,
            isQuoteFocused: focusedMessageId === messageId(item)
          }"
        >
          <button
            v-if="multiSelectMode && isSelectableMessage(item)"
            type="button"
            class="multiSelectMessageToggle"
            :class="{ selected: isMessageSelected(item) }"
            :title="isMessageSelected(item) ? '取消选择消息' : '选择消息'"
            @click.stop="toggleMessageSelection(item)"
          >
            <i :class="isMessageSelected(item) ? 'el-icon-check' : ''"></i>
          </button>
          <div v-if="isSystemMessage(item)" class="systemMessage">
            <span>{{ item.content }}</span>
          </div>
          <template v-else>
          <div class="showtime" :class="{ groupMessageMeta: isCurrentGroup }">
            <template v-if="isCurrentGroup">
              <button
                type="button"
                class="messageSenderAvatar"
                :title="messageSenderTitle(item)"
                @click.stop="openMessageSenderProfile(item)"
              >
                <span
                  v-if="isCurrentGroup"
                  class="presenceDot avatarPresenceDot"
                  :class="presenceDotClass(item.user_id)"
                  :title="presenceLabel(item.user_id)"
                ></span>
                {{ messageSenderInitial(item) }}
              </button>
              <button
                type="button"
                class="messageSenderIdentity"
                :title="messageSenderTitle(item)"
                @click.stop="openMessageSenderProfile(item)"
              >
                <strong>{{ messageSenderName(item) }}</strong>
              </button>
            </template>
            <span class="messageTime">{{ $dayjs(item.create_time * 1000).format("YYYY-MM-DD HH:mm") }}</span>
            <span v-if="item.edited" class="messageEdited">已编辑</span>
          </div>
          <div class="messageBody">
            <div class="messageContentStack">
              <el-tooltip
                v-if="messageQuoteSummary(item)"
                placement="top-start"
                effect="dark"
                :open-delay="250"
                popper-class="messageQuoteTooltip"
              >
                <div slot="content" class="messageQuoteTooltipContent">
                  <strong>{{ messageQuoteTooltipMeta(item) }}</strong>
                  <span>{{ messageQuoteTooltipContent(item) }}</span>
                </div>
                <button
                  type="button"
                  class="messageQuoteInFeed"
                  aria-label="定位引用消息"
                  @mouseenter="preloadQuoteDetails(item)"
                  @focus="preloadQuoteDetails(item)"
                  @click.stop="focusQuotedMessage(item)"
                >
                  <strong>{{ messageQuoteSenderLabel(item) }}</strong>
                  <span>{{ messageQuoteSummary(item) }}</span>
                </button>
              </el-tooltip>
              <img
                v-if="messageType(item) === 'image'"
                class="messageImage"
                :src="item.image_data"
                alt="聊天图片"
                loading="lazy"
                title="点击预览图片"
                @click="openImagePreview(item.image_data)"
              />
              <a
                v-else-if="messageType(item) === 'file'"
                class="messageFile"
                :href="item.file_url"
                :download="item.file_name || '下载文件'"
                :aria-busy="isMessageFileDownloading(item)"
                title="下载文件"
                @click.prevent="downloadMessageFile(item)"
              >
                <i class="el-icon-document fileMessageIcon"></i>
                <span class="fileInfo">
                  <strong>{{ item.file_name || "未命名文件" }}</strong>
                  <em>文件附件</em>
                  <small>{{ formatFileSize(item.file_size) }}</small>
                </span>
                <i
                  class="fileDownloadIcon"
                  :class="isMessageFileDownloading(item) ? 'el-icon-loading' : 'el-icon-download'"
                ></i>
              </a>
              <div
                v-else-if="isEmailMessage(item)"
                class="contentItem1 emailMessage"
                v-html="renderMessageContent(item)"
              ></div>
              <div v-else-if="isAIMessage(item)" class="contentItem1 aiMessage">
                <div class="aiMessageTag">
                  <i :class="isAIMessageStreaming(item) ? 'el-icon-loading' : 'el-icon-magic-stick'"></i>
                  <span>{{ isAIMessageStreaming(item) ? 'AI 生成中 · ' + aiMessageModelLabel(item) : 'AI 回复 · ' + aiMessageModelLabel(item) }}</span>
                  <span v-if="aiMessageAskerLabel(item)" class="aiMessageAsker">{{ aiMessageAskerLabel(item) }}</span>
                </div>
                <div class="aiMessageBody">
                  <div
                    class="markdownContent"
                    v-html="renderMessageContent(item)"
                  ></div>
                  <span v-if="isAIMessageStreaming(item)" class="aiStreamingCursor"></span>
                </div>
              </div>
              <div
                v-else
                class="contentItem1 markdownMessage"
                v-html="renderMessageContent(item)"
              ></div>
              <button
                v-if="messageLinkPreview(item)"
                type="button"
                class="messageLinkPreview"
                title="在浏览器打开链接"
                @click.stop="openExternalLink(messageLinkPreview(item).url)"
              >
                <img
                  v-if="messageLinkPreview(item).image"
                  :src="messageLinkPreview(item).image"
                  alt=""
                  loading="lazy"
                >
                <span class="linkPreviewInfo">
                  <strong>{{ linkPreviewTitle(messageLinkPreview(item)) }}</strong>
                  <small v-if="messageLinkPreview(item).description">{{ messageLinkPreview(item).description }}</small>
                  <em>{{ linkPreviewHost(messageLinkPreview(item)) }}</em>
                </span>
                <i class="el-icon-top-right linkPreviewIcon"></i>
              </button>
              <button
                v-if="messageTodoId(item)"
                type="button"
                class="messageTodoLink"
                :title="messageTodoTitle(item)"
                @click.stop="openLinkedTodo(item)"
              >
                <i class="el-icon-check"></i>
                已创建任务并关联
              </button>
              <div class="messageActions">
              <div class="messageReactionRow">
                <div
                  v-if="canReactToMessage(item)"
                  class="messageReactionPicker"
                  aria-label="回复表情"
                >
                  <button
                    v-for="reaction in messageReactionOptions"
                    :key="reaction.key"
                    type="button"
                    class="messageReactionButton"
                    :class="{ active: userReactionKey(item) === reaction.key }"
                    :title="reaction.label"
                    @click.stop="toggleMessageReaction(item, reaction)"
                  >
                    {{ reaction.emoji }}
                  </button>
                </div>
              </div>
              <div class="messageActionRow">
                <button
                  v-if="isSelectableMessage(item)"
                  type="button"
                  class="messageActionButton"
                  title="复制完整消息"
                  @click.stop="copyMessage(item)"
                >
                  <i class="el-icon-document-copy"></i>
                  <span>复制</span>
                </button>
                <button
                  v-if="!multiSelectMode && isSelectableMessage(item)"
                  type="button"
                  class="messageActionButton"
                  title="进入多选消息模式"
                  @click.stop="enterMultiSelectMode(item)"
                >
                  <i class="el-icon-finished"></i>
                  <span>多选</span>
                </button>
                <button
                  v-if="canEditMessage(item)"
                  type="button"
                  class="messageActionButton"
                  title="编辑这条消息"
                  @click.stop="openMessageEditDialog(item)"
                >
                  <i class="el-icon-edit"></i>
                  <span>编辑</span>
                </button>
                <button
                  v-if="canQuoteMessage(item)"
                  type="button"
                  class="messageActionButton"
                  title="引用回复这条消息"
                  @click.stop="quoteMessage(item)"
                >
                  <i class="el-icon-chat-line-square"></i>
                  <span>引用</span>
                </button>
                <button
                  v-if="canAskAIMessage(item)"
                  type="button"
                  class="messageActionButton askAIActionButton"
                  title="让 AI 解读这条消息"
                  @click.stop="openAskAIDialog(item)"
                >
                  <i class="el-icon-magic-stick"></i>
                  <span>问AI</span>
                </button>
                <button
                  v-if="canCreateTodoFromMessage(item)"
                  type="button"
                  class="messageActionButton"
                  title="用这条消息创建 TODO"
                  @click.stop="openTodoDialog(item)"
                >
                  <i class="el-icon-plus"></i>
                  <span>TODO</span>
                </button>
              </div>
              </div>
            </div>
          </div>
          <div v-if="messageReactionBadges(item).length" class="messageReactionBadges">
            <button
              v-for="reaction in messageReactionBadges(item)"
              :key="reaction.key"
              type="button"
              class="messageReactionBadge"
              :class="{ active: reaction.reactedByMe }"
              :title="reaction.title"
              @click.stop="toggleMessageReaction(item, reaction)"
            >
              <span>{{ reaction.emoji }}</span>
              <span v-if="reaction.count > 1">{{ reaction.count }}</span>
            </button>
          </div>
          <el-tooltip
            v-if="shouldShowMessageReceipt(item)"
            placement="top-end"
            effect="dark"
            :open-delay="200"
            popper-class="messageReceiptTooltip"
          >
            <div slot="content" class="messageReceiptTooltipContent">
              <section>
                <strong>已读 {{ messageReceiptReadMembers(item).length }} 人</strong>
                <span v-if="messageReceiptReadMembers(item).length">
                  <template v-for="(member, memberIndex) in messageReceiptReadMembers(item)">
                    <span :key="member.id">
                      {{ member.name }}<small v-if="member.readAt">（{{ formatMessageReceiptReadTime(member.readAt) }}）</small><template v-if="memberIndex < messageReceiptReadMembers(item).length - 1">、</template>
                    </span>
                  </template>
                </span>
                <span v-else>暂无人阅读</span>
              </section>
              <section>
                <strong>未读 {{ messageReceiptUnreadMembers(item).length }} 人</strong>
                <span v-if="messageReceiptUnreadMembers(item).length">
                  {{ messageReceiptUnreadMembers(item).map((member) => member.name).join('、') }}
                </span>
                <span v-else>全部已读</span>
              </section>
            </div>
            <div
              class="messageReceipt"
              :class="{ isRead: messageReceiptIsRead(item) }"
              :aria-label="messageReceiptTitle(item)"
            >
              <i :class="messageReceiptIsRead(item) ? 'el-icon-check' : 'el-icon-time'"></i>
              {{ messageReceiptLabel(item) }}
            </div>
          </el-tooltip>
          </template>
        </div>
        </div>
      </div>
      <div v-if="multiSelectMode" class="multiSelectionPanel">
        <span>已选择 {{ selectedMessageIds.length }} 条消息</span>
        <span class="multiSelectionActions">
          <button type="button" @click="toggleSelectAllMessages">
            {{ allSelectableMessagesSelected ? '取消全选' : '全选' }}
          </button>
          <button
            type="button"
            class="primary"
            :disabled="!selectedMessageIds.length || selectedMessagesEmailSending"
            @click="emailSelectedMessages"
          >
            <i class="el-icon-message"></i>
            {{ selectedMessagesEmailSending ? '发送中...' : '邮件通知对方' }}
          </button>
          <button type="button" class="close" title="退出多选" @click="exitMultiSelectMode">
            <i class="el-icon-close"></i>
          </button>
        </span>
      </div>
      <div v-if="aiPendingCount > 0" class="aiThinkingBar">
        <i class="el-icon-loading"></i>
        <span>AI 正在回复，内容将流式出现在当前对话中...</span>
      </div>
      <div class="inputContainer">
        <div class="toolInput">
          <div class="composeModeSwitch">
            <button
              type="button"
              :class="{ active: composeMode === 'chat' }"
              @click="setComposeMode('chat')"
            >
              聊天
            </button>
            <button
              type="button"
              :class="{ active: composeMode === 'email' }"
              @click="setComposeMode('email')"
            >
              邮件消息
            </button>
          </div>
          <button
            type="button"
            class="toolItem"
            :class="{ disabled: attachmentBusy || sending || !currentConversationId }"
            title="发送图片或文件"
            @click="chooseAttachment"
          >
            <i class="el-icon-paperclip"></i>
          </button>
          <input
            ref="attachmentInput"
            class="attachmentInput"
            type="file"
            multiple
            @change="handleAttachment"
          />
          <span v-if="attachmentBusy" class="uploadStatus">
            {{ attachmentStatus }}
          </span>
          <button
            type="button"
            class="toolItem voiceTool"
            :class="{ active: voiceInputVisible, disabled: composeMode !== 'chat' }"
            :title="composeMode !== 'chat' ? '语音输入仅支持聊天消息模式' : (voiceInputVisible ? '收起语音输入' : '语音输入，识别文字填入输入框')"
            @click="toggleVoiceInput"
          >
            <i class="el-icon-microphone"></i>
          </button>
          <button
            type="button"
            class="toolItem notificationTool"
            :class="{ enabled: notificationEnabled, disabled: !notificationSupported || notificationPermission === 'denied' || !currentConversationId }"
            :title="notificationButtonTitle"
            @click="requestBrowserNotificationPermission"
          >
            <i class="el-icon-bell"></i>
          </button>
          <div v-if="composeMode === 'email'" class="editorModeSwitch">
            <button
              type="button"
              :class="{ active: emailEditorMode === 'markdown' }"
              @click="setEmailEditorMode('markdown')"
            >
              Markdown
            </button>
            <button
              type="button"
              :class="{ active: emailEditorMode === 'rich' }"
              @click="setEmailEditorMode('rich')"
            >
              富文本
            </button>
          </div>
        </div>
        <div
          class="inputContent"
          :class="{ hasImageDraft: pendingImages.length, hasFileDraft: pendingFiles.length, hasAttachmentDraft: hasPendingAttachments, hasQuoteDraft: quotedMessage, hasVoiceInput: voiceInputVisible, isDragOver: composerDragActive }"
          @dragenter.prevent="handleComposerDragEnter"
          @dragover.prevent="handleComposerDragOver"
          @dragleave.prevent="handleComposerDragLeave"
          @drop.prevent="handleComposerDrop"
        >
          <div v-if="composerDragActive" class="composerDropOverlay">
            <i class="el-icon-upload"></i>
            <span>松开添加附件</span>
          </div>
          <div v-if="voiceInputVisible" class="voiceInputBar">
            <i class="el-icon-microphone voiceInputIcon"></i>
            <Translate
              ref="voiceTranslate"
              class="voiceTranslate"
              :need-translate="false"
              start-btn-text="开始语音输入"
              @addText="appendVoiceText"
            />
            <span class="voiceInputHint">识别文字会自动填入输入框，可编辑后按原流程发送</span>
            <button
              type="button"
              class="removeVoiceInput"
              title="收起语音输入"
              @click="voiceInputVisible = false"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div v-if="quotedMessage" class="quoteDraft">
            <span class="quoteDraftBar"></span>
            <span class="quoteDraftInfo">
              <strong>{{ quoteDraftSenderLabel }}</strong>
              <small>{{ quoteDraftSummary }}</small>
            </span>
            <button
              type="button"
              class="removeQuoteDraft"
              title="取消引用"
              @click="clearQuotedMessage"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div v-if="hasPendingAttachments" class="attachmentDraftList">
            <div v-if="pendingImages.length" class="imageDraftList">
              <div
                v-for="(image, index) in pendingImages"
                :key="image.draft_id"
                class="imageDraft"
              >
                <img :src="image.image_data" alt="待发送图片" />
                <button
                  type="button"
                  class="removeImageDraft"
                  title="移除图片"
                  @click="removePendingImage(index)"
                >
                  <i class="el-icon-close"></i>
                </button>
                <span>{{ imageDraftLabel(image, index) }}</span>
              </div>
            </div>
            <div
              v-for="(file, index) in pendingFiles"
              :key="fileDraftKey(file, index)"
              class="fileDraft"
            >
              <i class="el-icon-document fileDraftIcon"></i>
              <span class="fileDraftInfo">
                <strong>{{ file.name || "未命名文件" }}</strong>
                <small>{{ formatFileSize(file.size) }}</small>
              </span>
              <button
                type="button"
                class="removeFileDraft"
                title="移除文件"
                @click="removePendingFile(index)"
              >
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>
          <quill-editor
            v-if="composeMode === 'email'"
            ref="emailEditor"
            class="wechatEmailEditor"
            v-model="emailContentHtml"
            :options="emailEditorOptions"
            @ready="handleEmailEditorReady"
            @paste.native="handleEmailPaste"
            @keydown.native.capture="handleComposerKeydown"
          ></quill-editor>
          <el-input
            v-else
            ref="messageInput"
            type="textarea"
            resize="none"
            :placeholder="composerPlaceholder"
            maxlength="20000"
            v-model="inputContent"
            @paste.native="handlePaste"
            @keydown.native="handleComposerKeydown"
            @input="handleChatInput"
            @blur="deferCloseMentionMenu"
          ></el-input>
          <div
            v-if="mentionMenuVisible"
            class="mentionMenu"
          >
            <button
              v-for="(member, index) in mentionCandidates"
              :key="member.mention_type === 'all' ? 'mention-all' : (member.mention_type === 'ai' ? `mention-ai-${member.token}` : chatUserId(member))"
              type="button"
              class="mentionCandidate"
              :class="{ mentionAllCandidate: member.mention_type === 'all', mentionAiCandidate: member.mention_type === 'ai', active: index === mentionActiveIndex }"
              @mousedown.prevent="selectMentionCandidate(member)"
            >
              <span class="mentionAvatar">
                <span v-if="member.mention_type === 'ai'" class="mentionAiAvatar">
                  <i class="el-icon-cpu"></i>
                </span>
                <template v-else>
                  <span
                    class="presenceDot mentionPresenceDot"
                    v-if="member.mention_type !== 'all'"
                    :class="presenceDotClass(chatUserId(member))"
                  ></span>
                  <i v-if="member.mention_type === 'all'" class="el-icon-user-solid"></i>
                  <template v-else>{{ chatUserInitial(member) }}</template>
                </template>
              </span>
              <span class="mentionInfo">
                <strong>{{ member.mention_type === 'ai' ? `@${member.token}` : chatUserName(member) }}</strong>
                <small>{{ member.mention_type === 'ai' ? member.description : (member.mention_type === 'all' ? `通知全部 ${groupMemberCount} 位成员` : presenceLabel(chatUserId(member))) }}</small>
              </span>
            </button>
            <div v-if="!mentionCandidates.length" class="mentionEmpty">
              没有匹配的成员或 AI
            </div>
          </div>
          <div class="composerHint">{{ keyboardHint }}</div>
          <div
            class="sendBtn"
            @click="sendMessage"
            :class="{ disabled: !canSend }"
          >
            {{ sendButtonLabel }}
          </div>
        </div>
      </div>
    </div>
    <transition name="groupPanelFade">
      <div
        v-if="groupPanelOpen"
        class="groupPanelBackdrop"
        @click.self="closeGroupPanel"
      >
        <aside class="groupPanel" role="dialog" aria-modal="true" :aria-label="groupPanelTitle">
          <div class="groupPanelHeader">
            <span>
              <strong>{{ groupPanelTitle }}</strong>
              <small>{{ groupPanelDescription }}</small>
            </span>
            <button type="button" title="关闭" @click="closeGroupPanel">
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div class="groupPanelBody">
            <label v-if="groupPanelMode === 'create'" class="groupNameField">
              <span>群名称</span>
              <input
                v-model.trim="groupName"
                type="text"
                maxlength="50"
                placeholder="例如：市场项目协作群"
              />
            </label>
            <label class="groupSearchField">
              <i class="el-icon-search"></i>
              <input
                v-model.trim="groupMemberSearch"
                type="search"
                :placeholder="groupPanelMode === 'members' ? '搜索群成员' : '搜索姓名或邮箱'"
              />
            </label>
            <div class="groupSelectionSummary">
              <template v-if="groupPanelMode === 'members'">
                <span>共 {{ groupMemberCount }} 位成员 · {{ groupOnlineCount }} 在线</span>
                <span v-if="groupOwnerName">群主：{{ groupOwnerName }}</span>
              </template>
              <template v-else>
                <span>已选择 {{ selectedGroupMemberIds.length }} 人</span>
                <span v-if="groupPanelMode === 'invite'">当前共 {{ groupMemberCount }} 人</span>
                <span v-else>建群至少选择 2 位同事</span>
              </template>
            </div>
            <div v-if="groupPanelMode === 'members'" class="groupCandidateList">
              <button
                v-for="user in filteredGroupMembers"
                :key="chatUserId(user)"
                class="groupCandidate groupMemberReadOnly"
                type="button"
                @click="openUserProfile(user)"
              >
                <span class="groupCandidateAvatar">
                  <span
                    class="presenceDot groupCandidatePresenceDot"
                    :class="presenceDotClass(chatUserId(user))"
                    :title="presenceLabel(chatUserId(user))"
                  ></span>
                  {{ chatUserInitial(user) }}
                </span>
                <span class="groupCandidateInfo">
                  <strong>
                    {{ chatUserName(user) }}
                    <span class="groupMemberPresenceText" :class="presenceTextClass(chatUserId(user))">
                      {{ presenceLabel(chatUserId(user)) }}
                    </span>
                  </strong>
                  <small>{{ userProfileSubtitle(user) }}</small>
                </span>
                <span v-if="groupMemberRoleLabel(user)" class="groupMemberRole">
                  {{ groupMemberRoleLabel(user) }}
                </span>
              </button>
              <div v-if="!filteredGroupMembers.length" class="groupCandidateEmpty">
                {{ groupMemberSearch ? "没有匹配的群成员" : "暂无群成员信息" }}
              </div>
            </div>
            <div v-else class="groupCandidateList">
              <button
                v-for="user in filteredGroupCandidates"
                :key="chatUserId(user)"
                type="button"
                class="groupCandidate"
                :class="{ selected: isGroupMemberSelected(chatUserId(user)) }"
                role="checkbox"
                :aria-checked="isGroupMemberSelected(chatUserId(user)) ? 'true' : 'false'"
                @click="toggleGroupMember(chatUserId(user))"
              >
                <span class="groupCandidateAvatar">{{ chatUserInitial(user) }}</span>
                <span class="groupCandidateInfo">
                  <strong>{{ chatUserName(user) }}</strong>
                  <small>{{ chatUserEmail(user) }}</small>
                </span>
                <i class="el-icon-check groupCandidateCheck"></i>
              </button>
              <div v-if="!filteredGroupCandidates.length" class="groupCandidateEmpty">
                {{ groupMemberSearch ? "没有匹配的同事" : "暂无可添加的同事" }}
              </div>
            </div>
          </div>
          <div class="groupPanelFooter">
            <button
              type="button"
              class="secondary"
              @click="closeGroupPanel"
            >
              {{ groupPanelMode === "members" ? "关闭" : "取消" }}
            </button>
            <button
              v-if="groupPanelMode !== 'members'"
              type="button"
              class="primary"
              :disabled="!canSubmitGroupPanel || groupActionLoading"
              @click="submitGroupPanel"
            >
              {{ groupActionLoading ? "处理中..." : groupPanelSubmitLabel }}
            </button>
          </div>
        </aside>
      </div>
    </transition>
    <transition name="imagePreviewFade">
      <div
        v-if="previewImage"
        class="imagePreviewOverlay"
        role="dialog"
        aria-modal="true"
        aria-label="图片预览"
        @click.self="closeImagePreview"
      >
        <button
          type="button"
          class="imagePreviewClose"
          title="关闭预览"
          aria-label="关闭图片预览"
          @click="closeImagePreview"
        >
          <i class="el-icon-close"></i>
        </button>
        <img :src="previewImage" alt="聊天图片预览" @click.stop />
      </div>
    </transition>
    <el-dialog
      title="从消息创建 TODO"
      :visible.sync="todoDialogVisible"
      width="460px"
      custom-class="messageTodoDialog"
      :close-on-click-modal="!todoCreating"
      @opened="focusTodoInput"
    >
      <el-form label-width="76px">
        <el-form-item label="内容">
          <el-input
            ref="todoContentInput"
            class="todoContentInput"
            type="textarea"
            resize="vertical"
            :rows="6"
            maxlength="20000"
            show-word-limit
            placeholder="可以修改任务内容"
            v-model="todoDraft.content"
          ></el-input>
        </el-form-item>
        <el-form-item label="负责人">
          <el-select
            v-model="todoDraft.ownerId"
            placeholder="选择负责人"
            style="width: 100%"
            @change="normalizeTodoDraftCollaborators"
          >
            <el-option
              v-for="user in todoOwnerOptions"
              :key="user.id"
              :label="user.label"
              :value="user.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="协作人">
          <el-select
            v-model="todoDraft.collaboratorIds"
            multiple
            collapse-tags
            placeholder="选择协作人（可多选）"
            style="width: 100%"
            @change="normalizeTodoDraftCollaborators"
          >
            <el-option
              v-for="user in todoCollaboratorOptions"
              :key="user.id"
              :label="user.label"
              :value="user.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="todoDraft.labels"
            class="todoLabelSelect"
            multiple
            filterable
            allow-create
            default-first-option
            :loading="todoLabelLoading"
            placeholder="选择或输入标签后回车"
            style="width: 100%"
            @change="normalizeTodoDraftLabels"
          >
            <el-option
              v-for="label in todoLabelOptions"
              :key="label"
              :label="`#${label}`"
              :value="label"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="List">
          <span class="todoDefaultListName">{{ todoDefaultListName }}</span>
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button :disabled="todoCreating" @click="todoDialogVisible = false">
          取消
        </el-button>
        <el-button
          type="primary"
          :loading="todoCreating"
          @click="createTodoFromMessage"
        >
          创建 TODO
        </el-button>
      </template>
    </el-dialog>
    <el-dialog
      title="编辑消息"
      :visible.sync="messageEditDialogVisible"
      width="460px"
      custom-class="messageEditDialog"
      :close-on-click-modal="!messageEditSubmitting"
      @opened="focusMessageEditInput"
    >
      <el-input
        ref="messageEditInput"
        v-model="messageEditContent"
        type="textarea"
        resize="vertical"
        :rows="6"
        maxlength="20000"
        show-word-limit
        placeholder="输入修改后的消息"
      ></el-input>
      <p class="messageEditHint">保存后会保留原内容的历史版本，并通知会话中的其他成员。</p>
      <template slot="footer">
        <el-button :disabled="messageEditSubmitting" @click="messageEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="messageEditSubmitting" @click="submitMessageEdit">保存修改</el-button>
      </template>
    </el-dialog>
    <el-dialog
      title="选择邮件接收人"
      :visible.sync="selectedEmailRecipientDialogVisible"
      width="460px"
      custom-class="selectedEmailRecipientDialog"
      :close-on-click-modal="!selectedMessagesEmailSending"
    >
      <el-select
        v-model="selectedEmailRecipientIds"
        multiple
        filterable
        collapse-tags
        placeholder="选择群成员"
        style="width: 100%"
      >
        <el-option
          v-for="user in selectedEmailRecipientOptions"
          :key="user.id"
          :label="user.label"
          :value="user.id"
        ></el-option>
      </el-select>
      <template slot="footer">
        <el-button :disabled="selectedMessagesEmailSending" @click="selectedEmailRecipientDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="selectedMessagesEmailSending"
          :disabled="!selectedEmailRecipientIds.length"
          @click="submitSelectedMessagesEmailRecipients"
        >
          发送邮件
        </el-button>
      </template>
    </el-dialog>
    <el-dialog
      title="关联任务"
      :visible.sync="linkedTodoDialogVisible"
      width="460px"
      custom-class="linkedTodoDialog"
    >
      <div v-if="linkedTodoLoading" class="linkedTodoLoading">
        <i class="el-icon-loading"></i> 正在加载任务详情
      </div>
      <div v-else-if="linkedTodo" class="linkedTodoDetails">
        <div class="linkedTodoStatusRow">
          <div class="linkedTodoStatus" :class="{ isComplete: linkedTodo.finish_status }">
            {{ linkedTodo.finish_status ? '已完成' : '待处理' }}
          </div>
          <button
            type="button"
            class="linkedTodoStatusButton"
            :disabled="isConversationTodoStatusUpdating(linkedTodo)"
            @click="toggleConversationTodoStatus(linkedTodo)"
          >
            <i :class="isConversationTodoStatusUpdating(linkedTodo) ? 'el-icon-loading' : (linkedTodo.finish_status ? 'el-icon-refresh-left' : 'el-icon-check')"></i>
            {{ linkedTodo.finish_status ? '重新打开' : '标记完成' }}
          </button>
        </div>
        <p class="linkedTodoContent">{{ linkedTodo.todo_content || '未填写任务内容' }}</p>
        <dl>
          <dt>负责人</dt>
          <dd>{{ linkedTodoOwnerLabel }}</dd>
          <dt>标签</dt>
          <dd>
            <span v-if="linkedTodoLabels.length" class="linkedTodoLabels">
              <span v-for="label in linkedTodoLabels" :key="label">#{{ label }}</span>
            </span>
            <span v-else>无</span>
          </dd>
          <dt>创建时间</dt>
          <dd>{{ linkedTodoCreateTime }}</dd>
          <dt>任务 ID</dt>
          <dd class="linkedTodoId">{{ linkedTodoId }}</dd>
        </dl>
      </div>
      <div v-else class="linkedTodoMissing">任务不存在、已被删除，或你无权查看。</div>
      <template slot="footer">
        <el-button @click="linkedTodoDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
    <transition name="conversationTodoDrawerFade">
      <aside v-if="conversationTodosVisible" class="conversationTodoDrawer" aria-label="相关任务">
        <header>
          <span>
            <strong>相关任务</strong>
            <small>{{ getConversationName(currentConversation) }}</small>
          </span>
          <button type="button" title="关闭相关任务" @click="conversationTodosVisible = false">
            <i class="el-icon-close"></i>
          </button>
        </header>
        <div class="conversationTodoDrawerBody">
          <div v-if="conversationTodosLoading" class="conversationTodosLoading">
            <i class="el-icon-loading"></i> 正在加载相关任务
          </div>
          <div v-else-if="!conversationTodos.length" class="conversationTodosEmpty">
            当前会话还没有创建相关任务。
          </div>
          <div v-else class="conversationTodoList">
            <article v-for="todo in conversationTodos" :key="conversationTodoId(todo)" class="conversationTodoItem">
              <div class="conversationTodoItemHeader">
                <span :class="['conversationTodoStatus', { isComplete: todo.finish_status }]">
                  {{ todo.finish_status ? '已完成' : '待处理' }}
                </span>
                <span class="conversationTodoHeaderActions">
                  <time>{{ conversationTodoTime(todo) }}</time>
                  <button
                    type="button"
                    class="conversationTodoStatusButton"
                    :disabled="isConversationTodoStatusUpdating(todo)"
                    @click="toggleConversationTodoStatus(todo)"
                  >
                    <i :class="isConversationTodoStatusUpdating(todo) ? 'el-icon-loading' : (todo.finish_status ? 'el-icon-refresh-left' : 'el-icon-check')"></i>
                    {{ todo.finish_status ? '重新打开' : '完成' }}
                  </button>
                </span>
              </div>
              <p>{{ todo.todo_content || '未填写任务内容' }}</p>
              <div class="conversationTodoMeta">
                <span>负责人：{{ conversationTodoOwners(todo) }}</span>
                <span v-if="conversationTodoLabels(todo).length">
                  {{ conversationTodoLabels(todo).map((label) => `#${label}`).join(' ') }}
                </span>
              </div>
            </article>
          </div>
        </div>
      </aside>
    </transition>
    <transition name="conversationFileDrawerFade">
      <aside v-if="conversationFilesVisible" class="conversationFileDrawer" aria-label="文件列表">
        <header>
          <span>
            <strong>文件列表</strong>
            <small>{{ getConversationName(currentConversation) }}</small>
          </span>
          <button type="button" title="关闭文件列表" @click="closeConversationFiles">
            <i class="el-icon-close"></i>
          </button>
        </header>
        <div class="conversationFileDrawerBody">
          <div v-if="conversationFilesLoading" class="conversationFilesLoading">
            <i class="el-icon-loading"></i> 正在加载文件列表
          </div>
          <div v-else-if="!conversationFiles.length" class="conversationFilesEmpty">
            当前会话还没有发送过文件。
          </div>
          <div v-else class="conversationFileList">
            <article
              v-for="file in conversationFiles"
              :key="conversationFileId(file)"
              class="conversationFileItem"
            >
              <i class="el-icon-document conversationFileIcon"></i>
              <span class="conversationFileInfo">
                <strong :title="conversationFileName(file)">{{ conversationFileName(file) }}</strong>
                <small>{{ messageSenderName(file) }} · {{ conversationFileTime(file) }} · {{ formatFileSize(file.file_size) }}</small>
              </span>
              <span class="conversationFileActions">
                <button
                  type="button"
                  :disabled="isMessageFileDownloading(file)"
                  title="定位到聊天消息"
                  @click="focusConversationFile(file)"
                >
                  <i class="el-icon-position"></i>
                </button>
                <button
                  type="button"
                  :disabled="isMessageFileDownloading(file)"
                  title="下载文件"
                  @click="downloadMessageFile(file)"
                >
                  <i :class="isMessageFileDownloading(file) ? 'el-icon-loading' : 'el-icon-download'"></i>
                </button>
              </span>
            </article>
          </div>
        </div>
      </aside>
    </transition>
    <transition name="historySearchDrawerFade">
      <aside v-if="historySearchPanelVisible" class="historySearchDrawer" aria-label="搜索聊天记录">
        <header>
          <div class="historySearchDrawerField">
            <i class="el-icon-search"></i>
            <input
              ref="historySearchInput"
              v-model="historySearchKeyword"
              type="search"
              placeholder="搜索聊天记录"
              :disabled="!currentConversationId"
              @keyup.enter="searchConversationHistory"
            />
          </div>
          <button
            type="button"
            class="historySearchSubmitButton"
            :disabled="!currentConversationId || !trimmedHistorySearchKeyword || historySearchLoading"
            title="搜索当前会话"
            @click="searchConversationHistory"
          >
            {{ historySearchLoading ? '搜索中' : '搜索' }}
          </button>
          <button
            type="button"
            class="historySearchDrawerClose"
            title="关闭搜索结果"
            @click="closeConversationHistorySearch"
          >
            <i class="el-icon-close"></i>
          </button>
        </header>
        <div class="historySearchDrawerSummary">
          <strong>搜索结果</strong>
          <small v-if="trimmedHistorySearchKeyword && !historySearchLoading">
            {{ historySearchResults.length }} 条
          </small>
        </div>
        <div class="historySearchPanelBody">
          <div v-if="historySearchLoading" class="historySearchState">
            <i class="el-icon-loading"></i>
            正在搜索
          </div>
          <div v-else-if="!trimmedHistorySearchKeyword" class="historySearchState">
            输入关键词搜索当前会话记录
          </div>
          <div v-else-if="!historySearchResults.length" class="historySearchState">
            没有匹配的聊天记录
          </div>
          <template v-else>
            <button
              v-for="(item, resultIndex) in historySearchResults"
              :key="messageKey(item, resultIndex)"
              type="button"
              class="historySearchResult"
              @click="selectHistorySearchResult(item)"
            >
              <span class="historySearchResultMeta">
                <strong>{{ messageSenderName(item) }}</strong>
                <small>{{ $dayjs(item.create_time * 1000).format("YYYY-MM-DD HH:mm") }}</small>
              </span>
              <span
                class="historySearchResultSnippet"
                v-html="highlightHistorySearchKeyword(historySearchSnippet(item))"
              ></span>
            </button>
          </template>
        </div>
      </aside>
    </transition>
    <el-dialog
      title="人员档案"
      :visible.sync="userProfileVisible"
      width="420px"
      custom-class="userProfileDialog"
    >
      <div v-if="profileUser" class="userProfileCard">
        <div class="userProfileHeader">
          <span class="userProfileAvatar">{{ chatUserInitial(profileUser) }}</span>
          <span>
            <strong>{{ chatUserName(profileUser) }}</strong>
            <small v-if="profileUserRole(profileUser)">{{ profileUserRole(profileUser) }}</small>
          </span>
        </div>
        <dl class="userProfileDetails">
          <template v-for="item in profileUserRows">
            <dt :key="`${item.key}-label`">{{ item.label }}</dt>
            <dd :key="item.key">{{ item.value }}</dd>
          </template>
        </dl>
      </div>
      <div v-else class="userProfileEmpty">暂无人员信息</div>
    </el-dialog>
    <el-dialog
      title="Ask AI"
      :visible.sync="askAIDialogVisible"
      width="480px"
      custom-class="chatAskAIDialog"
      :close-on-click-modal="false"
      @closed="resetAskAIDialog"
    >
      <div v-if="askAITargetMessage" class="askAIQuote">
        <span class="askAIQuoteBar"></span>
        <span class="askAIQuoteInfo">
          <strong>{{ askAIQuoteSenderLabel }}<template v-if="askAIQuoteTime"> · {{ askAIQuoteTime }}</template></strong>
          <small>{{ askAIQuoteSummary }}</small>
        </span>
      </div>
      <el-form label-position="top" @submit.native.prevent="submitAskAI">
        <el-form-item label="模型">
          <el-radio-group v-model="askAIModel">
            <el-radio label="deepseek">DeepSeek · deepseek-v4-flash（默认）</el-radio>
            <el-radio label="openai">OpenAI · gpt-5.5</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="补充追问（可选）">
          <el-input
            v-model="askAIQuestion"
            type="textarea"
            resize="none"
            :rows="3"
            maxlength="2000"
            placeholder="例如：帮我总结这条消息的要点。不填则由 AI 结合聊天记录自动分析"
          />
        </el-form-item>
      </el-form>
      <p class="askAIHint">AI 会结合这条消息与最近的聊天记录回答，回复会以 AI 消息的形式流式出现在当前对话中，参与会话的同事都能看到。</p>
      <template slot="footer">
        <el-button @click="askAIDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!canSubmitAskAI" @click="submitAskAI">发送给 AI</el-button>
      </template>
    </el-dialog>
    <el-dialog
      title="重新登录"
      :visible.sync="reauthDialogVisible"
      width="400px"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      custom-class="chatReauthDialog"
      @closed="resetReauthError"
    >
      <p class="reauthDescription">实时连接已失效。登录成功后将留在当前对话并自动恢复连接。</p>
      <el-form
        v-if="reauthStep === 'password'"
        ref="reauthPasswordForm"
        :model="reauthForm"
        :rules="reauthRules"
        label-position="top"
        @submit.native.prevent="submitReauthentication"
      >
        <el-form-item label="账号" prop="email">
          <el-input ref="reauthEmailInput" v-model.trim="reauthForm.email" autocomplete="username" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="reauthForm.password" type="password" show-password autocomplete="current-password" />
        </el-form-item>
        <el-form-item label="登录类型" prop="employment">
          <el-select v-model="reauthForm.employment" style="width: 100%">
            <el-option value="fulltime" label="全职人员" />
            <el-option value="parttime" label="兼职人员" />
          </el-select>
        </el-form-item>
      </el-form>
      <el-form
        v-else
        ref="reauthMobileForm"
        :model="reauthForm"
        :rules="reauthMobileRules"
        label-position="top"
        @submit.native.prevent="submitMobileVerification"
      >
        <p class="reauthMobileHint">验证码已发送至 {{ reauthMobileMasked }}。</p>
        <el-form-item label="验证码" prop="pin">
          <el-input v-model.trim="reauthForm.pin" autocomplete="one-time-code" />
        </el-form-item>
      </el-form>
      <p v-if="reauthError" class="reauthError" role="alert">{{ reauthError }}</p>
      <template slot="footer">
        <el-button v-if="reauthStep === 'mobile'" :disabled="reauthSubmitting" @click="returnToPasswordLogin">返回</el-button>
        <el-button :loading="reauthSubmitting" type="primary" @click="reauthStep === 'mobile' ? submitMobileVerification() : submitReauthentication()">
          {{ reauthStep === 'mobile' ? '验证并恢复连接' : '登录并恢复连接' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  addGroupConversation,
  addGroupConversationMembers,
  chatSendMessage,
  createChatMessageTodo,
  createChatSocket,
  editChatMessage,
  getChatMessageTodo,
  getConversationChatTodos,
  getConversationFiles,
  getChatLinkMetadata,
  getChatOnlineStatus,
  getChatMessage,
  getChatMessageContext,
  getChatQiniuToken,
  getChatUserList,
  getConversationMessages,
  getUserConversations,
  markChatMessageTodo,
  reactChatMessage,
  readChatConversation,
  repairChatMessageQuote,
  searchConversationMessages,
  setChatConversationPinned,
  sendSelectedChatMessagesEmail,
  talkToSomeone
} from '../../api/chat'
import {
  createLoginPin,
  getLabelsByList,
  getLists,
  loginByEmail,
  updateList,
  verifyLoginPin
} from '../../api/assistant'
import {
  getEmail,
  getNotificationToken,
  getUserId,
  removeNotificationToken,
  setEmail,
  setNotificationToken,
  setUserId,
  setUserName
} from '../../utils/store'
import {
  getNotificationTokenState,
  isNotificationTokenAuthError,
  notificationTokenRefreshDelay,
  renewNotificationToken
} from '../../utils/notificationToken'
import * as qiniu from 'qiniu-js'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import Translate from '../../components/Translate/index.vue'

const CHAT_IMAGE_TARGET_BYTES = 600 * 1024
const CHAT_IMAGE_MAX_DIMENSION = 1280
const QINIU_FILE_BASE_URL = 'https://mainbucket.reachable-edu.com/'
const CHAT_NOTIFICATION_SUBSCRIPTIONS_KEY = 'wechat_notification_subscriptions'
const CHAT_COMPOSER_DRAFTS_KEY = 'wechat_composer_drafts'
const REAUTH_REQUEST_CONFIG = { timeout: 20000 }
const TOKEN_REFRESH_RETRY_MIN_MS = 30000
const TOKEN_REFRESH_RETRY_MAX_MS = 5 * 60 * 1000
const MESSAGE_REACTION_OPTIONS = [
  { key: 'like', emoji: '👍', label: '点赞' },
  { key: 'ok', emoji: '👌', label: 'OK' },
  { key: 'heart', emoji: '❤️', label: '喜欢' },
  { key: 'laugh', emoji: '😂', label: '笑' },
  { key: 'thanks', emoji: '🙏', label: '收到' }
]
const AI_MENTION_CANDIDATES = [
  { mention_type: 'ai', token: 'AI', user_name: 'AI', model: 'deepseek', description: 'AI 助手 · deepseek-v4-flash' },
  { mention_type: 'ai', token: 'DeepSeek', user_name: 'DeepSeek', model: 'deepseek', description: 'AI 助手 · deepseek-v4-flash' },
  { mention_type: 'ai', token: 'OpenAI', user_name: 'OpenAI', model: 'openai', description: 'AI 助手 · gpt-5.5' }
]
const CHAT_AI_STREAM_URL = 'https://web-backend.eae.international/Assistant/ChatAIStream.php'

function getBrowserNotificationPermission () {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return window.Notification.permission
}

function requestBrowserNotificationPermission () {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return Promise.resolve('unsupported')
  }
  const request = window.Notification.requestPermission()
  if (request && typeof request.then === 'function') return request
  return new Promise((resolve) => {
    window.Notification.requestPermission(resolve)
  })
}

function notificationSubscriptionStorageKey (userId) {
  return `${CHAT_NOTIFICATION_SUBSCRIPTIONS_KEY}:${userId || 'anonymous'}`
}

function loadNotificationSubscriptions (userId) {
  if (typeof window === 'undefined' || !window.localStorage) return {}
  try {
    const value = window.localStorage.getItem(notificationSubscriptionStorageKey(userId))
    const parsed = value ? JSON.parse(value) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

function saveNotificationSubscriptions (userId, subscriptions) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(
      notificationSubscriptionStorageKey(userId),
      JSON.stringify(subscriptions || {})
    )
  } catch (error) {}
}

function composerDraftStorageKey (userId) {
  return `${CHAT_COMPOSER_DRAFTS_KEY}:${userId || 'anonymous'}`
}

function loadComposerDrafts (userId) {
  if (typeof window === 'undefined' || !window.localStorage) return {}
  try {
    const value = window.localStorage.getItem(composerDraftStorageKey(userId))
    const parsed = value ? JSON.parse(value) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

function saveComposerDrafts (userId, drafts) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(
      composerDraftStorageKey(userId),
      JSON.stringify(drafts || {})
    )
  } catch (error) {}
}

function escapeHtml (value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function isSafeMarkdownUrl (value, allowImage) {
  const url = String(value || '').trim()
  if (!url || /[\u0000-\u001f\u007f\s]/.test(url)) return false
  if (allowImage) return /^https?:\/\//i.test(url)
  return /^(https?:\/\/|mailto:)/i.test(url)
}

function normalizeDetectedHttpsUrl (value) {
  let url = String(value || '').trim()
  url = url.replace(/[)\]}>,.!?;:'"、，。！？；：]+$/g, '')
  if (!/^https:\/\//i.test(url)) return ''
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' ? parsed.href : ''
  } catch (error) {
    return ''
  }
}

function extractHttpsLinks (value) {
  const matches = String(value || '').match(/https:\/\/[^\s<>"'`]+/gi) || []
  const links = matches
    .map((item) => normalizeDetectedHttpsUrl(item))
    .filter(Boolean)
  return Array.from(new Set(links))
}

function renderMarkdownWithMarked (value) {
  if (
    typeof window === 'undefined' ||
    !window.marked ||
    typeof window.marked.parse !== 'function' ||
    typeof window.marked.Renderer !== 'function'
  ) return ''

  const renderer = new window.marked.Renderer()
  renderer.html = (html) => escapeHtml(html)
  renderer.link = (href, title, text) => {
    if (!isSafeMarkdownUrl(href, false)) return text || ''
    const safeHref = escapeHtml(href)
    const safeTitle = title ? ` title="${escapeHtml(title)}"` : ''
    return `<a href="${safeHref}"${safeTitle} target="_blank" rel="noopener noreferrer">${text || safeHref}</a>`
  }
  renderer.image = (href, title, text) => {
    if (!isSafeMarkdownUrl(href, true)) return escapeHtml(text || href || '')
    const safeHref = escapeHtml(href)
    const safeTitle = title ? ` title="${escapeHtml(title)}"` : ''
    return `<img src="${safeHref}" alt="${escapeHtml(text || '')}"${safeTitle} loading="lazy">`
  }

  return window.marked.parse(String(value || ''), {
    breaks: true,
    gfm: true,
    headerIds: false,
    mangle: false,
    renderer
  })
}

function escapeRegExp (value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function inlineMarkdown (value) {
  return escapeHtml(value)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
}

function renderMarkdown (value) {
  const markedHtml = renderMarkdownWithMarked(value)
  if (markedHtml) return markedHtml

  const lines = String(value || '').replace(/\r\n/g, '\n').split('\n')
  const output = []
  let listType = ''
  let inCode = false
  const closeList = () => {
    if (listType) {
      output.push(`</${listType}>`)
      listType = ''
    }
  }
  lines.forEach((line) => {
    if (/^\s*```/.test(line)) {
      if (inCode) {
        output.push('</code></pre>')
        inCode = false
      } else {
        closeList()
        output.push('<pre><code>')
        inCode = true
      }
      return
    }
    if (inCode) {
      output.push(`${escapeHtml(line)}\n`)
      return
    }
    if (!line.trim()) {
      closeList()
      return
    }
    const heading = line.match(/^(#{1,3})\s+(.+)$/)
    if (heading) {
      closeList()
      const level = heading[1].length
      output.push(`<h${level}>${inlineMarkdown(heading[2])}</h${level}>`)
      return
    }
    const unordered = line.match(/^\s*[-*]\s+(.+)$/)
    if (unordered) {
      if (listType !== 'ul') {
        closeList()
        listType = 'ul'
        output.push('<ul>')
      }
      output.push(`<li>${inlineMarkdown(unordered[1])}</li>`)
      return
    }
    const ordered = line.match(/^\s*\d+\.\s+(.+)$/)
    if (ordered) {
      if (listType !== 'ol') {
        closeList()
        listType = 'ol'
        output.push('<ol>')
      }
      output.push(`<li>${inlineMarkdown(ordered[1])}</li>`)
      return
    }
    const quote = line.match(/^\s*>\s+(.+)$/)
    if (quote) {
      closeList()
      output.push(`<blockquote>${inlineMarkdown(quote[1])}</blockquote>`)
      return
    }
    closeList()
    output.push(`<p>${inlineMarkdown(line)}</p>`)
  })
  closeList()
  if (inCode) output.push('</code></pre>')
  return output.join('')
}

function markdownFromInlineNode (node) {
  if (node.nodeType === 3) return (node.nodeValue || '').replace(/\u00a0/g, ' ')
  if (node.nodeType !== 1) return ''
  const tag = node.tagName.toLowerCase()
  const content = Array.from(node.childNodes).map(markdownFromInlineNode).join('')
  if (tag === 'br') return '\n'
  if (tag === 'strong' || tag === 'b') return content ? `**${content}**` : ''
  if (tag === 'em' || tag === 'i') return content ? `*${content}*` : ''
  if (tag === 'code') return content ? `\`${content}\`` : ''
  if (tag === 'a') {
    const href = node.getAttribute('href') || ''
    return /^(?:https?:\/\/|mailto:)/i.test(href) && content
      ? `[${content}](${href})`
      : content
  }
  return content
}

function markdownFromBlockNode (node) {
  if (node.nodeType === 3) return (node.nodeValue || '').trim()
  if (node.nodeType !== 1) return ''
  const tag = node.tagName.toLowerCase()
  if (/^h[1-3]$/.test(tag)) {
    return `${'#'.repeat(Number(tag.slice(1)))} ${markdownFromInlineNode(node).trim()}`
  }
  if (tag === 'blockquote') return `> ${markdownFromInlineNode(node).trim()}`
  if (tag === 'pre') return `\`\`\`\n${node.textContent || ''}\n\`\`\``
  if (tag === 'ul' || tag === 'ol') {
    return Array.from(node.children)
      .filter((child) => child.tagName.toLowerCase() === 'li')
      .map((child, index) => `${tag === 'ol' ? `${index + 1}.` : '-'} ${markdownFromInlineNode(child).trim()}`)
      .join('\n')
  }
  return markdownFromInlineNode(node).trimEnd()
}

function quillHtmlToMarkdown (value) {
  if (!value || typeof window === 'undefined' || !window.DOMParser) return ''
  const document = new window.DOMParser().parseFromString(value, 'text/html')
  return Array.from(document.body.childNodes)
    .map(markdownFromBlockNode)
    .filter((block) => block.trim())
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
}

export default {
  props: {
    conversationFilter: {
      type: String,
      default: 'all'
    }
  },
  components: {
    Translate
  },
  data () {
    return {
      user_id: getUserId(),
      chat_list: [],
      currentConversationId: '',
      currentConversation: {},
      userMap: {},
      userInfoMap: {},
      chatUsers: [],
      inputContent: '',
      emailContentHtml: '',
      composerDrafts: loadComposerDrafts(getUserId()),
      messageList: [],
      messageListRequestId: 0,
      historySearchKeyword: '',
      historySearchLoading: false,
      historySearchResults: [],
      historySearchPanelOpen: false,
      historySearchRequestId: 0,
      messageListLoading: false,
      olderHistoryLoading: false,
      olderHistoryRequestId: 0,
      linkMetadataCache: {},
      linkMetadataLoading: {},
      linkMetadataTimer: null,
      messageCache: {},
      activeConversationSyncTimer: null,
      ws: null,
      socketState: 'connecting',
      reconnectAttempt: 0,
      reconnectTimer: null,
      tokenRefreshTimer: null,
      tokenRefreshPromise: null,
      tokenRefreshRetryAttempt: 0,
      socketAuthRefreshAttempted: false,
      destroyed: false,
      authErrorShown: false,
      reauthDialogVisible: false,
      reauthSubmitting: false,
      reauthStep: 'password',
      reauthMobile: '',
      reauthMobileMasked: '',
      reauthError: '',
      reauthForm: {
        email: '',
        password: '',
        employment: 'fulltime',
        pin: ''
      },
      reauthRules: {
        email: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
        employment: [{ required: true, message: '请选择登录类型', trigger: 'change' }]
      },
      reauthMobileRules: {
        pin: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
      },
      sending: false,
      processingImage: false,
      uploadingFile: false,
      uploadProgress: 0,
      attachmentName: '',
      uploadSubscription: null,
      downloadingMessageFileIds: {},
      pendingImages: [],
      pendingFiles: [],
      composerDragActive: false,
      composerDragDepth: 0,
      quotedMessage: null,
      quoteDetailMap: {},
      quoteDetailLoading: {},
      quoteContextLoading: {},
      focusedMessageId: '',
      focusMessageTimer: null,
      previewImage: '',
      composeMode: 'chat',
      todoDialogVisible: false,
      todoCreating: false,
      todoSourceMessage: null,
      messageEditDialogVisible: false,
      messageEditSubmitting: false,
      messageEditTarget: null,
      messageEditContent: '',
      multiSelectMode: false,
      selectedMessageIds: [],
      selectedMessagesEmailSending: false,
      selectedEmailRecipientDialogVisible: false,
      selectedEmailRecipientIds: [],
      linkedTodoDialogVisible: false,
      linkedTodoLoading: false,
      linkedTodo: null,
      conversationTodosVisible: false,
      conversationTodosLoading: false,
      conversationTodos: [],
      conversationTodoStatusUpdating: {},
      conversationFilesVisible: false,
      conversationFilesLoading: false,
      conversationFiles: [],
      conversationFilesRequestId: 0,
      todoDefaultListName: '工作消息任务',
      todoLabelOptions: [],
      todoLabelLoading: false,
      todoDraft: {
        content: '',
        ownerId: '',
        collaboratorIds: [],
        labels: []
      },
      emailEditorMode: 'markdown',
      emailEditorOptions: {
        theme: 'snow',
        placeholder: '输入邮件内容，Markdown 写法会立即转换为格式',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'blockquote', 'code-block'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'clean']
          ]
        }
      },
      notificationPermission: getBrowserNotificationPermission(),
      notificationSubscriptions: loadNotificationSubscriptions(getUserId()),
      activeNotifications: [],
      realtimeReadReceipts: {},
      reactionRequestKeys: {},
      peerPresenceState: 'unknown',
      peerPresenceUpdatedAt: 0,
      peerPresenceTimer: null,
      peerPresenceRequestId: 0,
      peerPresenceInFlightPeerId: '',
      peerPresenceLastRequestedAt: 0,
      groupPresenceMap: {},
      groupPresenceTimer: null,
      groupPresenceRequestId: 0,
      groupPresenceRefreshing: false,
      groupPresenceLastRequestedAt: 0,
      conversationPresenceTimer: null,
      conversationPresenceRefreshing: false,
      conversationPresenceLastRequestedAt: 0,
      groupPanelOpen: false,
      groupPanelMode: 'create',
      groupName: '',
      groupMemberSearch: '',
      selectedGroupMemberIds: [],
      groupActionLoading: false,
      userProfileVisible: false,
      profileUserId: '',
      mentionMenuVisible: false,
      mentionQuery: '',
      mentionStart: -1,
      mentionActiveIndex: 0,
      insertedMentions: [],
      voiceInputVisible: false,
      askAIDialogVisible: false,
      askAITargetMessage: null,
      askAIModel: 'deepseek',
      askAIQuestion: '',
      aiPendingCount: 0,
      aiStreamControllers: []
    }
  },
  watch: {
    conversationFilter () {
      this.syncConversationWithFilter()
    },
    inputContent () {
      this.persistActiveComposerDraft('chat')
    },
    emailContentHtml () {
      this.persistActiveComposerDraft('email')
    },
    messageList: {
      handler () {
        this.scheduleMessageLinkMetadataFetch()
      },
      deep: true
    }
  },
  computed: {
    messageReactionOptions () {
      return MESSAGE_REACTION_OPTIONS
    },
    visibleChatList () {
      const conversations = this.conversationFilter === 'groups'
        ? this.chat_list.filter((conversation) => this.isGroupConversation(conversation))
        : this.chat_list
      return this.sortConversationsByPin(conversations)
    },
    conversationListTitle () {
      return this.conversationFilter === 'groups' ? '我的群组' : '消息'
    },
    conversationListEmptyTitle () {
      return this.conversationFilter === 'groups' ? '还没有加入群组' : '暂无会话'
    },
    conversationListEmptyDescription () {
      return this.conversationFilter === 'groups'
        ? '创建群聊或等待同事邀请后会显示在这里'
        : '从通讯录选择同事开始聊天'
    },
    socketStateLabel () {
      return {
        connecting: '连接中',
        connected: '实时连接',
        reconnecting: '正在重连',
        offline: '连接已断开',
        authRequired: '请重新登录'
      }[this.socketState]
    },
    trimmedHistorySearchKeyword () {
      return this.historySearchKeyword.trim()
    },
    historySearchPanelVisible () {
      return this.historySearchPanelOpen || this.historySearchLoading || this.historySearchResults.length > 0
    },
    canSend () {
      return Boolean(
        !this.sending &&
          !this.attachmentBusy &&
          (this.composerContent.trim() || this.hasPendingAttachments) &&
          this.currentConversationId
      )
    },
    hasPendingAttachments () {
      return Boolean(this.pendingImages.length || this.pendingFiles.length)
    },
    selectableMessageIds () {
      return this.messageList
        .filter((message) => this.isSelectableMessage(message))
        .map((message) => this.messageId(message))
    },
    allSelectableMessagesSelected () {
      return Boolean(
        this.selectableMessageIds.length &&
          this.selectableMessageIds.every((messageId) => this.selectedMessageIds.includes(messageId))
      )
    },
    emailMarkdownContent () {
      return quillHtmlToMarkdown(this.emailContentHtml)
    },
    composerContent () {
      return this.composeMode === 'email'
        ? this.emailMarkdownContent
        : this.inputContent
    },
    quoteDraftSenderLabel () {
      return this.quoteSenderLabel(this.quotedMessage)
    },
    quoteDraftSummary () {
      return this.quoteMessageSummary(this.quotedMessage)
    },
    askAIQuoteSenderLabel () {
      return this.quoteSenderLabel(this.askAITargetMessage)
    },
    askAIQuoteSummary () {
      return this.quoteMessageSummary(this.askAITargetMessage)
    },
    askAIQuoteTime () {
      const time = Number(this.askAITargetMessage && this.askAITargetMessage.create_time)
      return Number.isFinite(time) && time > 0
        ? this.$dayjs(time * 1000).format('YYYY-MM-DD HH:mm')
        : ''
    },
    canSubmitAskAI () {
      return Boolean(this.askAITargetMessage && this.currentConversationId)
    },
    attachmentBusy () {
      return this.processingImage || this.uploadingFile
    },
    attachmentStatus () {
      if (this.processingImage) return `正在压缩 ${this.attachmentName}`
      if (this.uploadingFile) return `正在上传 ${this.uploadProgress}%`
      return ''
    },
    composerPlaceholder () {
      if (this.hasPendingAttachments) return '可继续输入文字，附件与文字将依次发送'
      return '请输入聊天消息，支持直接粘贴图片'
    },
    keyboardHint () {
      return this.composeMode === 'email'
        ? 'Enter 换行 · Alt+Enter 发送并邮件通知'
        : 'Enter 换行 · Alt+Enter 发送'
    },
    sendButtonLabel () {
      if (this.sending) return '发送中'
      return this.composeMode === 'email' ? '发送邮件消息' : '发送'
    },
    notificationSupported () {
      return this.notificationPermission !== 'unsupported'
    },
    notificationEnabled () {
      return Boolean(
        this.notificationPermission === 'granted' &&
          this.currentConversationId &&
          this.notificationSubscriptions[this.currentConversationId]
      )
    },
    notificationButtonTitle () {
      if (!this.notificationSupported) return '当前浏览器不支持系统通知'
      if (!this.currentConversationId) return '请选择一个会话后再设置通知'
      if (this.notificationPermission === 'denied') return '系统通知已被浏览器拦截，请在浏览器设置中开启'
      if (this.notificationEnabled) return '当前会话通知已订阅，点击关闭'
      if (this.notificationPermission === 'granted') return '当前会话通知未订阅，点击开启'
      return '开启浏览器通知并订阅当前会话'
    },
    activePeerId () {
      return this.conversationPeerId(this.currentConversation)
    },
    activePeerEmail () {
      if (!this.activePeerId) return ''
      const peer = this.userInfoMap[this.activePeerId] || {}
      return peer.user_email || peer.email || peer.employee_email || ''
    },
    todoOwnerOptions () {
      const options = []
      const addUser = (id, fallbackLabel) => {
        if (!id || options.some((item) => item.id === id)) return
        options.push({
          id,
          label: this.todoOwnerLabel(id, fallbackLabel)
        })
      }
      addUser(this.user_id, '我')
      addUser(this.activePeerId, '对方')
      this.groupMemberIds.forEach((memberId) => addUser(memberId, '群成员'))
      return options
    },
    todoCollaboratorOptions () {
      return this.todoOwnerOptions.filter((user) => user.id !== this.todoDraft.ownerId)
    },
    linkedTodoId () {
      if (!this.linkedTodo) return ''
      const id = this.linkedTodo.todo_id ||
        this.linkedTodo.created_todo_id ||
        (this.linkedTodo.todo && (this.linkedTodo.todo._id || this.linkedTodo.todo.id || this.linkedTodo.todo.todo_id)) ||
        this.linkedTodo._id ||
        this.linkedTodo.id ||
        ''
      return String(id.$id || id.$oid || id)
    },
    linkedTodoLabels () {
      return this.linkedTodo && Array.isArray(this.linkedTodo.todo_labels)
        ? this.linkedTodo.todo_labels.filter(Boolean).map(String)
        : []
    },
    linkedTodoOwnerLabel () {
      if (!this.linkedTodo) return '未分配'
      const assignees = Array.isArray(this.linkedTodo.assign_users)
        ? this.linkedTodo.assign_users
        : []
      const names = assignees
        .map((user) => this.chatUserName(user))
        .filter((name) => name && name !== '未命名同事')
      return names.length ? names.join('、') : '未分配'
    },
    linkedTodoCreateTime () {
      const time = Number(this.linkedTodo && this.linkedTodo.create_time)
      return Number.isFinite(time) && time > 0
        ? this.$dayjs(time * 1000).format('YYYY-MM-DD HH:mm')
        : '未知'
    },
    showPeerPresence () {
      return Boolean(this.activePeerId)
    },
    isCurrentGroup () {
      return this.isGroupConversation(this.currentConversation)
    },
    groupMemberIds () {
      if (!this.isCurrentGroup || !Array.isArray(this.currentConversation.group_user_ids)) return []
      return this.currentConversation.group_user_ids.map(String)
    },
    groupMemberCount () {
      return this.groupMemberIds.length
    },
    groupOnlineCount () {
      return this.groupMemberIds.filter((memberId) => this.memberIsOnline(memberId)).length
    },
    groupPresenceTitle () {
      const onlineNames = this.groupMemberIds
        .filter((memberId) => this.memberIsOnline(memberId))
        .map((memberId) => memberId === this.user_id ? '我' : this.getUserName(memberId))
        .filter(Boolean)
      if (!onlineNames.length) return '查看群成员 · 暂无成员在线'
      return `查看群成员 · 在线：${onlineNames.join('、')}`
    },
    groupMemberSummary () {
      const names = this.groupMemberIds
        .map((memberId) => memberId === this.user_id ? '我' : this.getUserName(memberId))
        .filter((name) => name && name !== '未知成员')
      if (!names.length) return ''
      const visibleNames = names.slice(0, 4).join('、')
      return names.length > 4 ? `${visibleNames} 等 ${names.length} 人` : visibleNames
    },
    groupMembers () {
      return this.groupMemberIds.map((memberId) => {
        return this.userInfoMap[memberId] || {
          user_id: memberId,
          user_name: this.getUserName(memberId) === '未知成员' ? '未知成员' : this.getUserName(memberId)
        }
      })
    },
    filteredGroupMembers () {
      const keyword = this.groupMemberSearch.toLowerCase()
      if (!keyword) return this.groupMembers
      return this.groupMembers.filter((user) => {
        return `${this.chatUserName(user)} ${this.chatUserEmail(user)}`.toLowerCase().includes(keyword)
      })
    },
    selectedEmailRecipientOptions () {
      return this.groupMembers
        .map((user) => {
          const id = this.chatUserId(user)
          const email = this.chatUserEmail(user)
          const name = this.chatUserName(user)
          return {
            id,
            email,
            label: email ? `${name} · ${email}` : name
          }
        })
        .filter((user) => user.id && user.id !== this.user_id && user.email)
    },
    mentionCandidates () {
      if (this.composeMode !== 'chat') return []
      const keyword = this.mentionQuery.trim().toLowerCase()
      const aiCandidates = AI_MENTION_CANDIDATES.filter((candidate) => {
        return !keyword || candidate.token.toLowerCase().includes(keyword)
      })
      if (!this.isCurrentGroup) return aiCandidates
      const members = this.groupMembers.filter((user) => this.chatUserId(user) !== this.user_id)
      const everyoneAliases = ['所有人', '全体', 'everyone', 'all']
      const includeEveryone = !keyword || everyoneAliases.some((alias) => alias.includes(keyword))
      const matchedMembers = !keyword ? members : members.filter((user) => {
        return `${this.chatUserName(user)} ${this.chatUserEmail(user)}`.toLowerCase().includes(keyword)
      })
      const candidates = []
      if (includeEveryone) candidates.push({ user_id: '__mention_all__', user_name: '所有人', mention_type: 'all' })
      candidates.push(...aiCandidates, ...matchedMembers)
      return candidates.slice(0, 9)
    },
    groupOwnerId () {
      if (!this.isCurrentGroup) return ''
      return String(this.currentConversation.owner_id || this.currentConversation.created_by || '')
    },
    groupOwnerName () {
      if (!this.groupOwnerId) return ''
      return this.groupOwnerId === this.user_id ? '我' : this.getUserName(this.groupOwnerId)
    },
    profileUser () {
      if (!this.profileUserId) return null
      return this.userInfoMap[this.profileUserId] || {
        user_id: this.profileUserId,
        user_name: this.getUserName(this.profileUserId)
      }
    },
    profileUserRows () {
      const user = this.profileUser
      if (!user) return []
      const rows = [
        ['email', '邮箱', this.chatUserEmail(user)],
        ['department', '部门', user.department || user.user_department || user.employee_department],
        ['title', '职位', user.title || user.position || user.user_position || user.employee_position],
        ['phone', '电话', user.phone || user.mobile || user.user_phone || user.employee_phone]
      ]
      return rows
        .filter((row) => row[2])
        .map((row) => ({
          key: row[0],
          label: row[1],
          value: row[2]
        }))
    },
    groupPanelTitle () {
      if (this.groupPanelMode === 'members') return '群成员'
      return this.groupPanelMode === 'invite' ? '添加群成员' : '新建群聊'
    },
    groupPanelDescription () {
      if (this.groupPanelMode === 'members') {
        return `${this.getConversationName(this.currentConversation) || '当前群聊'} · ${this.groupMemberCount} 位成员`
      }
      return this.groupPanelMode === 'invite'
        ? `邀请同事加入“${this.getConversationName(this.currentConversation) || '当前群聊'}”`
        : '创建后即可发送文字、图片和文件'
    },
    groupPanelSubmitLabel () {
      return this.groupPanelMode === 'invite' ? '添加成员' : '创建群聊'
    },
    groupCandidates () {
      const existingIds = new Set(this.groupPanelMode === 'invite' ? this.groupMemberIds : [this.user_id])
      return this.chatUsers.filter((user) => {
        const userId = this.chatUserId(user)
        return userId && !existingIds.has(userId)
      })
    },
    filteredGroupCandidates () {
      const keyword = this.groupMemberSearch.toLowerCase()
      if (!keyword) return this.groupCandidates
      return this.groupCandidates.filter((user) => {
        return `${this.chatUserName(user)} ${this.chatUserEmail(user)}`.toLowerCase().includes(keyword)
      })
    },
    canSubmitGroupPanel () {
      if (this.groupPanelMode === 'invite') return this.selectedGroupMemberIds.length > 0
      return Boolean(this.groupName.trim() && this.selectedGroupMemberIds.length >= 2)
    },
    peerPresenceClass () {
      return `is-${this.peerPresenceState}`
    },
    peerPresenceLabel () {
      return {
        checking: '检查中',
        online: '在线',
        offline: '离线',
        unknown: '状态未知'
      }[this.peerPresenceState] || '状态未知'
    },
    peerPresenceTitle () {
      if (this.peerPresenceState === 'unknown') return '后端暂未返回在线状态'
      if (!this.peerPresenceUpdatedAt) return this.peerPresenceLabel
      if (this.peerPresenceState === 'offline') {
        return `离线 · 最后在线 ${this.$dayjs(this.peerPresenceUpdatedAt).format('HH:mm:ss')}`
      }
      return `${this.peerPresenceLabel} · ${this.$dayjs(this.peerPresenceUpdatedAt).format('HH:mm:ss')}`
    }
  },
  mounted () {
    this.getChatList()
    this.getUserMap()
    this.initializeRealtimeConnection()
    document.addEventListener('keydown', this.handleGlobalKeydown)
    document.addEventListener('visibilitychange', this.handleTokenRefreshWake)
    window.addEventListener('focus', this.handleTokenRefreshWake)
  },
  beforeDestroy () {
    this.persistComposerDraftsForConversation(this.currentConversationId)
    this.destroyed = true
    this.aiStreamControllers.forEach((controller) => controller.abort())
    this.aiStreamControllers = []
    clearTimeout(this.reconnectTimer)
    clearTimeout(this.tokenRefreshTimer)
    clearTimeout(this.peerPresenceTimer)
    clearTimeout(this.groupPresenceTimer)
    clearTimeout(this.conversationPresenceTimer)
    clearTimeout(this.focusMessageTimer)
    clearTimeout(this.activeConversationSyncTimer)
    clearTimeout(this.linkMetadataTimer)
    if (this.ws) this.ws.close()
    if (this.uploadSubscription) this.uploadSubscription.unsubscribe()
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    document.removeEventListener('visibilitychange', this.handleTokenRefreshWake)
    window.removeEventListener('focus', this.handleTokenRefreshWake)
    this.closeActiveNotifications()
  },
  methods: {
    async initializeRealtimeConnection () {
      try {
        await this.ensureFreshNotificationToken()
      } catch (error) {
        if (isNotificationTokenAuthError(error)) {
          this.requireRealtimeReauthentication(error.message)
          return
        }
        this.scheduleTokenRefreshRetry()
      }
      if (!this.destroyed && this.socketState !== 'authRequired') this.connectSocket()
    },
    ensureFreshNotificationToken (options = {}) {
      if (this.tokenRefreshPromise) return this.tokenRefreshPromise
      const refreshPromise = renewNotificationToken(options)
        .then((result) => {
          this.tokenRefreshRetryAttempt = 0
          this.scheduleTokenRefresh(result.token)
          return result
        })
        .finally(() => {
          if (this.tokenRefreshPromise === refreshPromise) this.tokenRefreshPromise = null
        })
      this.tokenRefreshPromise = refreshPromise
      return refreshPromise
    },
    scheduleTokenRefresh (token = getNotificationToken()) {
      clearTimeout(this.tokenRefreshTimer)
      if (this.destroyed) return
      const delay = notificationTokenRefreshDelay(token)
      if (delay === null) {
        this.requireRealtimeReauthentication('登录令牌已失效，请重新登录')
        return
      }
      this.tokenRefreshTimer = setTimeout(
        () => this.refreshRealtimeToken(),
        Math.max(1000, delay)
      )
    },
    scheduleTokenRefreshRetry () {
      clearTimeout(this.tokenRefreshTimer)
      if (this.destroyed) return
      const state = getNotificationTokenState(getNotificationToken())
      if (!state.valid) {
        this.requireRealtimeReauthentication('登录令牌已失效，请重新登录')
        return
      }
      this.tokenRefreshRetryAttempt += 1
      const retryDelay = Math.min(
        TOKEN_REFRESH_RETRY_MAX_MS,
        TOKEN_REFRESH_RETRY_MIN_MS * Math.pow(2, Math.min(this.tokenRefreshRetryAttempt - 1, 3))
      )
      const beforeExpiryDelay = Math.max(1000, (state.remainingSeconds - 5) * 1000)
      this.tokenRefreshTimer = setTimeout(
        () => this.refreshRealtimeToken(),
        Math.min(retryDelay, beforeExpiryDelay)
      )
    },
    async refreshRealtimeToken (options = {}) {
      try {
        await this.ensureFreshNotificationToken(options)
      } catch (error) {
        if (isNotificationTokenAuthError(error)) {
          this.requireRealtimeReauthentication(error.message)
        } else {
          this.scheduleTokenRefreshRetry()
        }
      }
    },
    handleTokenRefreshWake () {
      if (document.hidden || this.destroyed || this.socketState === 'authRequired') return
      this.refreshRealtimeToken()
      this.scheduleActiveConversationSync()
    },
    scheduleActiveConversationSync () {
      clearTimeout(this.activeConversationSyncTimer)
      if (!this.currentConversationId) return
      this.activeConversationSyncTimer = setTimeout(() => {
        this.loadConversationMessages(this.currentConversationId).catch(() => {})
      }, 150)
    },
    requireRealtimeReauthentication (message = '') {
      clearTimeout(this.reconnectTimer)
      clearTimeout(this.tokenRefreshTimer)
      removeNotificationToken()
      this.socketState = 'authRequired'
      if (message) this.reauthError = message
      if (this.ws) {
        const socket = this.ws
        this.ws = null
        socket.close()
      }
      if (!this.authErrorShown) {
        this.authErrorShown = true
        this.openReauthDialog({ preserveError: Boolean(message) })
      }
    },
    async handleSocketAuthenticationFailure () {
      const tokenState = getNotificationTokenState(getNotificationToken())
      if (tokenState.valid && !this.socketAuthRefreshAttempted) {
        this.socketAuthRefreshAttempted = true
        try {
          await this.ensureFreshNotificationToken({ force: true })
          if (!this.destroyed) {
            this.socketState = 'offline'
            this.connectSocket()
          }
          return
        } catch (error) {
          const message = isNotificationTokenAuthError(error)
            ? error.message
            : '实时认证失败，自动续期暂时不可用，请重新登录'
          this.requireRealtimeReauthentication(message)
          return
        }
      }
      this.requireRealtimeReauthentication('实时连接认证失败，请重新登录')
    },
    openReauthDialog ({ preserveError = false } = {}) {
      const currentUser = this.userInfoMap[this.user_id] || {}
      if (!this.reauthForm.email) {
        this.reauthForm.email = getEmail() || currentUser.user_email || ''
      }
      if (currentUser.user_employment === 'parttime' || currentUser.user_employment === 'fulltime') {
        this.reauthForm.employment = currentUser.user_employment
      }
      this.reauthDialogVisible = true
      if (!preserveError) this.reauthError = ''
      this.$nextTick(() => {
        const input = this.$refs.reauthEmailInput
        if (input && input.focus) input.focus()
      })
    },
    resetReauthError () {
      this.reauthError = ''
    },
    validateReauthForm (refName) {
      const form = this.$refs[refName]
      if (!form || !form.validate) return Promise.resolve(true)
      return new Promise((resolve) => form.validate((valid) => resolve(valid)))
    },
    async submitReauthentication () {
      const valid = await this.validateReauthForm('reauthPasswordForm')
      if (!valid || this.reauthSubmitting) return
      this.reauthSubmitting = true
      this.reauthError = ''
      try {
        const response = await loginByEmail(
          this.reauthForm.email,
          this.reauthForm.password,
          this.reauthForm.employment,
          REAUTH_REQUEST_CONFIG
        )
        const result = response.data || {}
        const statusCode = Number(result.status_code)
        if (statusCode === 0) {
          this.completeReauthentication(result)
          return
        }
        if (statusCode === 1) {
          if (!result.user_mobile) {
            this.reauthError = '该账号尚未绑定手机号，请联系管理员后再试'
            return
          }
          const pinResponse = await createLoginPin(
            { user_email: this.reauthForm.email },
            REAUTH_REQUEST_CONFIG
          )
          const pinResult = pinResponse.data || {}
          if (Number(pinResult.code) !== 0) {
            this.reauthError = this.reauthBusinessError(pinResult, '验证码发送失败，请稍后重试')
            return
          }
          this.reauthMobile = String(result.user_mobile)
          this.reauthMobileMasked = `*******${this.reauthMobile.slice(-4)}`
          this.reauthForm.pin = ''
          this.reauthStep = 'mobile'
          this.$message.success('验证码已发送至手机')
          return
        }
        this.reauthError = this.reauthBusinessError(result, ({
          3: '密码不正确',
          4: '找不到账户',
          5: '登录类型不匹配'
        }[statusCode]) || '登录失败，请检查账号信息后重试')
      } catch (error) {
        this.reauthError = this.reauthRequestError(error, '暂时无法登录，请稍后重试')
      } finally {
        this.reauthSubmitting = false
      }
    },
    async submitMobileVerification () {
      const valid = await this.validateReauthForm('reauthMobileForm')
      if (!valid || this.reauthSubmitting) return
      this.reauthSubmitting = true
      this.reauthError = ''
      try {
        const response = await verifyLoginPin({
          mobile: this.reauthMobile,
          pin: this.reauthForm.pin
        }, REAUTH_REQUEST_CONFIG)
        const result = response.data || {}
        if (Number(result.code) !== 0 || !result.data) {
          this.reauthError = Number(result.code) === 1
            ? '验证码不正确，请重新输入'
            : this.reauthBusinessError(result, '验证码验证失败，请重试')
          return
        }
        const user = result.data
        this.completeReauthentication({
          user_id: this.chatUserId(user),
          user_name: user.user_name,
          user_email: user.user_email,
          notification_token: user.notification_token
        })
      } catch (error) {
        this.reauthError = this.reauthRequestError(error, '验证码验证失败，请稍后重试')
      } finally {
        this.reauthSubmitting = false
      }
    },
    returnToPasswordLogin () {
      this.reauthStep = 'password'
      this.reauthForm.pin = ''
      this.reauthError = ''
    },
    reauthBusinessError (result, fallback) {
      return result && typeof result.msg === 'string' && result.msg.trim()
        ? result.msg.trim()
        : fallback
    },
    reauthRequestError (error, fallback) {
      const response = error && error.response
      const status = response ? Number(response.status) : 0
      const businessMessage = response && response.data && typeof response.data.msg === 'string'
        ? response.data.msg.trim()
        : ''
      if (businessMessage) return businessMessage
      if (error && error.code === 'ECONNABORTED') return '请求超时，请检查网络后重试'
      if (!response) return '网络连接失败，请检查网络后重试'
      if (status === 429) return '操作过于频繁，请稍后再试'
      if (status >= 500) return '登录服务暂时不可用，请稍后重试'
      return fallback
    },
    completeReauthentication (result) {
      if (!result.user_id || !result.notification_token) {
        this.reauthError = '登录成功但未获得实时连接令牌，请联系管理员'
        return
      }
      setUserId(result.user_id)
      setUserName(result.user_name || '')
      setEmail(result.user_email || '')
      setNotificationToken(result.notification_token)
      this.user_id = result.user_id
      this.authErrorShown = false
      this.socketAuthRefreshAttempted = false
      this.reconnectAttempt = 0
      this.tokenRefreshRetryAttempt = 0
      this.socketState = 'offline'
      this.reauthDialogVisible = false
      this.reauthForm.password = ''
      this.reauthForm.pin = ''
      this.reauthStep = 'password'
      if (this.ws) {
        const socket = this.ws
        this.ws = null
        socket.close()
      }
      this.scheduleTokenRefresh(result.notification_token)
      this.connectSocket()
      this.refreshAfterReconnect().catch(() => {})
      this.$message.success('登录成功，正在恢复实时连接')
    },
    connectSocket () {
      if (this.destroyed || this.socketState === 'authRequired') return
      const token = getNotificationToken()
      if (!token) {
        this.requireRealtimeReauthentication('登录令牌已失效，请重新登录')
        return
      }

      this.socketState = this.reconnectAttempt ? 'reconnecting' : 'connecting'
      const socket = createChatSocket()
      this.ws = socket
      socket.onopen = () => {
        socket.send(JSON.stringify({ type: 'authenticate', token }))
      }
      socket.onmessage = (event) => {
        let payload
        try {
          payload = JSON.parse(event.data)
        } catch (error) {
          return
        }
        if (payload.type === 'ready') {
          const recoveredConnection = this.reconnectAttempt > 0
          this.reconnectAttempt = 0
          this.socketAuthRefreshAttempted = false
          this.socketState = 'connected'
          this.scheduleTokenRefresh()
          this.subscribePeerPresence()
          this.refreshPeerPresence()
          this.refreshGroupPresence({ force: true })
          this.refreshConversationPresence({ force: true })
          if (recoveredConnection) this.refreshAfterReconnect().catch(() => {})
        } else if (payload.type === 'message' && payload.data) {
          this.receiveSocketMessage(payload.data).catch(() => {})
        } else if (payload.type === 'read_receipt' && payload.data) {
          this.applyReadReceipt(payload.data)
        } else if (payload.type === 'conversation_updated' && payload.data) {
          this.receiveConversationUpdate(payload.data).catch(() => {})
        } else if (payload.type === 'message_reaction' && payload.data) {
          this.applyMessageReaction(payload.data)
        } else {
          this.applyPresencePayload(payload)
        }
      }
      socket.onerror = () => {
        if (this.ws === socket) this.socketState = 'offline'
      }
      socket.onclose = (event) => {
        if (this.ws !== socket) return
        this.ws = null
        if (event.code === 4401) {
          this.handleSocketAuthenticationFailure()
          return
        }
        if (!this.destroyed) this.scheduleReconnect()
      }
    },
    scheduleReconnect () {
      clearTimeout(this.reconnectTimer)
      this.reconnectAttempt += 1
      this.socketState = 'reconnecting'
      const delay = Math.min(
        30000,
        1000 * Math.pow(2, Math.min(this.reconnectAttempt - 1, 5))
      )
      this.reconnectTimer = setTimeout(() => this.connectSocket(), delay)
    },
    async refreshAfterReconnect () {
      await this.getChatList(false)
      const conversation = this.chat_list.find(
        (item) => item._id.$id === this.currentConversationId
      )
      if (conversation) this.startChat(conversation)
    },
    async receiveSocketMessage (data) {
      let conversation = this.chat_list.find(
        (item) => item._id.$id === data.conversation_id
      )
      if (!conversation) {
        await this.getChatList(false)
        conversation = this.chat_list.find(
          (item) => item._id.$id === data.conversation_id
        )
      }
      if (!conversation) return

      conversation.currentMessage = data.content
      conversation.update_time = data.create_time
      conversation[`no_read_${this.user_id}`] = true
      this.moveConversationToTop(conversation)
      if (!this.isSystemMessage(data)) this.showIncomingNotification(data, conversation)
      if (data.user_id && data.user_id !== this.user_id) {
        this.markMessagesReadBy(data.conversation_id, data.user_id)
      }

      if (this.currentConversationId === data.conversation_id) {
        conversation[`no_read_${this.user_id}`] = false
        const shouldLoadFullMessage = (data.message_type || 'text') !== 'text' ||
          Boolean(this.messageQuoteId(data))
        if (shouldLoadFullMessage) {
          const socketMessage = this.socketMessageFromEvent(data)
          let loaded = false
          if (this.messageQuote(socketMessage) && !this.hasMessage(data.message_id)) {
            this.messageList.push(socketMessage)
            this.scrollToLatest()
            loaded = true
          }
          try {
            if (!loaded) {
              const response = await getChatMessage({
                message_id: data.message_id,
                user_id: this.user_id
              })
              const incomingMessage = response.data && response.data.data
              if (response.data.code === 0 && incomingMessage) {
                this.applyIncomingSystemMessage(incomingMessage, conversation)
              }
              if (response.data.code === 0 && incomingMessage && !this.hasMessage(data.message_id)) {
                this.messageList.push(incomingMessage)
                this.scrollToLatest()
                loaded = true
              }
            }
          } catch (error) {
            // The embedded quote summary already renders a complete realtime message.
          }
          if (!loaded && !this.hasMessage(data.message_id)) {
            await this.loadConversationMessages(data.conversation_id)
          }
        } else if (!this.hasMessage(data.message_id)) {
          this.messageList.push(this.socketMessageFromEvent(data))
          this.scrollToLatest()
        }
        readChatConversation({
          conversation_id: data.conversation_id,
          user_id: this.user_id
        }).catch(() => {})
      } else {
        this.cacheSocketMessage(data)
      }
    },
    async receiveConversationUpdate (data) {
      if (!data || !data.conversation_id) return
      await this.getChatList(false)
      const conversation = this.chat_list.find(
        (item) => item._id.$id === data.conversation_id
      )
      if (!conversation) return
      if (this.currentConversationId === data.conversation_id) {
        this.currentConversation = conversation
      } else if (!this.currentConversationId) {
        this.startChat(conversation)
      }
    },
    requestBrowserNotificationPermission () {
      if (!this.currentConversationId) {
        this.$message.warning('请先选择一个会话')
        return
      }
      if (!this.notificationSupported || this.notificationPermission === 'denied') return
      if (this.notificationPermission === 'granted') {
        this.toggleConversationNotification()
        return
      }
      requestBrowserNotificationPermission().then((permission) => {
        this.notificationPermission = permission
        if (permission === 'granted') {
          this.setConversationNotification(this.currentConversationId, true)
          this.$message.success('已订阅当前会话通知')
        } else if (permission === 'denied') {
          this.$message.warning('系统通知已被浏览器拦截，请在浏览器设置中开启')
        }
      }).catch(() => {
        this.$message.error('系统通知授权失败，请检查浏览器设置')
      })
    },
    toggleConversationNotification () {
      const conversationId = this.currentConversationId
      const nextEnabled = !this.notificationSubscriptions[conversationId]
      this.setConversationNotification(conversationId, nextEnabled)
      this.$message.success(nextEnabled ? '已订阅当前会话通知' : '已关闭当前会话通知')
    },
    setConversationNotification (conversationId, enabled) {
      if (!conversationId) return
      if (enabled) {
        this.$set(this.notificationSubscriptions, conversationId, true)
      } else {
        this.$delete(this.notificationSubscriptions, conversationId)
      }
      saveNotificationSubscriptions(this.user_id, this.notificationSubscriptions)
    },
    showIncomingNotification (data, conversation) {
      this.notificationPermission = getBrowserNotificationPermission()
      if (this.notificationPermission !== 'granted' || data.user_id === this.user_id) return
      const mentionsCurrentUser = this.messageMentionsUser(data, this.user_id)
      if (!mentionsCurrentUser && !this.notificationSubscriptions[data.conversation_id]) return

      const activeVisibleConversation =
        document.visibilityState === 'visible' &&
        this.currentConversationId === data.conversation_id
      if (activeVisibleConversation) return

      const conversationName = this.getConversationName(conversation) || '新消息'
      const senderName = this.getUserName(data.user_id)
      const notificationTitle = mentionsCurrentUser
        ? `${senderName || '群成员'} 在 ${conversationName} 中 @你`
        : conversationName
      const notification = new window.Notification(notificationTitle, {
        body: this.notificationBody(data),
        icon: '/todo.ico',
        tag: `${mentionsCurrentUser ? 'wechat-mention' : 'wechat'}-${data.conversation_id}`,
        renotify: true
      })
      this.activeNotifications.push(notification)
      notification.onclick = () => {
        window.focus()
        this.startChat(conversation)
        notification.close()
      }
      notification.onclose = () => {
        const index = this.activeNotifications.indexOf(notification)
        if (index !== -1) this.activeNotifications.splice(index, 1)
      }
      setTimeout(() => notification.close(), 8000)
    },
    notificationBody (data) {
      const type = data.message_type || 'text'
      if (type === 'image') return '[图片]'
      if (type === 'file') return '[文件]'
      if (type === 'email' || type === 'email_message') return '[邮件消息]'
      if (type === 'ai' || type === 'ai_message') {
        const aiContent = (data.content || '').replace(/\s+/g, ' ').trim()
        return aiContent ? `[AI回复] ${aiContent.slice(0, 100)}` : '[AI回复]'
      }
      const content = (data.content || '').replace(/\s+/g, ' ').trim()
      if (!content) return '收到一条新消息'
      return content.length > 120 ? `${content.slice(0, 120)}...` : content
    },
    closeActiveNotifications () {
      this.activeNotifications.forEach((notification) => notification.close())
      this.activeNotifications = []
    },
    async getUserMap () {
      const res = await getChatUserList()
      const userMap = {}
      const userInfoMap = {}
      for (const item of res.data) {
        const userId = this.chatUserId(item)
        if (!userId) continue
        userInfoMap[userId] = item
        userMap[userId] = this.chatUserName(item)
      }
      this.chatUsers = res.data || []
      this.userMap = userMap
      this.userInfoMap = userInfoMap
      const currentUser = userInfoMap[this.user_id]
      if (this.reauthDialogVisible && currentUser) {
        if (!this.reauthForm.email) this.reauthForm.email = currentUser.user_email || ''
        if (currentUser.user_employment === 'parttime' || currentUser.user_employment === 'fulltime') {
          this.reauthForm.employment = currentUser.user_employment
        }
      }
    },
    async getChatList (selectFirst = true) {
      const res = await getUserConversations({
        user_id: this.user_id
      })
      if (res.data.code === 0) {
        this.chat_list = res.data.data
        const currentConversation = this.chat_list.find(
          (item) => item._id.$id === this.currentConversationId
        )
        if (currentConversation) this.currentConversation = currentConversation
        if (selectFirst && !this.currentConversationId && this.visibleChatList.length) {
          this.startChat(this.visibleChatList[0])
        } else if (this.conversationFilter === 'groups') {
          this.syncConversationWithFilter()
        }
        this.subscribePeerPresence()
        this.refreshConversationPresence({ force: true })
      }
    },
    isConversationPinned (conversation) {
      return Boolean(conversation && (conversation.pinned || conversation.is_pinned))
    },
    sortConversationsByPin (conversations) {
      return [...conversations].sort((left, right) => {
        const leftPinned = this.isConversationPinned(left)
        const rightPinned = this.isConversationPinned(right)
        if (leftPinned !== rightPinned) return leftPinned ? -1 : 1
        if (leftPinned && rightPinned) {
          return Number(right.pinned_at || 0) - Number(left.pinned_at || 0)
        }
        return Number(right.update_time || 0) - Number(left.update_time || 0)
      })
    },
    async toggleConversationPinned (conversation) {
      const rawConversationId = conversation && (conversation._id || conversation.conversation_id)
      const conversationId = rawConversationId
        ? String(rawConversationId.$id || rawConversationId.$oid || rawConversationId)
        : ''
      if (!conversationId) return
      const pinned = !this.isConversationPinned(conversation)
      try {
        const response = await setChatConversationPinned({
          conversation_id: conversationId,
          user_id: this.user_id,
          pinned
        })
        const result = response.data || {}
        if (result.code !== 0 || !result.data) {
          throw new Error(result.msg || '会话置顶设置失败')
        }
        this.$set(conversation, 'pinned', Boolean(result.data.pinned))
        this.$set(conversation, 'pinned_at', Number(result.data.pinned_at || 0))
        this.chat_list = this.sortConversationsByPin(this.chat_list)
        this.$message.success(pinned ? '会话已置顶' : '已取消会话置顶')
      } catch (error) {
        this.$message.error(error.message || '会话置顶设置失败，请稍后重试')
      }
    },
    syncConversationWithFilter () {
      if (this.conversationFilter !== 'groups') return
      if (this.isGroupConversation(this.currentConversation)) return
      if (this.visibleChatList.length) {
        this.startChat(this.visibleChatList[0])
        return
      }
      this.currentConversationId = ''
      this.currentConversation = {}
      this.messageList = []
      this.peerPresenceState = 'unknown'
      this.peerPresenceUpdatedAt = 0
      this.removePendingAttachment()
    },
    composerDraftKey (conversationId, mode) {
      return `${conversationId}:${mode}`
    },
    composerDraftHasContent (mode, value) {
      if (mode === 'email') return Boolean(quillHtmlToMarkdown(value).trim())
      return Boolean(String(value || '').trim())
    },
    updateComposerDraft (conversationId, mode, value) {
      if (!conversationId || !mode) return
      const key = this.composerDraftKey(conversationId, mode)
      if (this.composerDraftHasContent(mode, value)) {
        this.$set(this.composerDrafts, key, value)
      } else {
        this.$delete(this.composerDrafts, key)
      }
      saveComposerDrafts(this.user_id, this.composerDrafts)
    },
    persistActiveComposerDraft (mode = this.composeMode) {
      if (!this.currentConversationId) return
      const value = mode === 'email' ? this.emailContentHtml : this.inputContent
      this.updateComposerDraft(this.currentConversationId, mode, value)
    },
    persistComposerDraftsForConversation (conversationId) {
      if (!conversationId) return
      this.updateComposerDraft(conversationId, 'chat', this.inputContent)
      this.updateComposerDraft(conversationId, 'email', this.emailContentHtml)
    },
    loadComposerDraftForMode (mode) {
      if (!this.currentConversationId) return
      const key = this.composerDraftKey(this.currentConversationId, mode)
      const value = this.composerDrafts[key] || ''
      if (mode === 'email') {
        this.emailContentHtml = value
      } else {
        this.inputContent = value
      }
    },
    loadComposerDraftsForConversation (conversationId) {
      if (!conversationId) return
      this.inputContent = this.composerDrafts[this.composerDraftKey(conversationId, 'chat')] || ''
      this.emailContentHtml = this.composerDrafts[this.composerDraftKey(conversationId, 'email')] || ''
    },
    clearComposerDraft (conversationId, mode) {
      if (!conversationId || !mode) return
      this.$delete(this.composerDrafts, this.composerDraftKey(conversationId, mode))
      saveComposerDrafts(this.user_id, this.composerDrafts)
    },
    setComposeMode (mode) {
      if (this.composeMode === mode) return
      this.persistActiveComposerDraft(this.composeMode)
      this.composeMode = mode
      this.closeMentionMenu()
      if (mode !== 'chat') this.voiceInputVisible = false
      this.loadComposerDraftForMode(mode)
      this.focusMessageInput()
    },
    setEmailEditorMode (mode) {
      this.emailEditorMode = mode
      this.focusMessageInput()
    },
    handleEmailEditorReady (quill) {
      if (!quill || quill.markdownShortcutsInstalled) return
      quill.markdownShortcutsInstalled = true
      quill.keyboard.addBinding(
        { key: ' ', collapsed: true },
        { prefix: /^(#{1,3}|>|```)$/ },
        function (range, context) {
          const marker = context.prefix.trim()
          const start = range.index - context.prefix.length
          let format = 'blockquote'
          let value = true
          if (marker.startsWith('#')) {
            format = 'header'
            value = marker.length
          } else if (marker === '```') {
            format = 'code-block'
          }
          this.quill.deleteText(start, context.prefix.length, 'user')
          this.quill.formatLine(start, 1, format, value, 'user')
          this.quill.setSelection(start, 0, 'silent')
          return false
        }
      )
      const component = this
      quill.on('text-change', (delta, oldDelta, source) => {
        if (source !== 'user' || component.emailEditorMode !== 'markdown') return
        window.setTimeout(() => {
          if (quill.markdownTransforming) return
          const range = quill.getSelection()
          if (!range || range.length) return
          const lineInfo = quill.getLine(range.index)
          if (!lineInfo || !lineInfo[0]) return
          const lineStart = range.index - lineInfo[1]
          const prefix = quill.getText(lineStart, lineInfo[1])
          const rules = [
            { pattern: /\*\*([^*\n]+)\*\*/, attributes: { bold: true } },
            { pattern: /_([^_\n]+)_/, attributes: { italic: true } },
            { pattern: /`([^`\n]+)`/, attributes: { code: true } },
            { pattern: /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/, link: true }
          ]
          for (const rule of rules) {
            const match = prefix.match(rule.pattern)
            if (!match) continue
            const start = lineStart + match.index
            const text = match[1]
            const attributes = rule.link ? { link: match[2] } : rule.attributes
            const nextCursor = range.index - (match[0].length - text.length)
            quill.markdownTransforming = true
            quill.deleteText(start, match[0].length, 'api')
            quill.insertText(start, text, attributes, 'api')
            quill.setSelection(nextCursor, 0, 'silent')
            quill.format('bold', false, 'silent')
            quill.format('italic', false, 'silent')
            quill.format('code', false, 'silent')
            quill.format('link', false, 'silent')
            quill.markdownTransforming = false
            break
          }
        }, 0)
      })
    },
    handleEmailPaste (event) {
      const clipboardData = event.clipboardData
      if (!clipboardData) return
      const hasImage = Array.from(clipboardData.items || []).some(
        (item) => item.kind === 'file' && /^image\//i.test(item.type)
      )
      if (hasImage) {
        this.handlePaste(event)
        return
      }
      const text = clipboardData.getData('text/plain')
      const looksLikeMarkdown = /(^|\n)\s*(?:#{1,3}\s|[-*]\s|\d+\.\s|>\s|```)|\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((?:https?:\/\/|mailto:)/.test(text)
      if (!text || !looksLikeMarkdown) return
      const editor = this.$refs.emailEditor
      const quill = editor && editor.quill
      if (!quill) return
      event.preventDefault()
      const range = quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 }
      const previousLength = quill.getLength()
      if (range.length) quill.deleteText(range.index, range.length, 'user')
      quill.clipboard.dangerouslyPasteHTML(range.index, renderMarkdown(text), 'user')
      const insertedLength = Math.max(0, quill.getLength() - previousLength + range.length)
      quill.setSelection(range.index + insertedLength, 0, 'silent')
    },
    handleComposerKeydown (event) {
      if (this.mentionMenuVisible) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          event.stopPropagation()
          this.moveMentionSelection(1)
          return
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          this.moveMentionSelection(-1)
          return
        }
        if ((event.key === 'Enter' && !event.altKey) || event.key === 'Tab') {
          event.preventDefault()
          event.stopPropagation()
          const member = this.mentionCandidates[this.mentionActiveIndex]
          if (member) this.selectMentionCandidate(member)
          return
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          this.closeMentionMenu()
          return
        }
      }
      if (event.key === 'Enter' && event.altKey) {
        event.preventDefault()
        event.stopPropagation()
        this.sendMessage()
      }
    },
    handleChatInput () {
      this.refreshMentionMenu()
    },
    messageTextarea () {
      const input = this.$refs.messageInput
      return input && (input.$refs.textarea || input.$el.querySelector('textarea'))
    },
    refreshMentionMenu () {
      if (this.composeMode !== 'chat') {
        this.closeMentionMenu()
        return
      }
      const textarea = this.messageTextarea()
      const cursor = textarea ? textarea.selectionStart : this.inputContent.length
      const prefix = this.inputContent.slice(0, cursor)
      const match = prefix.match(/(^|[\s\n])@([^\s@]*)$/)
      if (!match) {
        this.closeMentionMenu()
        return
      }
      this.mentionStart = cursor - match[2].length - 1
      this.mentionQuery = match[2]
      this.mentionMenuVisible = true
      if (this.mentionActiveIndex >= this.mentionCandidates.length) {
        this.mentionActiveIndex = 0
      }
    },
    moveMentionSelection (offset) {
      const total = this.mentionCandidates.length
      if (!total) return
      this.mentionActiveIndex = (this.mentionActiveIndex + offset + total) % total
    },
    selectMentionCandidate (member) {
      if (member && member.mention_type === 'ai') {
        this.insertAIMentionToken(member)
        return
      }
      const isEveryone = member && member.mention_type === 'all'
      const memberId = this.chatUserId(member)
      const name = isEveryone ? '所有人' : this.chatUserName(member)
      if ((!isEveryone && !memberId) || !name || this.mentionStart < 0) return
      const textarea = this.messageTextarea()
      const cursor = textarea ? textarea.selectionStart : this.inputContent.length
      const before = this.inputContent.slice(0, this.mentionStart)
      const after = this.inputContent.slice(cursor)
      const insertedText = `@${name} `
      this.inputContent = `${before}${insertedText}${after}`
      if (!isEveryone && !this.insertedMentions.some((item) => item.user_id === memberId)) {
        this.insertedMentions.push({
          user_id: memberId,
          name,
          email: this.chatUserEmail(member)
        })
      }
      this.closeMentionMenu()
      this.$nextTick(() => {
        const nextTextarea = this.messageTextarea()
        if (!nextTextarea) return
        const nextCursor = before.length + insertedText.length
        nextTextarea.focus()
        nextTextarea.setSelectionRange(nextCursor, nextCursor)
      })
    },
    insertAIMentionToken (member) {
      if (!member || !member.token || this.mentionStart < 0) return
      const textarea = this.messageTextarea()
      const cursor = textarea ? textarea.selectionStart : this.inputContent.length
      const before = this.inputContent.slice(0, this.mentionStart)
      const after = this.inputContent.slice(cursor)
      const insertedText = `@${member.token} `
      this.inputContent = `${before}${insertedText}${after}`
      this.closeMentionMenu()
      this.$nextTick(() => {
        const nextTextarea = this.messageTextarea()
        if (!nextTextarea) return
        const nextCursor = before.length + insertedText.length
        nextTextarea.focus()
        nextTextarea.setSelectionRange(nextCursor, nextCursor)
      })
    },
    parseAIMention (content) {
      const text = String(content || '')
      const match = text.match(/(^|[\s\n])@(AI|DeepSeek|OpenAI)(?=$|[\s\n，,。.!！?？])/i)
      if (!match) return null
      const candidate = AI_MENTION_CANDIDATES.find((item) => item.token.toLowerCase() === match[2].toLowerCase())
      if (!candidate) return null
      const question = text
        .replace(match[0], ' ')
        .replace(/\s+/g, ' ')
        .trim()
      return { token: candidate.token, model: candidate.model, question }
    },
    closeMentionMenu () {
      this.mentionMenuVisible = false
      this.mentionQuery = ''
      this.mentionStart = -1
      this.mentionActiveIndex = 0
    },
    deferCloseMentionMenu () {
      window.setTimeout(() => this.closeMentionMenu(), 150)
    },
    messageMentionsFromContent (content) {
      if (!this.isCurrentGroup || !content) return []
      const mentions = []
      const mentionsEveryone = this.messageMentionsEveryone(content)
      const addMention = (user, force = false) => {
        const memberId = this.chatUserId(user)
        const name = this.chatUserName(user)
        if (!memberId || !name || memberId === this.user_id) return
        if (!force && !content.includes(`@${name}`)) return
        if (mentions.some((item) => item.user_id === memberId)) return
        mentions.push({
          user_id: memberId,
          name,
          email: this.chatUserEmail(user)
        })
      }
      this.insertedMentions.forEach((user) => addMention(user))
      this.groupMembers.forEach((user) => addMention(user))
      if (mentionsEveryone) this.groupMembers.forEach((user) => addMention(user, true))
      return mentions
    },
    messageMentionsEveryone (content) {
      return this.isCurrentGroup && /(^|[\s\n])@(?:所有人|全体|everyone|all)(?=$|[\s\n，,。.!！?？])/i.test(content || '')
    },
    handleGlobalKeydown (event) {
      if (event.key !== 'Escape') return
      if (this.previewImage) {
        this.closeImagePreview()
      } else if (this.groupPanelOpen) {
        this.closeGroupPanel()
      } else if (this.historySearchPanelVisible) {
        this.closeConversationHistorySearch()
      } else if (this.conversationFilesVisible) {
        this.closeConversationFiles()
      } else if (this.conversationTodosVisible) {
        this.conversationTodosVisible = false
      }
    },
    openImagePreview (imageData) {
      if (imageData) this.previewImage = imageData
    },
    closeImagePreview () {
      this.previewImage = ''
    },
    chatUserId (user) {
      if (!user) return ''
      return String(
        (user._id && (user._id.$id || user._id.$oid)) ||
          user.user_id ||
          user.id ||
          ''
      )
    },
    chatUserName (user) {
      if (!user) return ''
      const fullName = `${user.user_lastName || ''}${user.user_givenName || ''}`.trim()
      return user.username || user.user_name || fullName || this.chatUserEmail(user) || '未命名同事'
    },
    chatUserEmail (user) {
      if (!user) return ''
      return user.user_email || user.email || user.employee_email || ''
    },
    chatUserInitial (user) {
      return this.chatUserName(user).trim().slice(0, 1).toUpperCase() || '同'
    },
    userProfileSubtitle (user) {
      return this.profileUserRole(user) || this.chatUserId(user) || '查看详细档案'
    },
    profileUserRole (user) {
      if (!user) return ''
      return user.title || user.position || user.user_position || user.employee_position ||
        user.department || user.user_department || user.employee_department || ''
    },
    openUserProfile (user) {
      const userId = this.chatUserId(user)
      if (!userId) return
      if (!this.userInfoMap[userId]) this.$set(this.userInfoMap, userId, user)
      this.profileUserId = userId
      this.userProfileVisible = true
    },
    openActivePeerProfile () {
      if (!this.activePeerId) return
      this.openUserProfile(this.userInfoMap[this.activePeerId] || {
        user_id: this.activePeerId,
        user_name: this.getUserName(this.activePeerId)
      })
    },
    openMessageSenderProfile (message) {
      if (this.isAIMessage(message)) return
      const senderId = String((message && message.user_id) || '')
      if (!senderId) return
      this.openUserProfile(this.messageSenderUser(message))
    },
    openCreateGroupPanel () {
      this.groupPanelMode = 'create'
      this.groupName = ''
      this.groupMemberSearch = ''
      this.selectedGroupMemberIds = []
      this.groupPanelOpen = true
    },
    openInviteGroupPanel () {
      if (!this.isCurrentGroup) return
      this.groupPanelMode = 'invite'
      this.groupName = ''
      this.groupMemberSearch = ''
      this.selectedGroupMemberIds = []
      this.groupPanelOpen = true
    },
    openGroupMembersPanel () {
      if (!this.isCurrentGroup) return
      this.groupPanelMode = 'members'
      this.groupName = ''
      this.groupMemberSearch = ''
      this.selectedGroupMemberIds = []
      this.groupPanelOpen = true
    },
    closeGroupPanel () {
      if (this.groupActionLoading) return
      this.groupPanelOpen = false
      this.groupMemberSearch = ''
      this.selectedGroupMemberIds = []
    },
    isGroupMemberSelected (userId) {
      return this.selectedGroupMemberIds.includes(userId)
    },
    groupMemberRoleLabel (user) {
      const userId = this.chatUserId(user)
      if (!userId) return ''
      if (userId === this.groupOwnerId) return userId === this.user_id ? '群主 · 我' : '群主'
      return userId === this.user_id ? '我' : ''
    },
    toggleGroupMember (userId) {
      if (!userId) return
      const selectedIndex = this.selectedGroupMemberIds.indexOf(userId)
      if (selectedIndex !== -1) {
        this.selectedGroupMemberIds.splice(selectedIndex, 1)
        return
      }
      const maximum = this.groupPanelMode === 'invite'
        ? Math.max(0, 100 - this.groupMemberCount)
        : 99
      if (this.selectedGroupMemberIds.length >= maximum) {
        this.$message.warning('群成员不能超过100人')
        return
      }
      this.selectedGroupMemberIds.push(userId)
    },
    async submitGroupPanel () {
      if (!this.canSubmitGroupPanel || this.groupActionLoading) return
      this.groupActionLoading = true
      try {
        const response = this.groupPanelMode === 'invite'
          ? await addGroupConversationMembers({
            user_id: this.user_id,
            conversation_id: this.currentConversationId,
            member_ids: this.selectedGroupMemberIds
          })
          : await addGroupConversation({
            user_id: this.user_id,
            name: this.groupName.trim(),
            group_user_ids: [this.user_id, ...this.selectedGroupMemberIds]
          })
        const result = response.data || {}
        if (result.code !== 0 || !result.data) {
          throw new Error(result.msg || '群聊操作失败')
        }

        const conversation = result.data
        const conversationId = conversation._id.$id
        const existingIndex = this.chat_list.findIndex((item) => item._id.$id === conversationId)
        if (existingIndex === -1) {
          this.chat_list.unshift(conversation)
        } else {
          this.$set(this.chat_list, existingIndex, conversation)
        }
        const wasCreate = this.groupPanelMode === 'create'
        this.groupActionLoading = false
        this.closeGroupPanel()
        if (wasCreate || this.currentConversationId === conversationId) {
          this.startChat(conversation)
        }
        this.$message.success(wasCreate ? '群聊已创建' : '成员已加入群聊')
        if (result.realtime && result.realtime.status === 'error') {
          this.$message.warning('群聊已保存，其他成员刷新页面后即可看到')
        }
      } catch (error) {
        this.$message.error(error.message || '群聊操作失败，请稍后重试')
      } finally {
        this.groupActionLoading = false
      }
    },
    talkToSomeone (e) {
      const conversation = this.chat_list.find((item) => {
        return (
          item.type === 'private' &&
          (item.group_user_ids[0] === e || item.group_user_ids[1] === e)
        )
      })
      if (conversation) {
        this.startChat(conversation)
      } else {
        talkToSomeone({
          user_id: this.user_id,
          to_id: e
        }).then((res) => {
          if (res.data.code === 0) {
            this.chat_list.unshift(res.data.data)
            this.startChat(res.data.data)
          }
        })
      }
    },
    startChat (conversation) {
      const conversationId = conversation._id.$id
      const isConversationSwitch = Boolean(this.currentConversationId) && this.currentConversationId !== conversationId
      const messageCache = this.getConversationMessageCache(conversationId)
      this.closeImagePreview()
      this.conversationTodosVisible = false
      this.exitMultiSelectMode()
      if (isConversationSwitch) {
        this.persistComposerDraftsForConversation(this.currentConversationId)
        this.removePendingAttachment()
        this.clearQuotedMessage()
        this.closeMentionMenu()
        this.insertedMentions = []
        this.voiceInputVisible = false
        this.aiPendingCount = 0
      }
      clearTimeout(this.focusMessageTimer)
      this.focusedMessageId = ''
      this.currentConversationId = conversationId
      this.currentConversation = conversation
      this.resetHistorySearch()
      this.resetConversationFiles()
      this.messageList = messageCache.messages
      this.resetHistorySearch()
      this.loadComposerDraftsForConversation(conversationId)
      this.peerPresenceState = this.activePeerId ? 'checking' : 'unknown'
      this.peerPresenceUpdatedAt = 0
      clearTimeout(this.groupPresenceTimer)
      this.subscribePeerPresence()
      this.refreshPeerPresence({ force: true })
      this.refreshGroupPresence({ force: true })
      this.loadConversationMessages(conversationId).catch(() => {
        this.$message.error('聊天记录加载失败，请稍后重试')
      })
    },
    subscribePeerPresence () {
      const userIds = Array.from(new Set([
        ...(this.isCurrentGroup ? this.groupMemberIds : []),
        ...(this.activePeerId ? [this.activePeerId] : []),
        ...this.privateConversationPeerIds()
      ].filter(Boolean)))
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
      this.ws.send(JSON.stringify({
        type: 'presence_subscribe',
        user_ids: userIds
      }))
    },
    privateConversationPeerIds () {
      return this.chat_list
        .filter((conversation) => !this.isGroupConversation(conversation))
        .map((conversation) => this.conversationPeerId(conversation))
        .filter(Boolean)
    },
    scheduleConversationPresenceRefresh () {
      clearTimeout(this.conversationPresenceTimer)
      if (!this.destroyed && this.privateConversationPeerIds().length) {
        this.conversationPresenceTimer = setTimeout(() => this.refreshConversationPresence(), 30000)
      }
    },
    async refreshConversationPresence (options = {}) {
      const peerIds = Array.from(new Set(this.privateConversationPeerIds()))
      if (!peerIds.length) {
        clearTimeout(this.conversationPresenceTimer)
        return
      }
      const now = Date.now()
      if (this.conversationPresenceRefreshing) return
      if (!options.force && now - this.conversationPresenceLastRequestedAt < 10000) return
      clearTimeout(this.conversationPresenceTimer)
      this.conversationPresenceRefreshing = true
      this.conversationPresenceLastRequestedAt = now
      try {
        const results = await Promise.all(peerIds.map((peerId) => {
          return getChatOnlineStatus({
            user_id: this.user_id,
            target_user_id: peerId
          }).then((response) => {
            const result = response.data || {}
            const status = result.code === 0 ? (result.data || result) : {}
            return { peerId, online: status.online, lastSeen: status.last_seen }
          }).catch(() => ({ peerId, online: null, lastSeen: null }))
        }))
        results.forEach((item) => this.applyMemberPresence(item.peerId, item.online, item.lastSeen))
      } finally {
        this.conversationPresenceRefreshing = false
        this.scheduleConversationPresenceRefresh()
      }
    },
    scheduleGroupPresenceRefresh () {
      clearTimeout(this.groupPresenceTimer)
      if (!this.destroyed && this.isCurrentGroup && this.groupMemberIds.length) {
        this.groupPresenceTimer = setTimeout(() => this.refreshGroupPresence(), 30000)
      }
    },
    async refreshGroupPresence (options = {}) {
      if (!this.isCurrentGroup || !this.groupMemberIds.length) {
        clearTimeout(this.groupPresenceTimer)
        return
      }
      const now = Date.now()
      if (this.groupPresenceRefreshing) return
      if (!options.force && now - this.groupPresenceLastRequestedAt < 10000) return
      clearTimeout(this.groupPresenceTimer)
      const requestId = this.groupPresenceRequestId + 1
      const conversationId = this.currentConversationId
      this.groupPresenceRequestId = requestId
      this.groupPresenceRefreshing = true
      this.groupPresenceLastRequestedAt = now
      try {
        const memberIds = this.groupMemberIds.slice()
        const results = await Promise.all(memberIds.map((memberId) => {
          if (memberId === this.user_id) {
            return Promise.resolve({ memberId, online: true, lastSeen: Math.floor(Date.now() / 1000) })
          }
          return getChatOnlineStatus({
            user_id: this.user_id,
            target_user_id: memberId
          }).then((response) => {
            const result = response.data || {}
            const status = result.code === 0 ? (result.data || result) : {}
            return { memberId, online: status.online, lastSeen: status.last_seen }
          }).catch(() => ({ memberId, online: null, lastSeen: null }))
        }))
        if (this.groupPresenceRequestId !== requestId || this.currentConversationId !== conversationId) return
        results.forEach((item) => this.applyMemberPresence(item.memberId, item.online, item.lastSeen))
      } finally {
        if (this.groupPresenceRequestId === requestId) {
          this.groupPresenceRefreshing = false
        }
        this.scheduleGroupPresenceRefresh()
      }
    },
    async refreshPeerPresence (options = {}) {
      const peerId = this.activePeerId
      if (!peerId) {
        clearTimeout(this.peerPresenceTimer)
        this.peerPresenceState = 'unknown'
        return
      }
      const now = Date.now()
      if (this.peerPresenceInFlightPeerId === peerId) return
      if (!options.force && now - this.peerPresenceLastRequestedAt < 10000) return
      clearTimeout(this.peerPresenceTimer)
      const requestId = this.peerPresenceRequestId + 1
      this.peerPresenceRequestId = requestId
      this.peerPresenceInFlightPeerId = peerId
      this.peerPresenceLastRequestedAt = now
      if (this.peerPresenceState !== 'online' && this.peerPresenceState !== 'offline') {
        this.peerPresenceState = 'checking'
      }
      try {
        const response = await getChatOnlineStatus({
          user_id: this.user_id,
          target_user_id: peerId
        })
        if (this.peerPresenceRequestId !== requestId || this.activePeerId !== peerId) return
        const result = response.data || {}
        if (result.code === 0) {
          const status = result.data || result
          this.applyPeerPresence(peerId, status.online, status.last_seen)
        } else {
          this.applyPeerPresence(peerId, null)
        }
      } catch (error) {
        if (this.peerPresenceRequestId === requestId && this.activePeerId === peerId) {
          this.applyPeerPresence(peerId, null)
        }
      } finally {
        if (this.peerPresenceInFlightPeerId === peerId) {
          this.peerPresenceInFlightPeerId = ''
        }
        if (!this.destroyed && this.activePeerId === peerId) {
          this.peerPresenceTimer = setTimeout(() => this.refreshPeerPresence(), 30000)
        }
      }
    },
    applyPresencePayload (payload) {
      if (!payload) return
      if (payload.type === 'presence' || payload.type === 'user_status' || payload.type === 'online_status') {
        const data = payload.data && typeof payload.data === 'object' ? payload.data : payload
        const userId = data.user_id || data.userId || data.target_user_id
        this.applyMemberPresence(userId, data.online, data.last_seen)
        if (userId === this.activePeerId) {
          this.applyPeerPresence(userId, data.online, data.last_seen)
        }
        return
      }
      if (payload.type === 'online_users' && Array.isArray(payload.user_ids)) {
        const onlineIds = new Set(payload.user_ids.map(String))
        const trackedIds = Array.from(new Set([
          ...this.groupMemberIds,
          ...this.privateConversationPeerIds()
        ]))
        trackedIds.forEach((memberId) => {
          this.applyMemberPresence(memberId, onlineIds.has(memberId))
        })
        if (this.activePeerId) this.applyPeerPresence(this.activePeerId, onlineIds.has(this.activePeerId))
      }
    },
    applyMemberPresence (memberId, online, lastSeen = null) {
      memberId = String(memberId || '')
      if (!memberId) return
      let state = 'unknown'
      if (online === true || online === 1 || online === '1' || online === 'online') {
        state = 'online'
      } else if (online === false || online === 0 || online === '0' || online === 'offline') {
        state = 'offline'
      }
      const lastSeenNumber = Number(lastSeen)
      this.$set(this.groupPresenceMap, memberId, {
        state,
        updatedAt: Number.isFinite(lastSeenNumber) && lastSeenNumber > 0
          ? lastSeenNumber * 1000
          : Date.now()
      })
    },
    memberPresenceState (memberId) {
      if (memberId === this.user_id) return 'online'
      const presence = this.groupPresenceMap[String(memberId || '')]
      return presence ? presence.state : 'unknown'
    },
    memberIsOnline (memberId) {
      return this.memberPresenceState(memberId) === 'online'
    },
    presenceDotClass (memberId) {
      return `is-${this.memberPresenceState(memberId)}`
    },
    presenceTextClass (memberId) {
      return `is-${this.memberPresenceState(memberId)}`
    },
    presenceLabel (memberId) {
      const state = this.memberPresenceState(memberId)
      return {
        online: '在线',
        offline: '离线',
        unknown: '状态未知'
      }[state] || '状态未知'
    },
    applyPeerPresence (peerId, online, lastSeen = null) {
      if (!peerId || peerId !== this.activePeerId) return
      if (online === true || online === 1 || online === '1' || online === 'online') {
        this.peerPresenceState = 'online'
      } else if (online === false || online === 0 || online === '0' || online === 'offline') {
        this.peerPresenceState = 'offline'
      } else {
        this.peerPresenceState = 'unknown'
      }
      const lastSeenNumber = Number(lastSeen)
      this.peerPresenceUpdatedAt = Number.isFinite(lastSeenNumber) && lastSeenNumber > 0
        ? lastSeenNumber * 1000
        : Date.now()
    },
    getConversationMessageCache (conversationId) {
      const id = String(conversationId || '')
      if (!id) return { messages: [], cursor: '', oldestCursor: '', hasMore: false }
      if (!this.messageCache[id]) {
        this.$set(this.messageCache, id, { messages: [], cursor: '', oldestCursor: '', hasMore: false })
      }
      return this.messageCache[id]
    },
    mergeConversationMessages (messageCollections) {
      const byId = new Map()
      const anonymousMessages = []
      messageCollections.forEach((messages) => {
        ;(messages || []).forEach((message) => {
          if (!message) return
          const messageId = this.messageId(message)
          if (messageId) {
            byId.set(messageId, message)
          } else {
            anonymousMessages.push(message)
          }
        })
      })
      return [...byId.values(), ...anonymousMessages].sort((a, b) => {
        const timeDiff = Number(a.create_time || 0) - Number(b.create_time || 0)
        if (timeDiff !== 0) return timeDiff
        return String(this.messageId(a)).localeCompare(String(this.messageId(b)))
      })
    },
    updateConversationMessageCache (conversationId, incomingMessages = [], latestCursor = '') {
      const cache = this.getConversationMessageCache(conversationId)
      cache.messages = this.mergeConversationMessages([cache.messages, incomingMessages])
      if (latestCursor) cache.cursor = latestCursor
      if (this.currentConversationId === conversationId) {
        this.messageList = cache.messages
      }
      return cache
    },
    cacheSocketMessage (data) {
      if (!data || !data.conversation_id) return
      this.updateConversationMessageCache(data.conversation_id, [this.socketMessageFromEvent(data)])
    },
    async loadConversationMessages (conversationId, forceFull = false) {
      const requestId = this.messageListRequestId + 1
      this.messageListRequestId = requestId
      this.messageListLoading = true
      try {
        const messageCache = this.getConversationMessageCache(conversationId)
        const afterCursor = forceFull ? '' : messageCache.cursor
        const res = await getConversationMessages({
          conversation_id: conversationId,
          user_id: this.user_id,
          history_limit: 50,
          ...(afterCursor ? { after_cursor: afterCursor } : {}),
          recent_limit: 50
        })
        if (res.data.code !== 0 && afterCursor && res.data.msg === '消息同步游标无效') {
          messageCache.cursor = ''
          return this.loadConversationMessages(conversationId, true)
        }
        if (
          res.data.code === 0 &&
          this.currentConversationId === conversationId &&
          this.messageListRequestId === requestId
        ) {
          this.applyConversationMessagePage(conversationId, res.data)
          this.currentConversation[`no_read_${this.user_id}`] = false
          this.scrollToLatest()
        }
        return res
      } finally {
        if (this.currentConversationId === conversationId && this.messageListRequestId === requestId) {
          this.messageListLoading = false
        }
      }
    },
    applyConversationMessagePage (conversationId, payload) {
      const syncMode = payload.sync_mode
      const cache = this.updateConversationMessageCache(
        conversationId,
        [...(payload.data || []), ...(payload.recent_messages || [])],
        payload.latest_cursor || ''
      )
      if (syncMode === 'recent') {
        cache.oldestCursor = payload.oldest_cursor || ''
        cache.hasMore = Boolean(payload.has_more && payload.oldest_cursor)
      } else if (syncMode === 'history') {
        cache.hasMore = Boolean(payload.has_more && payload.oldest_cursor)
        cache.oldestCursor = cache.hasMore ? payload.oldest_cursor : ''
      } else if (syncMode === 'full') {
        // 旧后端/兜底：整段历史都在 data 里，无需上翻
        cache.hasMore = false
        cache.oldestCursor = ''
      }
      if (this.currentConversationId === conversationId) {
        this.messageList = cache.messages
      }
      return cache
    },
    onMessageFlowScroll () {
      const flow = this.$refs.messageFlow
      if (!flow || flow.scrollTop > 60) return
      if (this.messageListLoading || this.olderHistoryLoading) return
      const conversationId = this.currentConversationId
      if (!conversationId) return
      const cache = this.getConversationMessageCache(conversationId)
      if (!cache.hasMore || !cache.oldestCursor) return
      this.loadOlderMessages()
    },
    async loadOlderMessages () {
      const conversationId = this.currentConversationId
      if (!conversationId || this.olderHistoryLoading) return
      const cache = this.getConversationMessageCache(conversationId)
      if (!cache.hasMore || !cache.oldestCursor) return
      const requestId = this.olderHistoryRequestId + 1
      this.olderHistoryRequestId = requestId
      this.olderHistoryLoading = true
      try {
        const flow = this.$refs.messageFlow
        const previousHeight = flow ? flow.scrollHeight : 0
        const previousTop = flow ? flow.scrollTop : 0
        const res = await getConversationMessages({
          conversation_id: conversationId,
          user_id: this.user_id,
          before_cursor: cache.oldestCursor,
          history_limit: 50,
          recent_limit: 50
        })
        if (
          this.olderHistoryRequestId !== requestId ||
            this.currentConversationId !== conversationId
        ) return
        if (!res.data || res.data.code !== 0) {
          this.$message.error((res.data && res.data.msg) || '更早消息加载失败，请稍后重试')
          return
        }
        this.applyConversationMessagePage(conversationId, res.data)
        this.$nextTick(() => {
          const updatedFlow = this.$refs.messageFlow
          if (updatedFlow) {
            updatedFlow.scrollTop = updatedFlow.scrollHeight - previousHeight + previousTop
          }
        })
      } catch (error) {
        if (this.olderHistoryRequestId === requestId) {
          this.$message.error('更早消息加载失败，请稍后重试')
        }
      } finally {
        if (this.olderHistoryRequestId === requestId) {
          this.olderHistoryLoading = false
        }
      }
    },
    resetHistorySearch () {
      this.historySearchKeyword = ''
      this.historySearchLoading = false
      this.historySearchResults = []
      this.historySearchPanelOpen = false
      this.historySearchRequestId += 1
    },
    openConversationHistorySearch () {
      if (!this.currentConversationId) {
        this.$message.info('请先选择一个会话')
        return
      }
      this.conversationTodosVisible = false
      this.closeConversationFiles()
      this.historySearchPanelOpen = true
      this.$nextTick(() => {
        const input = this.$refs.historySearchInput
        if (input && input.focus) input.focus()
      })
    },
    clearConversationHistorySearch () {
      this.historySearchKeyword = ''
      this.historySearchLoading = false
      this.historySearchResults = []
      this.historySearchPanelOpen = true
      this.historySearchRequestId += 1
    },
    closeConversationHistorySearch () {
      this.historySearchPanelOpen = false
      this.historySearchResults = []
      this.historySearchLoading = false
      this.historySearchRequestId += 1
    },
    async searchConversationHistory () {
      const keyword = this.trimmedHistorySearchKeyword
      if (!this.currentConversationId) {
        this.$message.info('请先选择一个会话')
        return
      }
      this.historySearchPanelOpen = true
      if (!keyword) {
        this.historySearchResults = []
        return
      }
      const conversationId = this.currentConversationId
      const requestId = this.historySearchRequestId + 1
      this.historySearchRequestId = requestId
      this.historySearchLoading = true
      try {
        const response = await searchConversationMessages({
          conversation_id: conversationId,
          user_id: this.user_id,
          search_word: keyword,
          limit: 50
        })
        if (
          this.historySearchRequestId !== requestId ||
            this.currentConversationId !== conversationId
        ) return
        if (!response.data || response.data.code !== 0) {
          this.historySearchResults = []
          this.$message.error((response.data && response.data.msg) || '聊天记录搜索失败')
          return
        }
        this.historySearchResults = Array.isArray(response.data.data)
          ? response.data.data
          : []
      } catch (error) {
        if (this.historySearchRequestId === requestId) {
          this.historySearchResults = []
          this.$message.error('聊天记录搜索失败，请稍后重试')
        }
      } finally {
        if (this.historySearchRequestId === requestId) {
          this.historySearchLoading = false
        }
      }
    },
    historySearchSnippet (message) {
      const content = String((message && message.content) || '')
      if (!content) {
        if (this.messageType(message) === 'image') return '[图片]'
        if (this.messageType(message) === 'file') return `[文件] ${message.file_name || ''}`.trim()
        return '[无文字内容]'
      }
      const keyword = this.trimmedHistorySearchKeyword.toLowerCase()
      const normalizedContent = content.replace(/\s+/g, ' ').trim()
      if (!keyword) return normalizedContent.slice(0, 120)
      const matchIndex = normalizedContent.toLowerCase().indexOf(keyword)
      if (matchIndex < 0) return normalizedContent.slice(0, 120)
      const start = Math.max(0, matchIndex - 36)
      const end = Math.min(normalizedContent.length, matchIndex + keyword.length + 72)
      return `${start > 0 ? '...' : ''}${normalizedContent.slice(start, end)}${end < normalizedContent.length ? '...' : ''}`
    },
    highlightHistorySearchKeyword (value) {
      const keyword = this.trimmedHistorySearchKeyword
      const escaped = escapeHtml(value)
      if (!keyword) return escaped
      const pattern = new RegExp(`(${escapeRegExp(keyword)})`, 'gi')
      return escaped.replace(pattern, '<mark>$1</mark>')
    },
    selectHistorySearchResult (message) {
      const messageId = this.messageId(message)
      if (!messageId) return
      if (!this.hasMessage(messageId)) {
        this.messageList = this.mergeMessageList([message])
      }
      this.$nextTick(() => {
        if (!this.highlightMessageInFeed(messageId)) {
          this.$message.info('暂时无法定位搜索结果')
        }
      })
    },
    socketMessageFromEvent (data) {
      return {
        _id: { $id: data.message_id },
        conversation_id: data.conversation_id,
        message_type: data.message_type || 'text',
        content: data.content || '',
        user_id: data.user_id,
        create_time: data.create_time,
        mention_user_ids: data.mention_user_ids || data.mentioned_user_ids || [],
        mentioned_user_ids: data.mentioned_user_ids || data.mention_user_ids || [],
        mentions: Array.isArray(data.mentions) ? data.mentions : [],
        mention_all: Boolean(data.mention_all),
        quote_message_id: data.quote_message_id || data.quoted_message_id || data.reply_to_message_id || '',
        quoted_message_id: data.quoted_message_id || data.quote_message_id || data.reply_to_message_id || '',
        reply_to_message_id: data.reply_to_message_id || data.quote_message_id || data.quoted_message_id || '',
        quote_message: data.quote_message || data.quoted_message || data.reply_to_message || null
      }
    },
    getUserName (id) {
      return this.userMap[String(id || '')] || '未知成员'
    },
    isGroupConversation (conversation) {
      if (!conversation || typeof conversation !== 'object') return false
      if (conversation.type === 'group') return true
      if (conversation.type === 'private') return false
      const memberIds = Array.isArray(conversation.group_user_ids)
        ? conversation.group_user_ids
        : []
      return memberIds.length > 2 || Boolean(conversation.name && (conversation.owner_id || conversation.created_by))
    },
    getConversationName (item) {
      if (!item) return ''
      if (!this.isGroupConversation(item)) {
        const userId = this.conversationPeerId(item)
        return this.userMap[userId] || item.name || '未知同事'
      }
      return item.name || '未命名群聊'
    },
    conversationPeerId (conversation) {
      if (!conversation || this.isGroupConversation(conversation)) return ''
      const memberIds = Array.isArray(conversation.group_user_ids) ? conversation.group_user_ids.map(String) : []
      return memberIds.find((id) => id !== this.user_id) || ''
    },
    conversationPresenceLabel (conversation) {
      return this.presenceLabel(this.conversationPeerId(conversation))
    },
    conversationPresenceTitle (conversation) {
      const peerId = this.conversationPeerId(conversation)
      const state = this.memberPresenceState(peerId)
      const presence = this.groupPresenceMap[String(peerId || '')]
      if (state === 'unknown') return '在线状态未知'
      if (!presence || !presence.updatedAt) return this.presenceLabel(peerId)
      if (state === 'offline') {
        return `离线 · 最后在线 ${this.$dayjs(presence.updatedAt).format('HH:mm:ss')}`
      }
      return `在线 · ${this.$dayjs(presence.updatedAt).format('HH:mm:ss')}`
    },
    messageSenderUser (message) {
      if (!message) return {}
      const senderId = String(message.user_id || '')
      return this.userInfoMap[senderId] || {
        user_id: senderId,
        user_name: message.sender_name || '',
        user_email: message.sender_email || ''
      }
    },
    messageSenderName (message) {
      if (!message) return '未知成员'
      if (this.isAIMessage(message)) return 'AI 助手'
      const senderId = String(message.user_id || '')
      const sender = this.messageSenderUser(message)
      const senderName = this.chatUserName(sender)
      if (senderId === this.user_id) {
        return senderName && senderName !== '未命名同事' ? `${senderName}（我）` : '我'
      }
      return senderName && senderName !== '未命名同事' ? senderName : '未知成员'
    },
    messageSenderEmail (message) {
      return this.chatUserEmail(this.messageSenderUser(message))
    },
    messageSenderInitial (message) {
      if (this.isAIMessage(message)) return 'AI'
      const name = this.messageSenderName(message).replace('（我）', '')
      return name.trim().slice(0, 1).toUpperCase() || '同'
    },
    messageSenderTitle (message) {
      if (this.isAIMessage(message)) return `AI 助手 · ${this.aiMessageModelLabel(message)}`
      return [this.messageSenderName(message), this.messageSenderEmail(message)]
        .filter(Boolean)
        .join(' · ')
    },
    todoOwnerLabel (userId, fallbackLabel = '') {
      if (!userId) return fallbackLabel
      if (userId === this.user_id) {
        const name = this.userMap[userId] || fallbackLabel || '我'
        return name === '我' ? name : `${name}（我）`
      }
      const user = this.userInfoMap[userId] || {}
      const name = this.userMap[userId] || fallbackLabel || userId
      const email = user.user_email || user.email || user.employee_email || ''
      return email ? `${name} · ${email}` : name
    },
    messageTodoId (message) {
      if (!message) return ''
      const id = message.todo_id ||
        message.created_todo_id ||
        (message.todo && (message.todo._id || message.todo.id || message.todo.todo_id)) ||
        ''
      return String(id.$id || id.$oid || id)
    },
    messageTodoTitle (message) {
      const todoId = this.messageTodoId(message)
      const ownerId = String(message && message.todo_owner_id ? message.todo_owner_id : '')
      const ownerName = ownerId ? this.getUserName(ownerId) : ''
      return ownerName
        ? `已关联任务，负责人：${ownerName}（任务 ID：${todoId}）`
        : `已关联任务（任务 ID：${todoId}）`
    },
    async openLinkedTodo (message) {
      const messageId = this.messageId(message)
      if (!messageId) return
      this.linkedTodoDialogVisible = true
      this.linkedTodoLoading = true
      this.linkedTodo = null
      try {
        const response = await getChatMessageTodo({
          message_id: messageId,
          user_id: this.user_id
        })
        const result = response && response.data
        if (!result || result.code !== 0 || !result.data || !result.data.todo) {
          throw new Error((result && result.msg) || '无法获取关联任务详情')
        }
        this.linkedTodo = result.data.todo
      } catch (error) {
        this.linkedTodo = null
        this.$message.warning((error && error.message) || '无法获取关联任务详情')
      } finally {
        this.linkedTodoLoading = false
      }
    },
    conversationTodoId (todo) {
      if (!todo) return ''
      const id = todo.todo_id ||
        todo.created_todo_id ||
        (todo.todo && (todo.todo._id || todo.todo.id || todo.todo.todo_id)) ||
        todo._id ||
        todo.id ||
        ''
      return String(id.$id || id.$oid || id)
    },
    conversationTodoMessageId (todo) {
      if (!todo) return ''
      const id = todo.chat_message_id ||
        todo.message_id ||
        todo._chat_message_id ||
        (todo.message && (todo.message._id || todo.message.id)) ||
        ''
      return String(id.$id || id.$oid || id)
    },
    conversationTodoTime (todo) {
      const time = Number(todo && todo.create_time)
      return Number.isFinite(time) && time > 0
        ? this.$dayjs(time * 1000).format('YYYY-MM-DD HH:mm')
        : '未知时间'
    },
    conversationTodoLabels (todo) {
      return todo && Array.isArray(todo.todo_labels)
        ? todo.todo_labels.filter(Boolean).map(String)
        : []
    },
    conversationTodoOwners (todo) {
      const users = todo && Array.isArray(todo.assign_users) ? todo.assign_users : []
      const names = users
        .map((user) => this.chatUserName(user))
        .filter((name) => name && name !== '未命名同事')
      return names.length ? names.join('、') : '未分配'
    },
    isConversationTodoStatusUpdating (todo) {
      const todoId = this.conversationTodoId(todo)
      return Boolean(todoId && this.conversationTodoStatusUpdating[todoId])
    },
    applyConversationTodoUpdate (updatedTodo) {
      const todoId = this.conversationTodoId(updatedTodo)
      if (!todoId) return
      const index = this.conversationTodos.findIndex((todo) => this.conversationTodoId(todo) === todoId)
      if (index >= 0) this.$set(this.conversationTodos, index, updatedTodo)
      if (this.linkedTodo && this.conversationTodoId(this.linkedTodo) === todoId) {
        this.linkedTodo = updatedTodo
      }
    },
    async toggleConversationTodoStatus (todo) {
      const todoId = this.conversationTodoId(todo)
      if (!todoId || this.isConversationTodoStatusUpdating(todo)) return
      const nextFinishStatus = !todo.finish_status
      this.$set(this.conversationTodoStatusUpdating, todoId, true)
      try {
        const response = await markChatMessageTodo({
          todo_id: todoId,
          message_id: this.conversationTodoMessageId(todo),
          user_id: this.user_id,
          conversation_id: this.currentConversationId,
          finish_status: nextFinishStatus
        })
        const result = response && response.data
        if (!result || result.code !== 0 || !result.data || !result.data.todo) {
          throw new Error((result && result.msg) || '任务状态更新失败')
        }
        this.applyConversationTodoUpdate(result.data.todo)
        this.$message.success(nextFinishStatus ? '任务已标记完成' : '任务已重新打开')
      } catch (error) {
        this.$message.error((error && error.message) || '任务状态更新失败')
      } finally {
        this.$delete(this.conversationTodoStatusUpdating, todoId)
      }
    },
    resetConversationFiles () {
      this.conversationFilesVisible = false
      this.conversationFilesLoading = false
      this.conversationFiles = []
      this.conversationFilesRequestId += 1
    },
    closeConversationFiles () {
      this.resetConversationFiles()
    },
    conversationFileId (file) {
      return this.messageId(file) || `${file && file.file_url}-${file && file.create_time}`
    },
    conversationFileName (file) {
      return (file && file.file_name) || '未命名文件'
    },
    conversationFileTime (file) {
      const time = Number(file && file.create_time)
      return Number.isFinite(time) && time > 0
        ? this.$dayjs(time * 1000).format('YYYY-MM-DD HH:mm')
        : '未知时间'
    },
    focusConversationFile (file) {
      const fileMessageId = this.messageId(file)
      if (!fileMessageId) return
      if (!this.hasMessage(fileMessageId)) {
        this.messageList = this.mergeMessageList([file])
      }
      this.$nextTick(() => {
        if (!this.highlightMessageInFeed(fileMessageId)) {
          this.$message.info('暂时无法定位文件消息')
        }
      })
    },
    async openConversationFiles () {
      if (!this.currentConversationId) return
      this.closeConversationHistorySearch()
      this.conversationTodosVisible = false
      this.conversationFilesVisible = true
      this.conversationFilesLoading = true
      this.conversationFiles = []
      const conversationId = this.currentConversationId
      const requestId = this.conversationFilesRequestId + 1
      this.conversationFilesRequestId = requestId
      try {
        const response = await getConversationFiles({
          conversation_id: conversationId,
          user_id: this.user_id,
          limit: 200
        })
        if (
          this.conversationFilesRequestId !== requestId ||
            this.currentConversationId !== conversationId
        ) return
        const result = response && response.data
        if (!result || result.code !== 0 || !Array.isArray(result.data)) {
          throw new Error((result && result.msg) || '无法获取文件列表')
        }
        this.conversationFiles = result.data
      } catch (error) {
        if (this.conversationFilesRequestId === requestId) {
          this.conversationFilesVisible = false
          this.$message.warning((error && error.message) || '无法获取文件列表')
        }
      } finally {
        if (this.conversationFilesRequestId === requestId) {
          this.conversationFilesLoading = false
        }
      }
    },
    async openConversationTodos () {
      if (!this.currentConversationId) return
      this.closeConversationHistorySearch()
      this.closeConversationFiles()
      this.conversationTodosVisible = true
      this.conversationTodosLoading = true
      this.conversationTodos = []
      try {
        const response = await getConversationChatTodos({
          conversation_id: this.currentConversationId,
          user_id: this.user_id
        })
        const result = response && response.data
        if (!result || result.code !== 0 || !Array.isArray(result.data)) {
          throw new Error((result && result.msg) || '无法获取相关任务')
        }
        this.conversationTodos = result.data
      } catch (error) {
        this.conversationTodosVisible = false
        this.$message.warning((error && error.message) || '无法获取相关任务')
      } finally {
        this.conversationTodosLoading = false
      }
    },
    canCreateTodoFromMessage (message) {
      return Boolean(
        !this.isSystemMessage(message) &&
          !this.messageTodoId(message) &&
          this.currentConversationId &&
          this.messageTodoContent(message).trim()
      )
    },
    canQuoteMessage (message) {
      return Boolean(
        !this.isSystemMessage(message) &&
          !this.isAIMessageStreaming(message) &&
          this.currentConversationId &&
          this.messageId(message)
      )
    },
    quoteMessage (message) {
      if (!this.canQuoteMessage(message)) return
      this.quotedMessage = message
      this.focusMessageInput()
    },
    clearQuotedMessage () {
      this.quotedMessage = null
    },
    quoteSenderLabel (message) {
      if (!message) return ''
      if (message.user_id === this.user_id) return '我'
      return this.messageSenderName(message)
    },
    quoteMessageSummary (message) {
      if (!message) return ''
      const type = this.messageType(message)
      if (type === 'image') return '[图片]'
      if (type === 'file') return `[文件] ${message.file_name || '未命名文件'}`
      const prefix = this.isAIMessage(message) ? '[AI回复] ' : ''
      const content = String(message.content || '').replace(/\s+/g, ' ').trim()
      if (!content) return '这条消息'
      const clipped = content.length > 80 ? `${content.slice(0, 80)}...` : content
      return `${prefix}${clipped}`
    },
    quotePayloadFromMessage (message) {
      const messageId = this.messageId(message)
      if (!messageId) return null
      return {
        message_id: messageId,
        user_id: String(message.user_id || ''),
        sender_name: this.quoteSenderLabel(message),
        message_type: this.messageType(message),
        content: this.quoteMessageSummary(message),
        create_time: message.create_time || 0
      }
    },
    canAskAIMessage (message) {
      return Boolean(
        !this.isSystemMessage(message) &&
          !this.isAIMessageStreaming(message) &&
          this.currentConversationId &&
          this.messageId(message)
      )
    },
    openAskAIDialog (message) {
      if (!this.canAskAIMessage(message)) return
      this.askAITargetMessage = message
      this.askAIModel = 'deepseek'
      this.askAIQuestion = ''
      this.askAIDialogVisible = true
    },
    resetAskAIDialog () {
      this.askAITargetMessage = null
      this.askAIQuestion = ''
    },
    submitAskAI () {
      if (!this.canSubmitAskAI) return
      const sourceMessageId = this.messageId(this.askAITargetMessage)
      const payload = {
        trigger: 'button',
        model: this.askAIModel,
        question: this.askAIQuestion.trim(),
        source_message_id: sourceMessageId,
        context_messages: this.buildAIContextMessages(sourceMessageId)
      }
      this.askAIDialogVisible = false
      this.runAskAI(payload)
    },
    buildAIContextMessages (excludeMessageId) {
      return this.messageList
        .filter((message) => {
          if (this.isSystemMessage(message)) return false
          if (excludeMessageId && this.messageId(message) === excludeMessageId) return false
          const type = this.messageType(message)
          if (!['text', 'email', 'email_message', 'ai', 'ai_message'].includes(type)) return false
          return Boolean(String(message.content || '').trim())
        })
        .slice(-12)
        .map((message) => ({
          sender_name: this.messageSenderName(message),
          content: String(message.content || '').slice(0, 600),
          create_time: Number(message.create_time) || 0
        }))
    },
    runAskAI ({ trigger, model, question, sourceMessageId, contextMessages }) {
      const conversationId = this.currentConversationId
      if (!conversationId) return
      const tempId = `local-ai-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
      const tempMessage = {
        _id: { $id: tempId },
        conversation_id: conversationId,
        message_type: 'ai',
        user_id: 'ai_assistant',
        content: '',
        create_time: Math.floor(Date.now() / 1000),
        streaming: true,
        ai_metadata: {
          model: model === 'openai' ? 'OpenAI' : 'DeepSeek',
          trigger
        }
      }
      this.aiPendingCount += 1
      if (this.currentConversationId === conversationId) {
        this.messageList.push(tempMessage)
        this.scrollToLatest()
      }
      let finished = false
      const controller = new AbortController()
      this.aiStreamControllers.push(controller)
      const finish = (realMessage, errorMessage) => {
        if (finished) return
        finished = true
        const silent = this.destroyed
        this.aiStreamControllers = this.aiStreamControllers.filter((item) => item !== controller)
        if (this.currentConversationId === conversationId) {
          this.messageList = this.messageList.filter((message) => this.messageId(message) !== tempId)
        }
        if (errorMessage) {
          if (!silent) this.$message.error(errorMessage)
        } else if (realMessage) {
          this.appendAIMessage(realMessage)
          if (!silent) this.$message.success('AI 已回复')
        }
        this.aiPendingCount = Math.max(0, this.aiPendingCount - 1)
      }
      fetchEventSource(CHAT_AI_STREAM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        openWhenHidden: true,
        signal: controller.signal,
        body: JSON.stringify({
          user_id: this.user_id,
          conversation_id: conversationId,
          model: model || 'deepseek',
          question: question || '',
          source_message_id: sourceMessageId || '',
          context_messages: contextMessages || [],
          trigger,
          chat_time: Math.floor(Date.now() / 1000)
        }),
        onopen: async (response) => {
          // 不校验 content-type，只看 HTTP 状态，避免代理层改写头导致中断。
          if (!response.ok) throw new Error(`AI 服务连接失败 (${response.status})`)
        },
        onmessage: (event) => {
          if (!event.data || event.data === 'Connection closed') return
          let parsed = null
          try {
            parsed = JSON.parse(event.data)
          } catch (error) {
            return
          }
          if (!parsed || !parsed.type) return
          if (parsed.type === 'delta' && parsed.content) {
            tempMessage.content += parsed.content
            if (this.currentConversationId === conversationId) this.scrollToLatest()
          } else if (parsed.type === 'done') {
            finish(parsed.message || null, null)
          } else if (parsed.type === 'error') {
            finish(null, parsed.message || 'AI 回复失败，请稍后重试')
          }
        },
        onclose: () => {
          if (!finished) finish(null, 'AI 回复中断，请稍后重试')
        },
        onerror: (error) => {
          finish(null, (error && error.message) || 'AI 连接中断，请稍后重试')
          throw error
        }
      }).catch(() => {})
    },
    appendAIMessage (message) {
      if (!message) return
      const conversationId = String(message.conversation_id || '')
      const messageId = this.messageId(message)
      if (this.currentConversationId !== conversationId) {
        const conversation = this.chat_list.find((item) => item._id.$id === conversationId)
        if (conversation) {
          conversation.currentMessage = message.content
          conversation.update_time = message.create_time
          this.moveConversationToTop(conversation)
        }
        return
      }
      if (messageId && this.hasMessage(messageId)) return
      this.messageList.push(message)
      this.scrollToLatest()
    },
    toggleVoiceInput () {
      if (this.composeMode !== 'chat') return
      this.voiceInputVisible = !this.voiceInputVisible
    },
    appendVoiceText (text) {
      if (!text) return
      this.inputContent = `${this.inputContent}${text}`
    },
    messageQuote (message) {
      if (!message) return null
      const quote = message.quote_message ||
        message.quoted_message ||
        message.reply_to_message ||
        message.reply_to ||
        message.reply_message ||
        message.reference_message ||
        message.referenced_message ||
        null
      if (typeof quote !== 'string') return quote
      try {
        const parsedQuote = JSON.parse(quote)
        return parsedQuote && typeof parsedQuote === 'object' ? parsedQuote : null
      } catch (error) {
        return null
      }
    },
    messageQuoteId (message) {
      if (!message) return ''
      const quote = this.messageQuote(message)
      return String(
        message.quote_message_id ||
        message.quoted_message_id ||
        message.reply_to_message_id ||
        message.reply_message_id ||
        message.reference_message_id ||
        message.referenced_message_id ||
        (quote && (quote.message_id || quote.quote_message_id || quote.id)) ||
        ''
      )
    },
    messageQuoteSummary (message) {
      const quote = this.messageQuote(message)
      if (quote) return String(quote.content || quote.summary || '').trim() || '已引用消息'
      return this.messageQuoteId(message) ? '已引用消息' : ''
    },
    messageQuoteSenderLabel (message) {
      const quote = this.messageQuote(message)
      if (!quote) return '引用消息'
      return quote.sender_name || this.getUserName(quote.user_id) || '引用消息'
    },
    quotedMessageInFeed (message) {
      const quoteMessageId = this.messageQuoteId(message)
      if (!quoteMessageId) return null
      return this.messageList.find((item) => this.messageId(item) === quoteMessageId) || null
    },
    messageQuoteDetail (message) {
      const quoteMessageId = this.messageQuoteId(message)
      if (!quoteMessageId) return this.messageQuote(message)
      return this.quotedMessageInFeed(message) ||
        this.quoteDetailMap[quoteMessageId] ||
        this.messageQuote(message)
    },
    messageFullDisplayContent (message) {
      if (!message) return '消息内容不可用'
      const type = this.messageType(message)
      if (type === 'image') return '[图片消息]'
      if (type === 'file') return `[文件] ${message.file_name || '未命名文件'}`
      return String(message.content || '').trim() || '消息内容不可用'
    },
    messageCopyContent (message) {
      if (!message) return ''
      const type = this.messageType(message)
      if (type === 'file') {
        const name = message.file_name || '未命名文件'
        return message.file_url ? `[文件] ${name}\n${message.file_url}` : `[文件] ${name}`
      }
      return this.messageFullDisplayContent(message)
    },
    async copyMessage (message) {
      const content = this.messageCopyContent(message)
      if (!content) {
        this.$message.warning('没有可复制的消息内容')
        return
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(content)
        } else if (!this.copyTextWithFallback(content)) {
          throw new Error('clipboard_unavailable')
        }
        this.$message.success('消息已复制')
      } catch (error) {
        this.$message.error('复制失败，请检查浏览器剪贴板权限')
      }
    },
    copyTextWithFallback (content) {
      const textarea = document.createElement('textarea')
      textarea.value = content
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      textarea.remove()
      return copied
    },
    messageQuoteTooltipContent (message) {
      const quoteMessageId = this.messageQuoteId(message)
      if (quoteMessageId && this.quoteDetailLoading[quoteMessageId]) return '正在加载完整消息...'
      return this.messageFullDisplayContent(this.messageQuoteDetail(message))
    },
    messageQuoteTooltipMeta (message) {
      const detail = this.messageQuoteDetail(message)
      const sender = (detail && detail.sender_name) ||
        (detail && this.messageSenderName(detail)) ||
        this.messageQuoteSenderLabel(message)
      const createTime = detail && detail.create_time
      const time = createTime
        ? this.$dayjs(Number(createTime) * 1000).format('YYYY-MM-DD HH:mm:ss')
        : ''
      return [sender, time].filter(Boolean).join(' · ')
    },
    async preloadQuoteDetails (message) {
      const quoteMessageId = this.messageQuoteId(message)
      if (
        !quoteMessageId ||
        this.quotedMessageInFeed(message) ||
        this.quoteDetailMap[quoteMessageId] ||
        this.quoteDetailLoading[quoteMessageId]
      ) return
      this.$set(this.quoteDetailLoading, quoteMessageId, true)
      try {
        const response = await getChatMessage({
          message_id: quoteMessageId,
          user_id: this.user_id
        })
        if (response.data && response.data.code === 0 && response.data.data) {
          this.$set(this.quoteDetailMap, quoteMessageId, response.data.data)
        }
      } catch (error) {
        // The stored quote preview remains available when the detail request fails.
      } finally {
        this.$delete(this.quoteDetailLoading, quoteMessageId)
      }
    },
    highlightMessageInFeed (messageId) {
      const targetIndex = this.messageList.findIndex(
        (item) => this.messageId(item) === messageId
      )
      if (targetIndex < 0) return false
      this.$nextTick(() => {
        const messageItems = this.$refs.messageItems || []
        const target = Array.isArray(messageItems) ? messageItems[targetIndex] : null
        if (!target) {
          this.$message.info('暂时无法定位引用消息')
          return
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        clearTimeout(this.focusMessageTimer)
        this.focusedMessageId = messageId
        this.focusMessageTimer = setTimeout(() => {
          if (this.focusedMessageId === messageId) this.focusedMessageId = ''
        }, 2200)
      })
      return true
    },
    async focusQuotedMessage (message) {
      const quoteMessageId = this.messageQuoteId(message)
      if (!quoteMessageId) return
      if (this.highlightMessageInFeed(quoteMessageId)) return
      if (this.quoteContextLoading[quoteMessageId]) return

      const conversationId = this.currentConversationId
      this.$set(this.quoteContextLoading, quoteMessageId, true)
      try {
        const response = await getChatMessageContext({
          message_id: quoteMessageId,
          user_id: this.user_id,
          before_count: 5,
          after_count: 5
        })
        if (!response.data || response.data.code !== 0 || !Array.isArray(response.data.data)) {
          this.$message.info((response.data && response.data.msg) || '无法加载引用消息上下文')
          return
        }
        if (this.currentConversationId !== conversationId) return
        this.updateConversationMessageCache(conversationId, response.data.data)
        const targetMessage = response.data.data.find(
          (item) => this.messageId(item) === quoteMessageId
        )
        if (targetMessage) this.$set(this.quoteDetailMap, quoteMessageId, targetMessage)
        if (!this.highlightMessageInFeed(quoteMessageId)) {
          this.$message.info('引用消息不存在或无法定位')
        }
      } catch (error) {
        this.$message.error('引用消息及上下文加载失败')
      } finally {
        this.$delete(this.quoteContextLoading, quoteMessageId)
      }
    },
    defaultTodoOwnerId (message) {
      const mentionedOwnerId = this.messageMentionUserIds(message).find((userId) => {
        return userId === this.user_id ||
          userId === this.activePeerId ||
          this.groupMemberIds.includes(userId)
      })
      if (mentionedOwnerId) return mentionedOwnerId
      if (message && message.user_id && message.user_id !== this.user_id) return this.user_id
      return this.activePeerId || this.user_id
    },
    messageMentionUserIds (message) {
      if (!message) return []
      const ids = []
      const addId = (value) => {
        const userId = String(value || '')
        if (userId && !ids.includes(userId)) ids.push(userId)
      }
      const rawIds = message.mention_user_ids || message.mentioned_user_ids || []
      if (Array.isArray(rawIds)) rawIds.forEach(addId)
      if (Array.isArray(message.mentions)) {
        message.mentions.forEach((mention) => {
          if (mention && typeof mention === 'object') {
            addId(mention.user_id || mention.id || mention._id)
          } else {
            addId(mention)
          }
        })
      }
      return ids
    },
    messageMentionsUser (message, userId) {
      return Boolean(userId && this.messageMentionUserIds(message).includes(String(userId)))
    },
    messageTodoContent (message) {
      if (!message) return ''
      const type = this.messageType(message)
      if (type === 'image') return '[图片消息]'
      if (type === 'file') {
        const name = message.file_name || '未命名文件'
        return message.file_url ? `[文件] ${name}\n${message.file_url}` : `[文件] ${name}`
      }
      return String(message.content || '').trim()
    },
    openTodoDialog (message) {
      const content = this.messageTodoContent(message)
      if (!content) return
      this.todoSourceMessage = message
      this.todoDraft = {
        content,
        ownerId: this.defaultTodoOwnerId(message),
        collaboratorIds: [],
        labels: []
      }
      this.todoDialogVisible = true
      this.loadTodoLabelOptions()
    },
    applyMessageTodoCreated (message, todoId, ownerId, updatedMessage) {
      if (!message || !todoId) return
      const savedMessage = updatedMessage || {}
      this.$set(message, 'todo_id', savedMessage.todo_id || todoId)
      this.$set(message, 'todo_created', savedMessage.todo_created !== false)
      this.$set(message, 'todo_owner_id', savedMessage.todo_owner_id || ownerId || '')
      this.$set(message, 'todo_created_by', savedMessage.todo_created_by || this.user_id)
      this.$set(message, 'todo_create_time', savedMessage.todo_create_time || Math.floor(Date.now() / 1000))
    },
    focusTodoInput () {
      this.$nextTick(() => {
        const input = this.$refs.todoContentInput
        if (input && typeof input.focus === 'function') {
          input.focus()
        }
      })
    },
    normalizeTodoListResponse (response) {
      const data = response && response.data !== undefined ? response.data : response
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      return []
    },
    normalizeTodoLabels (labels) {
      if (!Array.isArray(labels)) return []
      const normalized = []
      labels.forEach((label) => {
        const value = String(label || '').trim().replace(/^#+/, '').trim()
        if (value && !normalized.includes(value)) normalized.push(value)
      })
      return normalized
    },
    normalizeTodoDraftLabels (labels) {
      this.todoDraft.labels = this.normalizeTodoLabels(labels)
    },
    normalizeTodoDraftCollaborators (collaboratorIds) {
      const allowedIds = this.todoOwnerOptions.map((user) => user.id)
      const selectedIds = Array.isArray(collaboratorIds)
        ? collaboratorIds
        : this.todoDraft.collaboratorIds
      this.todoDraft.collaboratorIds = Array.from(new Set(
        (selectedIds || [])
          .map((userId) => String(userId || ''))
          .filter((userId) => userId && userId !== this.todoDraft.ownerId && allowedIds.includes(userId))
      ))
    },
    async loadTodoLabelOptions () {
      this.todoLabelLoading = true
      try {
        const lists = this.normalizeTodoListResponse(await getLists(this.user_id, 'user'))
        const list = lists.find((item) => item && item.list_name === this.todoDefaultListName)
        const listId = list && list._id && (list._id.$id || list._id.$oid || list._id)
        if (!listId) {
          this.todoLabelOptions = []
          return
        }
        const response = await getLabelsByList(listId)
        this.todoLabelOptions = this.normalizeTodoLabels(
          response && response.data !== undefined ? response.data : response
        )
      } catch (error) {
        this.todoLabelOptions = []
      } finally {
        this.todoLabelLoading = false
      }
    },
    async ensureChatTodoList () {
      const lists = this.normalizeTodoListResponse(await getLists(this.user_id, 'user'))
      const existing = lists.find((list) => {
        return list &&
          list.list_name === this.todoDefaultListName &&
          String(list.user_id || '') === String(this.user_id)
      })
      if (existing && existing._id) return existing._id.$id || existing._id.$oid || existing._id

      await updateList('new', this.user_id, 'user', this.todoDefaultListName, [])
      const nextLists = this.normalizeTodoListResponse(await getLists(this.user_id, 'user'))
      const created = nextLists.find((list) => {
        return list &&
          list.list_name === this.todoDefaultListName &&
          String(list.user_id || '') === String(this.user_id)
      })
      if (created && created._id) return created._id.$id || created._id.$oid || created._id
      throw new Error('默认 TODO List 创建失败')
    },
    async createTodoFromMessage () {
      const content = String(this.todoDraft.content || '').trim()
      const labels = this.normalizeTodoLabels(this.todoDraft.labels)
      const collaboratorIds = Array.from(new Set(
        (this.todoDraft.collaboratorIds || [])
          .map((userId) => String(userId || ''))
          .filter((userId) => userId && userId !== this.todoDraft.ownerId)
      ))
      if (!content) {
        this.$message.warning('请输入 TODO 内容')
        return
      }
      if (!this.todoDraft.ownerId) {
        this.$message.warning('请选择负责人')
        return
      }

      this.todoCreating = true
      try {
        const listId = await this.ensureChatTodoList()
        const response = await createChatMessageTodo({
          message_id: this.messageId(this.todoSourceMessage),
          user_id: this.user_id,
          todo_owner_id: this.todoDraft.ownerId,
          todo_collaborator_ids: collaboratorIds,
          list_id: listId,
          content,
          todo_labels: labels
        })
        if (!response.data || response.data.code !== 0 || !response.data.data) {
          throw new Error((response.data && response.data.msg) || 'TODO 创建失败')
        }
        const result = response.data.data
        const todoId = result.todo_id || (result.todo && result.todo._id && (result.todo._id.$id || result.todo._id.$oid || result.todo._id))
        if (!todoId) throw new Error('TODO 已创建，但未返回任务 ID')
        this.applyMessageTodoCreated(
          this.todoSourceMessage,
          todoId,
          this.todoDraft.ownerId,
          result.message
        )
        await this.loadConversationMessages(this.currentConversationId)
        this.$message.success('TODO 已创建并分配')
        this.todoDialogVisible = false
      } catch (error) {
        this.$message.error((error && error.message) || 'TODO 创建失败')
      } finally {
        this.todoCreating = false
      }
    },
    shouldConfirmShortEmailMessage (content, pendingImages, pendingFiles) {
      if (
        this.composeMode !== 'email' ||
        this.isCurrentGroup ||
        this.peerPresenceState !== 'online' ||
        pendingImages.length ||
        pendingFiles.length
      ) return false

      const normalizedContent = String(content || '').replace(/\r\n/g, '\n').trim()
      return Boolean(
        normalizedContent &&
        !normalizedContent.includes('\n') &&
        normalizedContent.length <= 80
      )
    },
    async sendMessage () {
      if (!this.canSend) return
      const content = this.composerContent.trim()
      const emailContentHtml = this.emailContentHtml
      const pendingImages = this.pendingImages.slice()
      const pendingFiles = this.pendingFiles.slice()
      const conversation = this.currentConversation
      const conversationId = this.currentConversationId
      if (this.shouldConfirmShortEmailMessage(content, pendingImages, pendingFiles)) {
        try {
          await this.$confirm(
            '对方当前在线，且邮件内容较短。仍需要发送邮件通知吗？',
            '确认发送邮件消息',
            {
              confirmButtonText: '仍发送邮件',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        } catch (error) {
          return
        }
      }
      const quotePayload = this.quotePayloadFromMessage(this.quotedMessage)
      const mentions = this.messageMentionsFromContent(content)
      const mentionsEveryone = this.messageMentionsEveryone(content)
      const mentionFields = mentions.length
        ? {
          mention_user_ids: mentions.map((item) => item.user_id),
          mentioned_user_ids: mentions.map((item) => item.user_id),
          mentions,
          mention_all: mentionsEveryone
        }
        : {}
      const quoteFields = quotePayload
        ? {
          quote_message_id: quotePayload.message_id,
          quoted_message_id: quotePayload.message_id,
          reply_to_message_id: quotePayload.message_id,
          quote_message: quotePayload
        }
        : {}

      this.sending = true
      try {
        for (const pendingImage of pendingImages) {
          await this.persistChatMessage({
            conversation_id: conversationId,
            message_type: 'image',
            ...quoteFields,
            image_data: pendingImage.image_data,
            image_mime: pendingImage.image_mime,
            image_width: pendingImage.image_width,
            image_height: pendingImage.image_height
          }, conversation)
          this.removeSentPendingImage(pendingImage)
        }
        for (const pendingFile of pendingFiles) {
          this.uploadingFile = true
          this.uploadProgress = 0
          this.attachmentName = pendingFile.name || '文件'
          const uploaded = await this.uploadFileToQiniu(pendingFile)
          await this.persistChatMessage({
            conversation_id: conversationId,
            message_type: 'file',
            ...quoteFields,
            file_url: uploaded.url,
            file_name: pendingFile.name || uploaded.key,
            file_size: pendingFile.size,
            file_mime: pendingFile.type || 'application/octet-stream'
          }, conversation)
          this.removeSentPendingFile(pendingFile)
        }
        if (content) {
          const isEmailMessage = this.composeMode === 'email'
          const sentMessage = await this.persistChatMessage({
            message_type: isEmailMessage ? 'email' : 'text',
            delivery_mode: isEmailMessage ? 'email' : 'chat',
            notify_email: isEmailMessage,
            content_format: isEmailMessage ? 'markdown' : 'plain_text',
            content,
            ...mentionFields,
            ...quoteFields,
            conversation_id: conversationId
          }, conversation)
          if (isEmailMessage) {
            if (this.emailContentHtml === emailContentHtml) {
              this.emailContentHtml = ''
              this.clearComposerDraft(conversationId, 'email')
            }
          } else {
            const aiMention = this.parseAIMention(content)
            const aiSourceMessageId = this.messageId(sentMessage)
            const aiContextMessages = this.buildAIContextMessages(aiSourceMessageId)
            if (this.inputContent.trim() === content) {
              this.inputContent = ''
              this.insertedMentions = []
              this.clearComposerDraft(conversationId, 'chat')
            }
            if (aiMention) {
              this.runAskAI({
                trigger: 'mention',
                model: aiMention.model,
                question: aiMention.question,
                sourceMessageId: aiSourceMessageId,
                contextMessages: aiContextMessages
              })
            }
          }
        }
        if (quotePayload) this.clearQuotedMessage()
      } catch (error) {
        this.$message.error(error.message || '消息发送失败，请稍后重试')
      } finally {
        this.sending = false
        this.uploadingFile = false
        this.uploadProgress = 0
        this.attachmentName = ''
        this.uploadSubscription = null
      }
    },
    async persistChatMessage (payload, originalConversation) {
      const conversationId = payload.conversation_id
      const response = await chatSendMessage({
        ...payload,
        user_id: this.user_id
      })
      if (!response.data || response.data.code !== 0) {
        throw new Error((response.data && response.data.msg) || '消息保存失败')
      }

      let message = response.data.data
      const requestedQuoteId = String(payload.quote_message_id || '')
      const quotePersistence = response.data.quote_persistence || {}
      const returnedQuoteId = this.messageQuoteId(message)
      if (
        requestedQuoteId &&
          (quotePersistence.status === 'failed' || returnedQuoteId !== requestedQuoteId)
      ) {
        try {
          const repairResponse = await repairChatMessageQuote({
            message_id: this.messageId(message),
            quote_message_id: requestedQuoteId,
            user_id: this.user_id
          })
          if (!repairResponse.data || repairResponse.data.code !== 0) {
            throw new Error((repairResponse.data && repairResponse.data.msg) || '引用关系修复失败')
          }
          message = repairResponse.data.data
        } catch (error) {
          this.$message.warning('消息已发送，但引用关系保存失败，请重试引用')
        }
      }
      if (
        message &&
          message.email_notification &&
          ['failed', 'partial'].includes(message.email_notification.status)
      ) {
        this.$message.warning(
          message.email_notification.status === 'partial'
            ? '消息已发送，部分群成员未配置邮箱'
            : '消息已发送，邮件通知失败'
        )
      }
      if (
        this.currentConversationId === conversationId &&
          !this.hasMessage(this.messageId(message))
      ) {
        this.messageList.push(message)
      }
      const conversation = this.chat_list.find(
        (item) => item._id.$id === conversationId
      ) || originalConversation
      if (conversation) {
        conversation.currentMessage = message.content
        conversation.update_time = message.create_time
        this.moveConversationToTop(conversation)
      }
      if (this.currentConversationId === conversationId) this.scrollToLatest()

      if (response.data.realtime && response.data.realtime.status === 'error') {
        this.$message.warning('消息已保存，但实时推送暂不可用')
      }
      return message
    },
    chooseAttachment () {
      if (!this.currentConversationId || this.attachmentBusy || this.sending) return
      const input = this.$refs.attachmentInput
      if (!input) return
      input.value = ''
      input.click()
    },
    async handleAttachment (event) {
      const files = Array.from((event.target && event.target.files) || []).filter(Boolean)
      event.target.value = ''
      if (!files.length || !this.currentConversationId) return

      for (const file of files) {
        await this.prepareAttachmentDraft(file)
      }
    },
    eventHasFiles (event) {
      const types = event && event.dataTransfer && event.dataTransfer.types
      return Boolean(types && Array.from(types).includes('Files'))
    },
    droppedFilesFromEvent (event) {
      const files = event && event.dataTransfer && event.dataTransfer.files
      return Array.from(files || []).filter(Boolean)
    },
    handleComposerDragEnter (event) {
      if (!this.eventHasFiles(event)) return
      this.composerDragDepth += 1
      if (this.canPrepareAttachmentDraft()) {
        this.composerDragActive = true
      }
    },
    handleComposerDragOver (event) {
      if (!this.eventHasFiles(event)) return
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = this.canPrepareAttachmentDraft() ? 'copy' : 'none'
      }
      if (this.canPrepareAttachmentDraft()) {
        this.composerDragActive = true
      }
    },
    handleComposerDragLeave (event) {
      if (!this.eventHasFiles(event)) return
      this.composerDragDepth = Math.max(0, this.composerDragDepth - 1)
      if (this.composerDragDepth === 0) {
        this.composerDragActive = false
      }
    },
    async handleComposerDrop (event) {
      if (!this.eventHasFiles(event)) return
      this.composerDragDepth = 0
      this.composerDragActive = false
      const files = this.droppedFilesFromEvent(event)
      if (!files.length) return
      if (!this.canPrepareAttachmentDraft()) {
        this.$message.warning(this.currentConversationId ? '当前正在处理附件或发送消息' : '请先选择一个会话')
        return
      }
      for (const file of files) {
        await this.prepareAttachmentDraft(file)
      }
    },
    canPrepareAttachmentDraft () {
      return Boolean(this.currentConversationId && !this.attachmentBusy && !this.sending)
    },
    async prepareAttachmentDraft (file) {
      if (!file || !this.canPrepareAttachmentDraft()) return
      if (this.isImageFile(file)) {
        await this.prepareImageDraft(file)
        return
      }

      this.pendingFiles.push(file)
      this.focusMessageInput()
    },
    async handlePaste (event) {
      if (!this.currentConversationId || this.attachmentBusy || this.sending) return
      const clipboardData = event.clipboardData
      if (!clipboardData) return
      const imageFilesFromItems = Array.from(clipboardData.items || [])
        .filter(
          (item) => item.kind === 'file' && /^image\//i.test(item.type)
        )
        .map((item) => item.getAsFile())
        .filter(Boolean)
      const imageFiles = imageFilesFromItems.length
        ? imageFilesFromItems
        : Array.from(clipboardData.files || []).filter((file) => this.isImageFile(file))
      if (!imageFiles.length) return
      event.preventDefault()
      for (const imageFile of imageFiles) {
        await this.prepareImageDraft(imageFile)
      }
    },
    async prepareImageDraft (file) {
      if (!this.currentConversationId || this.attachmentBusy || this.sending) return
      const conversationId = this.currentConversationId
      this.processingImage = true
      this.attachmentName = file.name || '剪贴板图片'
      try {
        const image = await this.compressImage(file)
        if (this.currentConversationId !== conversationId) return
        this.pendingImages.push({
          ...image,
          draft_id: `${Date.now()}-${Math.random().toString(16).slice(2)}`
        })
        this.focusMessageInput()
      } catch (error) {
        this.$message.error(error.message || '图片压缩失败，请稍后重试')
      } finally {
        this.processingImage = false
        this.attachmentName = ''
      }
    },
    imageDraftLabel (image, index) {
      return `${index + 1}/${this.pendingImages.length} · ${this.formatFileSize(this.dataUrlBytes(image.image_data))}`
    },
    fileDraftKey (file, index) {
      return `${file && file.name}-${file && file.size}-${file && file.lastModified}-${index}`
    },
    removePendingImage (index) {
      this.pendingImages.splice(index, 1)
    },
    removeSentPendingImage (image) {
      const index = this.pendingImages.indexOf(image)
      if (index !== -1) {
        this.pendingImages.splice(index, 1)
        return
      }
      this.pendingImages = this.pendingImages.filter((item) => item.draft_id !== image.draft_id)
    },
    removePendingFile (index) {
      this.pendingFiles.splice(index, 1)
    },
    removeSentPendingFile (file) {
      const index = this.pendingFiles.indexOf(file)
      if (index !== -1) {
        this.pendingFiles.splice(index, 1)
      }
    },
    removePendingAttachment () {
      this.pendingImages = []
      this.pendingFiles = []
    },
    focusMessageInput () {
      this.$nextTick(() => {
        if (this.composeMode === 'email') {
          const editor = this.$refs.emailEditor
          if (editor && editor.quill) editor.quill.focus()
          return
        }
        const textarea = this.$el.querySelector('.el-textarea__inner')
        if (textarea) textarea.focus()
      })
    },
    isImageFile (file) {
      return /^image\//i.test(file.type) || /\.(?:jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(file.name)
    },
    async compressImage (file) {
      const objectUrl = URL.createObjectURL(file)
      try {
        const image = await new Promise((resolve, reject) => {
          const element = new Image()
          element.onload = () => resolve(element)
          element.onerror = () => reject(new Error('浏览器无法读取这张图片'))
          element.src = objectUrl
        })
        if (!image.naturalWidth || !image.naturalHeight) {
          throw new Error('图片尺寸无效')
        }

        const initialScale = Math.min(
          1,
          CHAT_IMAGE_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight)
        )
        let width = Math.max(1, Math.round(image.naturalWidth * initialScale))
        let height = Math.max(1, Math.round(image.naturalHeight * initialScale))
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (!context) throw new Error('当前浏览器不支持图片压缩')
        const qualities = [0.82, 0.72, 0.62, 0.52, 0.44, 0.36]

        for (let resizeAttempt = 0; resizeAttempt < 14; resizeAttempt += 1) {
          canvas.width = width
          canvas.height = height
          context.fillStyle = '#ffffff'
          context.fillRect(0, 0, width, height)
          context.drawImage(image, 0, 0, width, height)

          for (const quality of qualities) {
            const imageData = canvas.toDataURL('image/jpeg', quality)
            if (this.dataUrlBytes(imageData) <= CHAT_IMAGE_TARGET_BYTES) {
              return {
                image_data: imageData,
                image_mime: 'image/jpeg',
                image_width: width,
                image_height: height
              }
            }
          }
          width = Math.max(1, Math.round(width * 0.8))
          height = Math.max(1, Math.round(height * 0.8))
        }
        throw new Error('图片压缩失败，请换一种图片格式后重试')
      } finally {
        URL.revokeObjectURL(objectUrl)
      }
    },
    dataUrlBytes (dataUrl) {
      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
      const padding = base64.endsWith('==') ? 2 : (base64.endsWith('=') ? 1 : 0)
      return Math.floor(base64.length * 3 / 4) - padding
    },
    async uploadFileToQiniu (file) {
      const tokenResponse = await getChatQiniuToken()
      const tokenResult = tokenResponse.data
      if (!tokenResult || tokenResult.code !== 0 || !tokenResult.data) {
        throw new Error((tokenResult && tokenResult.msg) || '获取文件上传凭证失败')
      }
      const key = this.createQiniuKey(file.name)
      const result = await new Promise((resolve, reject) => {
        const observable = qiniu.upload(file, key, tokenResult.data)
        this.uploadSubscription = observable.subscribe({
          next: (progress) => {
            const percent = progress && progress.total ? progress.total.percent : 0
            this.uploadProgress = Math.min(99, Math.round(percent || 0))
          },
          error: (error) => {
            reject(new Error((error && error.message) || '文件上传七牛失败'))
          },
          complete: resolve
        })
      })
      this.uploadProgress = 100
      const uploadedKey = result.key || key
      const encodedKey = uploadedKey.split('/').map(encodeURIComponent).join('/')
      return { key: uploadedKey, url: QINIU_FILE_BASE_URL + encodedKey }
    },
    createQiniuKey (fileName) {
      const safeName = (fileName || 'file')
        .replace(/[^\w.\-\u4e00-\u9fa5]+/g, '_')
        .slice(-120)
      const bytes = new Uint8Array(8)
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(bytes)
      } else {
        for (let index = 0; index < bytes.length; index += 1) {
          bytes[index] = Math.floor(Math.random() * 256)
        }
      }
      const random = Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
      const month = new Date().toISOString().slice(0, 7)
      return `chat/${month}/${Date.now()}-${random}-${safeName}`
    },
    applyReadReceipt (receipt) {
      if (!receipt || !receipt.reader_id || !Array.isArray(receipt.message_ids)) return
      const readerId = String(receipt.reader_id)
      const readTime = Number(receipt.read_time) || Math.floor(Date.now() / 1000)
      receipt.message_ids.forEach((messageIdValue) => {
        const messageId = this.messageId(messageIdValue)
        if (!messageId) return
        const realtimeReceipt = {
          ...(this.realtimeReadReceipts[messageId] || {}),
          [readerId]: readTime
        }
        this.$set(this.realtimeReadReceipts, messageId, realtimeReceipt)

        const message = this.messageList.find(
          (item) => this.messageId(item) === messageId
        )
        if (!message) return
        const readBy = this.messageReaderIds(message)
        if (!readBy.includes(readerId)) this.$set(message, 'read_by', [...readBy, readerId])
        const readAt = message.read_at && typeof message.read_at === 'object'
          ? { ...message.read_at }
          : {}
        readAt[readerId] = readTime
        this.$set(message, 'read_at', readAt)
      })
    },
    reactionOptionByKey (key) {
      return MESSAGE_REACTION_OPTIONS.find((reaction) => reaction.key === key) || null
    },
    reactionMapFromMessage (message) {
      const raw = message && message.reactions
      const reactions = {}
      const addReaction = (userId, value) => {
        userId = String(userId || '')
        if (!userId || !value) return
        const key = typeof value === 'string'
          ? value
          : String(value.key || value.reaction || value.reaction_key || '')
        const option = this.reactionOptionByKey(key)
        if (!option) return
        reactions[userId] = {
          user_id: userId,
          key: option.key,
          emoji: option.emoji,
          label: option.label,
          update_time: typeof value === 'object' ? value.update_time : undefined
        }
      }
      if (Array.isArray(raw)) {
        raw.forEach((value) => addReaction(value && (value.user_id || value.userId), value))
      } else if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach((userId) => addReaction(userId, raw[userId]))
      }
      return reactions
    },
    canReactToMessage (message) {
      return Boolean(
        !this.isSystemMessage(message) &&
          this.currentConversationId &&
          this.messageId(message)
      )
    },
    userReactionKey (message) {
      const reactions = this.reactionMapFromMessage(message)
      return reactions[this.user_id] ? reactions[this.user_id].key : ''
    },
    messageReactionBadges (message) {
      const groups = {}
      Object.values(this.reactionMapFromMessage(message)).forEach((reaction) => {
        if (!groups[reaction.key]) {
          const option = this.reactionOptionByKey(reaction.key)
          groups[reaction.key] = {
            key: reaction.key,
            emoji: option ? option.emoji : reaction.emoji,
            label: option ? option.label : reaction.label,
            count: 0,
            reactedByMe: false,
            title: ''
          }
        }
        groups[reaction.key].count += 1
        if (reaction.user_id === this.user_id) groups[reaction.key].reactedByMe = true
      })
      return MESSAGE_REACTION_OPTIONS
        .map((option) => groups[option.key])
        .filter(Boolean)
        .map((reaction) => ({
          ...reaction,
          title: `${reaction.label} · ${reaction.count} 人`
        }))
    },
    applyMessageReaction (payload) {
      if (!payload) return
      const messageId = this.messageId(payload.message || payload)
      const reactions = payload.reactions || (payload.message && payload.message.reactions)
      if (!messageId || reactions === undefined) return
      const message = this.messageList.find((item) => this.messageId(item) === messageId)
      if (!message) return
      this.$set(message, 'reactions', reactions || {})
    },
    setMessageReactionOptimistic (message, reactionKey) {
      const reactions = this.reactionMapFromMessage(message)
      if (reactionKey) {
        const option = this.reactionOptionByKey(reactionKey)
        if (!option) return
        reactions[this.user_id] = {
          user_id: this.user_id,
          key: option.key,
          emoji: option.emoji,
          label: option.label,
          update_time: Math.floor(Date.now() / 1000)
        }
      } else {
        delete reactions[this.user_id]
      }
      this.$set(message, 'reactions', reactions)
    },
    async toggleMessageReaction (message, reaction) {
      if (!this.canReactToMessage(message) || !reaction || !reaction.key) return
      const messageId = this.messageId(message)
      const requestKey = `${messageId}:${reaction.key}`
      if (this.reactionRequestKeys[requestKey]) return
      const nextReactionKey = this.userReactionKey(message) === reaction.key ? '' : reaction.key
      const previousReactions = JSON.parse(JSON.stringify(this.reactionMapFromMessage(message)))
      this.$set(this.reactionRequestKeys, requestKey, true)
      this.setMessageReactionOptimistic(message, nextReactionKey)
      try {
        const response = await reactChatMessage({
          message_id: messageId,
          user_id: this.user_id,
          reaction: nextReactionKey
        })
        if (!response.data || response.data.code !== 0) {
          throw new Error((response.data && response.data.msg) || '表情回复失败')
        }
        const updatedMessage = response.data.data
        if (updatedMessage && updatedMessage.reactions !== undefined) {
          this.$set(message, 'reactions', updatedMessage.reactions || {})
        }
        const systemMessage = response.data.system_message
        if (
          systemMessage &&
            systemMessage.target_user_id === this.user_id &&
            this.currentConversationId === systemMessage.conversation_id &&
            !this.hasMessage(this.messageId(systemMessage))
        ) {
          this.messageList.push(systemMessage)
          this.scrollToLatest()
        }
      } catch (error) {
        this.$set(message, 'reactions', previousReactions)
        this.$message.error(error.message || '表情回复失败，请稍后重试')
      } finally {
        this.$delete(this.reactionRequestKeys, requestKey)
      }
    },
    canEditMessage (message) {
      return Boolean(
        message &&
          message.user_id === this.user_id &&
          this.messageType(message) === 'text' &&
          this.messageId(message) &&
          !this.isSystemMessage(message)
      )
    },
    isSelectableMessage (message) {
      return Boolean(
        message &&
          !this.isSystemMessage(message) &&
          this.messageId(message)
      )
    },
    isMessageSelected (message) {
      return this.selectedMessageIds.includes(this.messageId(message))
    },
    enterMultiSelectMode (message) {
      if (!this.isSelectableMessage(message)) return
      this.multiSelectMode = true
      this.selectedMessageIds = [this.messageId(message)]
    },
    exitMultiSelectMode () {
      this.multiSelectMode = false
      this.selectedMessageIds = []
      this.selectedEmailRecipientDialogVisible = false
      this.selectedEmailRecipientIds = []
    },
    toggleMessageSelection (message) {
      const messageId = this.messageId(message)
      if (!this.isSelectableMessage(message)) return
      if (this.selectedMessageIds.includes(messageId)) {
        this.selectedMessageIds = this.selectedMessageIds.filter((id) => id !== messageId)
      } else {
        this.selectedMessageIds = [...this.selectedMessageIds, messageId]
      }
    },
    toggleSelectAllMessages () {
      this.selectedMessageIds = this.allSelectableMessagesSelected
        ? []
        : [...this.selectableMessageIds]
    },
    selectedMessagesForEmail () {
      const selectedIds = new Set(this.selectedMessageIds.map(String))
      return this.messageList.filter((message) => selectedIds.has(this.messageId(message)))
    },
    selectedMentionEmailRecipientIds () {
      const recipientIds = []
      const addRecipient = (userId) => {
        const id = String(userId || '')
        if (!id || id === this.user_id || recipientIds.includes(id)) return
        if (!this.groupMemberIds.includes(id)) return
        recipientIds.push(id)
      }
      this.selectedMessagesForEmail().forEach((message) => {
        const content = String((message && message.content) || '')
        if ((message && message.mention_all) || this.messageMentionsEveryone(content)) {
          this.groupMemberIds.forEach(addRecipient)
          return
        }
        this.messageMentionUserIds(message).forEach(addRecipient)
        this.groupMembers.forEach((member) => {
          const memberId = this.chatUserId(member)
          const name = this.chatUserName(member)
          if (name && content.includes(`@${name}`)) addRecipient(memberId)
        })
      })
      return recipientIds
    },
    async emailSelectedMessages () {
      if (!this.currentConversationId || !this.selectedMessageIds.length || this.selectedMessagesEmailSending) return
      if (this.isCurrentGroup) {
        const mentionRecipientIds = this.selectedMentionEmailRecipientIds()
        if (mentionRecipientIds.length) {
          await this.sendSelectedMessagesEmailTo(mentionRecipientIds)
          return
        }
        this.selectedEmailRecipientIds = []
        this.selectedEmailRecipientDialogVisible = true
        if (!this.selectedEmailRecipientOptions.length) {
          this.$message.warning('当前群成员没有可用邮箱')
        }
        return
      }
      await this.sendSelectedMessagesEmailTo([])
    },
    async submitSelectedMessagesEmailRecipients () {
      if (!this.selectedEmailRecipientIds.length) return
      await this.sendSelectedMessagesEmailTo(this.selectedEmailRecipientIds)
    },
    async sendSelectedMessagesEmailTo (targetUserIds) {
      if (!this.currentConversationId || !this.selectedMessageIds.length || this.selectedMessagesEmailSending) return
      this.selectedMessagesEmailSending = true
      try {
        const response = await sendSelectedChatMessagesEmail({
          conversation_id: this.currentConversationId,
          user_id: this.user_id,
          message_ids: this.selectedMessageIds,
          target_user_ids: targetUserIds
        })
        const result = response.data || {}
        if (result.code !== 0) {
          throw new Error(result.msg || '邮件通知发送失败')
        }
        const sentCount = result.data && result.data.sent_count ? result.data.sent_count : 0
        this.$message.success(sentCount ? `已发送邮件通知 ${sentCount} 人` : '已将所选消息发送邮件通知对方')
        this.selectedEmailRecipientDialogVisible = false
        this.selectedEmailRecipientIds = []
        this.exitMultiSelectMode()
        if (result.realtime && result.realtime.status === 'error') {
          this.$message.warning('邮件已发送，但实时通知暂不可用')
        }
      } catch (error) {
        this.$message.error(error.message || '邮件通知发送失败，请稍后重试')
      } finally {
        this.selectedMessagesEmailSending = false
      }
    },
    openMessageEditDialog (message) {
      if (!this.canEditMessage(message)) return
      this.messageEditTarget = message
      this.messageEditContent = message.content || ''
      this.messageEditDialogVisible = true
    },
    focusMessageEditInput () {
      const input = this.$refs.messageEditInput
      if (input && input.focus) input.focus()
    },
    applyEditedChatMessage (updatedMessage) {
      const messageId = this.messageId(updatedMessage)
      if (!messageId) return
      const message = this.messageList.find((item) => this.messageId(item) === messageId)
      if (!message) return
      Object.keys(updatedMessage).forEach((key) => {
        if (key === '_id') return
        this.$set(message, key, updatedMessage[key])
      })
    },
    applyIncomingSystemMessage (message, conversation) {
      if (!this.isSystemMessage(message)) return
      if (message.system_type === 'message_edited' && message.edited_message_id) {
        this.applyEditedChatMessage({
          _id: { $id: message.edited_message_id },
          content: message.edited_content || '',
          edited: true,
          edited_at: message.edited_at,
          edited_by: message.edited_by,
          edit_count: message.edit_count
        })
      }
      if (
        message.system_type === 'message_edited' ||
        message.system_type === 'selected_messages_email'
      ) {
        this.showIncomingNotification(message, conversation)
      }
    },
    async submitMessageEdit () {
      const target = this.messageEditTarget
      const content = this.messageEditContent
      if (!this.canEditMessage(target)) {
        this.$message.error('只能编辑自己发送的文字消息')
        return
      }
      if (!String(content || '').trim()) {
        this.$message.warning('消息内容不能为空')
        return
      }
      this.messageEditSubmitting = true
      try {
        const response = await editChatMessage({
          message_id: this.messageId(target),
          user_id: this.user_id,
          content
        })
        const result = response.data || {}
        if (result.code !== 0 || !result.data) {
          throw new Error(result.msg || '消息编辑失败')
        }
        this.applyEditedChatMessage(result.data)
        this.messageEditDialogVisible = false
        this.messageEditTarget = null
        this.messageEditContent = ''
        this.$message.success('消息已修改')
        if (result.realtime && result.realtime.status === 'error') {
          this.$message.warning('消息已修改，但实时通知暂不可用')
        }
      } catch (error) {
        this.$message.error(error.message || '消息编辑失败，请稍后重试')
      } finally {
        this.messageEditSubmitting = false
      }
    },
    messageReaderIds (message) {
      const readBy = message && message.read_by
      if (Array.isArray(readBy)) return readBy.map(String)
      if (readBy && typeof readBy === 'object') return Object.values(readBy).map(String)
      return []
    },
    shouldShowMessageReceipt (message) {
      return Boolean(
        !this.isSystemMessage(message) &&
        message &&
          message.user_id === this.user_id &&
          this.messageId(message) &&
          this.messageReceiptTargetIds().length
      )
    },
    messageReceiptTargetIds () {
      if (this.isCurrentGroup) {
        return this.groupMemberIds.filter((memberId) => memberId !== this.user_id)
      }
      return this.activePeerId ? [this.activePeerId] : []
    },
    messageReceiptReadIds (message) {
      const realtimeReceipt = this.realtimeReadReceipts[this.messageId(message)] || {}
      return [...new Set([
        ...this.messageReaderIds(message),
        ...Object.keys(realtimeReceipt)
      ])]
    },
    messageReceiptReadTime (message, memberId) {
      const storedReadAt = message && message.read_at && typeof message.read_at === 'object'
        ? Number(message.read_at[memberId])
        : 0
      const realtimeReadAt = Number(
        (this.realtimeReadReceipts[this.messageId(message)] || {})[memberId]
      )
      return storedReadAt || realtimeReadAt || 0
    },
    messageReceiptMemberName (memberId) {
      return String(memberId) === this.user_id ? '我' : this.getUserName(memberId)
    },
    messageReceiptReadMembers (message) {
      const readIds = new Set(this.messageReceiptReadIds(message))
      return this.messageReceiptTargetIds()
        .filter((memberId) => readIds.has(memberId))
        .map((memberId) => ({
          id: memberId,
          name: this.messageReceiptMemberName(memberId),
          readAt: this.messageReceiptReadTime(message, memberId)
        }))
    },
    messageReceiptUnreadMembers (message) {
      const readIds = new Set(this.messageReceiptReadIds(message))
      return this.messageReceiptTargetIds()
        .filter((memberId) => !readIds.has(memberId))
        .map((memberId) => ({
          id: memberId,
          name: this.messageReceiptMemberName(memberId)
        }))
    },
    formatMessageReceiptReadTime (timestamp) {
      return this.$dayjs(Number(timestamp) * 1000).format('MM-DD HH:mm')
    },
    groupMessageReceiptStats (message) {
      const targetIds = this.messageReceiptTargetIds()
      const readIds = new Set(this.messageReceiptReadIds(message))
      const readCount = targetIds.filter((memberId) => readIds.has(memberId)).length
      return { targetIds, readCount, total: targetIds.length }
    },
    isMessageReadByPeer (message) {
      const peerId = this.activePeerId
      if (!peerId || !message) return false
      return this.messageReceiptReadIds(message).includes(peerId)
    },
    messageReceiptIsRead (message) {
      if (!this.isCurrentGroup) return this.isMessageReadByPeer(message)
      const stats = this.groupMessageReceiptStats(message)
      return stats.total > 0 && stats.readCount === stats.total
    },
    messageReceiptLabel (message) {
      if (!this.isCurrentGroup) return this.isMessageReadByPeer(message) ? '已读' : '未读'
      const stats = this.groupMessageReceiptStats(message)
      if (stats.total > 0 && stats.readCount === stats.total) return '全部已读'
      if (stats.readCount === 0) return '未读'
      return `${stats.readCount}/${stats.total} 已读`
    },
    messageReceiptTitle (message) {
      if (this.isCurrentGroup) {
        const stats = this.groupMessageReceiptStats(message)
        if (stats.total > 0 && stats.readCount === stats.total) return '全部群成员已读'
        const readIds = new Set(this.messageReceiptReadIds(message))
        const unreadNames = stats.targetIds
          .filter((memberId) => !readIds.has(memberId))
          .map((memberId) => this.getUserName(memberId))
        return unreadNames.length ? `未读：${unreadNames.join('、')}` : '群成员尚未查看'
      }
      if (!this.isMessageReadByPeer(message)) return '对方尚未查看'
      const peerId = this.activePeerId
      const readAt = message.read_at && typeof message.read_at === 'object'
        ? Number(message.read_at[peerId])
        : 0
      const realtimeReadAt = Number(
        (this.realtimeReadReceipts[this.messageId(message)] || {})[peerId]
      )
      const timestamp = readAt || realtimeReadAt
      return timestamp
        ? `对方已于 ${this.$dayjs(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss')} 阅读`
        : '对方已读'
    },
    messageType (message) {
      return message.message_type || 'text'
    },
    isSystemMessage (message) {
      return this.messageType(message) === 'system'
    },
    isEmailMessage (message) {
      const type = this.messageType(message)
      return type === 'email' || type === 'email_message'
    },
    isAIMessage (message) {
      const type = this.messageType(message)
      return type === 'ai' || type === 'ai_message'
    },
    isAIMessageStreaming (message) {
      return this.isAIMessage(message) && message.streaming === true
    },
    aiMessageModelLabel (message) {
      const meta = (message && message.ai_metadata) || {}
      if (meta.provider_model) return meta.provider_model
      return meta.model === 'OpenAI' ? 'gpt-5.5' : 'deepseek-v4-flash'
    },
    aiMessageAskerLabel (message) {
      const meta = (message && message.ai_metadata) || {}
      const askerName = String(meta.asker_name || '').trim()
      return askerName && askerName !== '同事' ? `${askerName} 提问` : ''
    },
    renderMessageContent (message) {
      return renderMarkdown(message.content || '')
    },
    messageHttpsLinks (message) {
      if (!message || this.messageType(message) === 'file' || this.messageType(message) === 'image') return []
      return extractHttpsLinks(message.content || '')
    },
    messageLinkPreview (message) {
      const url = this.messageHttpsLinks(message)[0]
      if (!url) return null
      const metadata = this.linkMetadataCache[url]
      if (!metadata || metadata.unavailable) return null
      return metadata
    },
    scheduleMessageLinkMetadataFetch () {
      clearTimeout(this.linkMetadataTimer)
      this.linkMetadataTimer = setTimeout(() => {
        this.linkMetadataTimer = null
        this.fetchMessageLinkMetadata()
      }, 150)
    },
    fetchMessageLinkMetadata () {
      if (this.destroyed || !Array.isArray(this.messageList)) return
      const urls = []
      this.messageList.forEach((message) => {
        const firstUrl = this.messageHttpsLinks(message)[0]
        if (firstUrl) urls.push(firstUrl)
      })
      Array.from(new Set(urls))
        .slice(0, 30)
        .forEach((url) => this.fetchLinkMetadata(url))
    },
    async fetchLinkMetadata (url) {
      const targetUrl = normalizeDetectedHttpsUrl(url)
      if (!targetUrl || this.linkMetadataCache[targetUrl] || this.linkMetadataLoading[targetUrl]) return
      this.$set(this.linkMetadataLoading, targetUrl, true)
      try {
        const response = await getChatLinkMetadata({ url: targetUrl })
        const result = response && response.data ? response.data : {}
        const metadata = result.code === 0 && result.data ? result.data : null
        this.$set(this.linkMetadataCache, targetUrl, metadata || { url: targetUrl, unavailable: true })
      } catch (error) {
        this.$set(this.linkMetadataCache, targetUrl, { url: targetUrl, unavailable: true })
      } finally {
        this.$delete(this.linkMetadataLoading, targetUrl)
      }
    },
    linkPreviewTitle (preview) {
      if (!preview) return ''
      return preview.title || preview.site_name || this.linkPreviewHost(preview) || preview.url
    },
    linkPreviewHost (preview) {
      if (!preview) return ''
      if (preview.host) return preview.host
      try {
        return new URL(preview.url).hostname
      } catch (error) {
        return preview.url || ''
      }
    },
    openExternalLink (url) {
      const targetUrl = normalizeDetectedHttpsUrl(url)
      if (!targetUrl) return
      const newWindow = window.open(targetUrl, '_blank', 'noopener,noreferrer')
      if (newWindow) newWindow.opener = null
    },
    markMessagesReadBy (conversationId, readerId) {
      if (!conversationId || !readerId) return
      this.messageList.forEach((message) => {
        if (
          message.conversation_id !== conversationId ||
            message.user_id !== this.user_id
        ) return
        const readBy = this.messageReaderIds(message)
        if (readBy.includes(readerId)) return
        this.$set(message, 'read_by', [...readBy, readerId])
        const readAt = message.read_at && typeof message.read_at === 'object'
          ? { ...message.read_at }
          : {}
        readAt[readerId] = Math.floor(Date.now() / 1000)
        this.$set(message, 'read_at', readAt)
      })
    },
    formatFileSize (size) {
      const bytes = Number(size)
      if (!Number.isFinite(bytes) || bytes < 0) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
      return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
    },
    isMessageFileDownloading (message) {
      return Boolean(this.downloadingMessageFileIds[this.messageId(message)])
    },
    async downloadMessageFile (message) {
      const url = String(message && message.file_url ? message.file_url : '').trim()
      const messageId = this.messageId(message)
      if (!url || !messageId || this.isMessageFileDownloading(message)) return
      this.$set(this.downloadingMessageFileIds, messageId, true)
      try {
        const response = await window.fetch(url)
        if (!response.ok) throw new Error(`下载失败 (${response.status})`)
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = downloadUrl
        anchor.download = message.file_name || '下载文件'
        anchor.style.display = 'none'
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0)
      } catch (error) {
        this.$message.error('文件下载失败，请稍后重试')
      } finally {
        this.$delete(this.downloadingMessageFileIds, messageId)
      }
    },
    messageId (message) {
      if (!message) return ''
      if (typeof message === 'string') return message
      return message.message_id ||
        (message._id && (message._id.$id || message._id.$oid)) ||
        message.id ||
        ''
    },
    messageKey (message, index) {
      return this.messageId(message) || `${message.create_time}-${index}`
    },
    hasMessage (messageId) {
      messageId = this.messageId(messageId)
      return Boolean(
        messageId &&
          this.messageList.some((message) => this.messageId(message) === messageId)
      )
    },
    mergeMessageList (messages) {
      const merged = []
      const seenIds = new Set()
      const addMessage = (message) => {
        if (!message) return
        const messageId = this.messageId(message)
        if (messageId) {
          if (seenIds.has(messageId)) return
          seenIds.add(messageId)
        }
        merged.push(message)
      }
      messages.forEach(addMessage)
      this.messageList
        .filter((message) => message.conversation_id === this.currentConversationId)
        .forEach(addMessage)
      return merged.sort((a, b) => {
        const timeDiff = Number(a.create_time || 0) - Number(b.create_time || 0)
        if (timeDiff !== 0) return timeDiff
        return String(this.messageId(a)).localeCompare(String(this.messageId(b)))
      })
    },
    moveConversationToTop (conversation) {
      const index = this.chat_list.indexOf(conversation)
      if (index <= 0) return
      this.chat_list.splice(index, 1)
      this.chat_list.unshift(conversation)
    },
    scrollToLatest () {
      this.$nextTick(() => {
        const flow = this.$refs.messageFlow
        if (flow) flow.scrollTop = flow.scrollHeight
      })
    },
    formateTimeLabel (time) {
      const now = new Date().getTime()
      const diff = now - time * 1000
      if (diff < 60 * 1000) {
        return '刚刚'
      } else if (diff < 60 * 60 * 1000) {
        return `${Math.floor(diff / (60 * 1000))}分钟前`
      } else if (diff < 24 * 60 * 60 * 1000) {
        return `${Math.floor(diff / (60 * 60 * 1000))}小时前`
      } else {
        return `${Math.floor(diff / (24 * 60 * 60 * 1000))}天前`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chatContentContainer {
  display: flex;
  height: 100%;
  position: relative;
  overflow: hidden;
  .left {
    background: #f9f9f9;
    width: 260px;
    height: 100%;
    border-right: 1px solid #ccc;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .conversationListHeader {
      flex: 0 0 56px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 14px 0 16px;
      border-bottom: 1px solid #ebeef5;
      box-sizing: border-box;
      background: #fff;
      strong {
        color: #303133;
        font-size: 15px;
      }
      button {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 30px;
        padding: 0 9px;
        border: 1px solid #d9ecff;
        border-radius: 4px;
        background: #ecf5ff;
        color: #409eff;
        cursor: pointer;
        font-size: 12px;
      }
      button:hover {
        border-color: #b3d8ff;
        background: #d9ecff;
      }
    }
    .conversationList {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }
    .conversationListEmpty {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 72px 22px 0;
      color: #c0c4cc;
      text-align: center;
      i {
        margin-bottom: 12px;
        font-size: 24px;
      }
      strong {
        color: #606266;
        font-size: 14px;
        font-weight: 500;
      }
      span {
        margin-top: 7px;
        font-size: 12px;
        line-height: 1.6;
      }
    }
    .conversationItem {
      padding: 16px;
      width: 100%;
      height: 74px;
      margin: 0 auto;
      box-sizing: border-box;
      cursor: pointer;
      border-bottom: 1px solid #eee;
      position: relative;
      &:hover {
        background: #f5f5f5;
      }
      .name {
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 195px;
        overflow: hidden;
        font-size: 15px;
        font-weight: bold;
        color: #333;
        text-overflow: ellipsis;
        white-space: nowrap;
        i {
          flex: 0 0 auto;
          color: #909399;
          font-size: 13px;
        }
        .conversationPresenceText {
          flex: 0 0 auto;
          color: #909399;
          font-size: 11px;
          font-weight: normal;
        }
        .conversationPresenceText.is-online {
          color: #67c23a;
        }
      }
      .currentMessage {
        font-size: 14px;
        color: #999;
        margin-top: 10px;
        width: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .statuscircle {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: red;
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
      }
      .conversationPinButton {
        position: absolute;
        top: 8px;
        right: 12px;
        display: inline-flex;
        width: 22px;
        height: 22px;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 0;
        background: transparent;
        color: #b4b9c2;
        cursor: pointer;
        font-size: 14px;
        opacity: 0;
      }
      &:hover .conversationPinButton,
      .conversationPinButton.pinned {
        opacity: 1;
      }
      .conversationPinButton.pinned {
        color: #409eff;
      }
      .updatetime {
        font-size: 12px;
        color: #999;
        position: absolute;
        right: 14px;
        top: 44px;
      }
    }
    .conversationItem.selected {
      background: rgba(209, 222, 240, 0.4);
    }
  }
  .right {
    flex: 1;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: #fafafa;
    font-size: 16px;
    overflow: hidden;
    .rightInfo {
      flex: 0 0 96px;
      width: 100%;
      height: 96px;
      border-bottom: 1px solid #eee;
      color: #333;
      padding: 14px 20px;
      box-sizing: border-box;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      .conversationIdentity {
        display: inline-flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
        gap: 7px;
      }
      .conversationTitle {
        display: inline-flex;
        align-items: center;
        min-width: 0;
        gap: 10px;
      }
      .conversationTitleButton {
        min-width: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font: inherit;
        font-weight: inherit;
        text-align: left;
      }
      .conversationTitleButton:hover {
        color: #409eff;
      }
      .groupMemberCount {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 2px 4px;
        border: 0;
        border-radius: 3px;
        background: transparent;
        color: #909399;
        cursor: pointer;
        font-size: 12px;
        font-weight: normal;
        white-space: nowrap;
        i {
          font-size: 10px;
        }
      }
      .groupMemberCount:hover,
      .groupMemberCount:focus-visible {
        background: #ecf5ff;
        color: #409eff;
        outline: none;
      }
      .conversationEmail {
        color: #909399;
        font-size: 12px;
        line-height: 1;
        font-weight: normal;
        max-width: 360px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .peerPresence {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: #909399;
        font-size: 12px;
        font-weight: normal;
        white-space: nowrap;
        i {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #c0c4cc;
        }
      }
      .peerPresence.is-online {
        color: #67c23a;
        i {
          background: #67c23a;
        }
      }
      .peerPresence.is-offline {
        color: #909399;
      }
      .peerPresence.is-checking {
        color: #e6a23c;
        i {
          background: #e6a23c;
        }
      }
      .headerActions {
        display: inline-flex;
        align-items: center;
        flex: 0 0 auto;
        gap: 16px;
      }
      .groupInviteButton {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        height: 30px;
        padding: 0 10px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: #fff;
        color: #606266;
        cursor: pointer;
        font-size: 12px;
      }
      .groupInviteButton:hover {
        border-color: #b3d8ff;
        color: #409eff;
      }
      .conversationTodoButton,
      .conversationFileButton,
      .historySearchToggleButton {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        height: 30px;
        padding: 0 9px;
        border: 1px solid #b3d8ff;
        border-radius: 4px;
        background: #ecf5ff;
        color: #409eff;
        cursor: pointer;
        font-family: inherit;
        font-size: 12px;
      }
      .conversationTodoButton:hover:not(:disabled),
      .conversationFileButton:hover:not(:disabled),
      .historySearchToggleButton:hover:not(:disabled) {
        border-color: #409eff;
        background: #d9ecff;
      }
      .conversationTodoButton:disabled,
      .conversationFileButton:disabled,
      .historySearchToggleButton:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }
      .socketState {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #999;
        font-size: 12px;
        font-weight: normal;
        i {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #c0c4cc;
        }
      }
      .socketState.connected {
        color: #67c23a;
        i {
          background: #67c23a;
        }
      }
      .socketState.authRequired,
      .socketState.offline {
        color: #f56c6c;
        i {
          background: #f56c6c;
        }
      }
      .socketState.authRequired {
        cursor: pointer;
      }
    }
    .messageContentArea {
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
      overflow: hidden;
    }
    .rightContent {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: scroll;
      padding: 20px;
      box-sizing: border-box;
      .messageHistoryLoading {
        display: flex;
        min-height: 100%;
        align-items: center;
        justify-content: center;
        color: #909399;
        font-size: 14px;
      }
      .olderHistoryLoading {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 4px 0 10px;
        color: #909399;
        font-size: 13px;
      }
      .contentItem {
        position: relative;
        text-align: left;
        margin-top: 10px;
        width: 100%;
        .multiSelectMessageToggle {
          position: absolute;
          top: 50%;
          left: 2px;
          z-index: 3;
          display: inline-flex;
          width: 20px;
          height: 20px;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: 1px solid #c0c4cc;
          border-radius: 50%;
          background: #fff;
          color: #fff;
          cursor: pointer;
          font-size: 13px;
          transform: translateY(-50%);
        }
        .multiSelectMessageToggle.selected {
          border-color: #409eff;
          background: #409eff;
        }
        &.isMultiSelecting {
          padding-left: 30px;
          box-sizing: border-box;
        }
        .systemMessage {
          display: flex;
          justify-content: center;
          margin: 12px 0;
          text-align: center;
          span {
            display: inline-block;
            max-width: #{"min(520px, 80%)"};
            padding: 5px 12px;
            border-radius: 12px;
            background: #f0f2f5;
            color: #909399;
            font-size: 12px;
            line-height: 1.5;
            overflow-wrap: anywhere;
          }
        }
        .messageBody {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          margin: 0;
          padding: 0;
          position: relative;
          width: 100%;
        }
        .presenceDot {
          position: absolute;
          width: 8px;
          height: 8px;
          border: 2px solid #fff;
          border-radius: 50%;
          background: #c0c4cc;
          box-sizing: content-box;
        }
        .presenceDot.is-online {
          background: #67c23a;
        }
        .presenceDot.is-offline {
          background: #c0c4cc;
        }
        .presenceDot.is-unknown {
          background: #dcdfe6;
        }
        .messageContentStack {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-start;
          max-width: 70%;
          position: relative;
        }
        .messageQuoteInFeed {
          display: flex;
          align-items: center;
          width: 100%;
          max-width: 100%;
          margin: 0 0 4px;
          padding: 6px 9px;
          border-top: 0;
          border-right: 0;
          border-bottom: 0;
          border-left: 3px solid #c0c4cc;
          border-radius: 4px;
          box-sizing: border-box;
          background: #f0f2f5;
          color: #606266;
          cursor: pointer;
          font-family: inherit;
          font-size: 12px;
          line-height: 1.35;
          gap: 6px;
          text-align: left;
          transition: background 0.15s ease, border-color 0.15s ease;
          &:hover,
          &:focus-visible {
            border-left-color: #409eff;
            background: #e8f3ff;
            outline: none;
          }
          strong {
            flex: 0 0 auto;
            color: #303133;
            font-weight: 600;
          }
          span {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
        .messageActions {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 5px;
          position: absolute;
          top: 50%;
          left: calc(100% + 8px);
          z-index: 2;
          opacity: 0;
          pointer-events: none;
          transform: translateY(calc(-50% + 2px));
          transition: opacity 0.15s ease, transform 0.15s ease;
          white-space: nowrap;
        }
        .messageReactionRow,
        .messageActionRow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .messageReactionPicker {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          height: 28px;
          padding: 0 4px;
          border: 1px solid #dcdfe6;
          border-radius: 14px;
          background: #fff;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        }
        .messageReactionButton {
          width: 24px;
          height: 24px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          background: transparent;
          cursor: pointer;
          font-size: 15px;
          line-height: 24px;
          text-align: center;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .messageReactionButton:hover,
        .messageReactionButton.active {
          background: #ecf5ff;
          transform: translateY(-1px);
        }
        .messageActionButton {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          height: 28px;
          padding: 0 8px;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          background: #fff;
          color: #606266;
          cursor: pointer;
          font-size: 12px;
          line-height: 1;
          transition: border-color 0.15s ease, color 0.15s ease;
          white-space: nowrap;
        }
        .messageActionButton:hover {
          border-color: #409eff;
          color: #409eff;
        }
        .messageTodoLink {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          width: fit-content;
          margin-top: 6px;
          min-height: 22px;
          padding: 0 7px;
          border: 1px solid #b7eb8f;
          border-radius: 3px;
          background: #f6ffed;
          color: #52c41a;
          cursor: pointer;
          font-family: inherit;
          font-size: 12px;
          line-height: 1;
          white-space: nowrap;
        }
        .messageLinkPreview {
          display: flex;
          align-items: stretch;
          width: 360px;
          max-width: 100%;
          min-height: 78px;
          margin-top: 6px;
          padding: 0;
          overflow: hidden;
          border: 1px solid #d8e4f2;
          border-radius: 7px;
          background: #fff;
          color: #303133;
          cursor: pointer;
          font-family: inherit;
          text-align: left;
          box-shadow: 0 2px 8px rgba(32, 45, 64, 0.08);
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
          img {
            width: 92px;
            min-height: 78px;
            flex: 0 0 92px;
            object-fit: cover;
            background: #f0f2f5;
          }
          .linkPreviewInfo {
            display: flex;
            min-width: 0;
            flex: 1;
            flex-direction: column;
            gap: 4px;
            justify-content: center;
            padding: 10px 12px;
            strong {
              display: -webkit-box;
              overflow: hidden;
              color: #303133;
              font-size: 14px;
              font-weight: 600;
              line-height: 1.35;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
            }
            small {
              display: -webkit-box;
              overflow: hidden;
              color: #606266;
              font-size: 12px;
              line-height: 1.35;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
            }
            em {
              overflow: hidden;
              color: #909399;
              font-size: 12px;
              font-style: normal;
              line-height: 1.2;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
          .linkPreviewIcon {
            flex: 0 0 auto;
            padding: 12px 12px 0 0;
            color: #909399;
            font-size: 15px;
          }
          &:hover,
          &:focus-visible {
            border-color: #409eff;
            box-shadow: 0 4px 14px rgba(64, 158, 255, 0.16);
            outline: none;
            transform: translateY(-1px);
          }
        }
        &:hover .messageActions {
          opacity: 1;
          pointer-events: auto;
          transform: translateY(-50%);
        }
        &.textRight .messageActions {
          align-items: flex-end;
          right: calc(100% + 8px);
          left: auto;
        }
        &.textRight .messageQuoteInFeed {
          border-left-color: #409eff;
          background: #ecf5ff;
        }
        &.textRight .messageLinkPreview {
          flex-direction: row-reverse;
          text-align: left;
          img {
            border-left: 1px solid #e4e7ed;
          }
          .linkPreviewIcon {
            padding: 12px 0 0 12px;
          }
        }
        &.isQuoteFocused {
          scroll-margin-block: 80px;
          animation: quotedMessageFocus 2.2s ease;
        }
        .showtime {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 14px;
          color: #999;
          margin-bottom: 4px;
          .messageSenderAvatar {
            position: relative;
            flex: 0 0 26px;
            width: 26px;
            height: 26px;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #ecf5ff;
            color: #409eff;
            cursor: pointer;
            font-size: 11px;
            font-weight: 600;
            line-height: 26px;
            text-align: center;
            .avatarPresenceDot {
              right: -2px;
              bottom: -2px;
            }
          }
          .messageSenderIdentity {
            display: inline-flex;
            align-items: center;
            min-width: 0;
            max-width: 220px;
            padding: 0;
            border: 0;
            background: transparent;
            cursor: pointer;
            line-height: 1.25;
            text-align: left;
            strong {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            strong {
              color: #606266;
              font-size: 12px;
              font-weight: 500;
            }
          }
          .messageSenderAvatar:hover,
          .messageSenderIdentity:hover strong {
            color: #409eff;
          }
          .messageTime {
            flex: 0 0 auto;
            overflow: hidden;
            font-size: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .messageEdited {
            flex: 0 0 auto;
            color: #909399;
            font-size: 11px;
            white-space: nowrap;
          }
        }
        .showtime.groupMessageMeta {
          height: 26px;
          min-height: 0;
          margin: 0 0 4px;
        }
        .contentItem1 {
          background: rgb(232, 232, 233);
          color: #000;
          padding: 10px 16px;
          border-radius: 4px;
          display: inline-block;
          max-width: 100%;
          text-align: left;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .contentItem1.markdownMessage,
        .contentItem1.emailMessage {
          white-space: normal;
        }
        .markdownContent {
          white-space: normal;
        }
        .contentItem1.markdownMessage,
        .contentItem1.emailMessage,
        .aiMessageBody .markdownContent {
          ::v-deep p {
            margin: 0 0 8px;
            line-height: 1.58;
          }
          ::v-deep p:last-child {
            margin-bottom: 0;
          }
          ::v-deep h1,
          ::v-deep h2,
          ::v-deep h3,
          ::v-deep h4,
          ::v-deep h5,
          ::v-deep h6 {
            margin: 8px 0 6px;
            color: inherit;
            font-weight: 600;
            line-height: 1.35;
          }
          ::v-deep h1 { font-size: 18px; }
          ::v-deep h2 { font-size: 16px; }
          ::v-deep h3,
          ::v-deep h4,
          ::v-deep h5,
          ::v-deep h6 { font-size: 15px; }
          ::v-deep ul,
          ::v-deep ol {
            margin: 6px 0 8px;
            padding-left: 22px;
          }
          ::v-deep li {
            margin: 2px 0;
            line-height: 1.55;
          }
          ::v-deep blockquote {
            margin: 8px 0;
            padding: 6px 10px;
            border-left: 3px solid #c0c4cc;
            background: rgba(255, 255, 255, 0.45);
            color: #606266;
          }
          ::v-deep pre {
            overflow-x: auto;
            max-width: 100%;
            margin: 8px 0;
            padding: 10px 12px;
            border-radius: 5px;
            background: #1f2933;
            color: #f8fafc;
            white-space: pre;
          }
          ::v-deep code {
            padding: 1px 4px;
            border-radius: 3px;
            background: rgba(31, 41, 51, 0.1);
            font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
            font-size: 12px;
          }
          ::v-deep pre code {
            padding: 0;
            background: transparent;
            color: inherit;
            font-size: 12px;
            line-height: 1.55;
          }
          ::v-deep a {
            color: #1677d2;
            text-decoration: underline;
            word-break: break-all;
          }
          ::v-deep table {
            display: block;
            overflow-x: auto;
            max-width: 100%;
            margin: 8px 0;
            border-collapse: collapse;
          }
          ::v-deep th,
          ::v-deep td {
            padding: 5px 8px;
            border: 1px solid #dcdfe6;
            white-space: nowrap;
          }
          ::v-deep img {
            display: block;
            max-width: 100%;
            max-height: 260px;
            margin: 8px 0;
            border-radius: 6px;
            object-fit: contain;
          }
        }
        .contentItem1.aiMessage {
          background: #f6f4ff;
          border: 1px solid #ded7fb;
          color: #303133;
          .aiMessageTag {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;
            margin-bottom: 6px;
            color: #6f55e8;
            font-size: 12px;
            font-weight: 600;
            i {
              font-size: 14px;
            }
            .aiMessageAsker {
              color: #909399;
              font-weight: 400;
            }
          }
          .aiMessageBody {
            white-space: pre-wrap;
            word-wrap: break-word;
            line-height: 1.55;
            font-size: 14px;
          }
          .aiStreamingCursor {
            display: inline-block;
            width: 2px;
            height: 14px;
            margin-left: 2px;
            vertical-align: -2px;
            background: #6f55e8;
            animation: aiCursorBlink 0.9s steps(1) infinite;
          }
          @keyframes aiCursorBlink {
            50% {
              opacity: 0;
            }
          }
        }
        .messageImage {
          display: inline-block;
          width: auto;
          height: auto;
          max-width: #{"min(420px, 100%)"};
          max-height: 280px;
          border-radius: 6px;
          object-fit: contain;
          vertical-align: top;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
          cursor: zoom-in;
          transition: opacity 0.15s ease;
        }
        .messageImage:hover {
          opacity: 0.88;
        }
        .messageFile {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          max-width: 74%;
          min-width: 260px;
          padding: 12px;
          box-sizing: border-box;
          border: 1px solid #c8d7ea;
          border-left: 4px solid #409eff;
          border-radius: 8px;
          background: #f8fbff;
          color: #303133;
          text-decoration: none;
          vertical-align: top;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.11);
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
          .fileMessageIcon {
            display: inline-flex;
            width: 40px;
            height: 40px;
            flex: 0 0 40px;
            align-items: center;
            justify-content: center;
            border-radius: 7px;
            background: #e8f3ff;
            color: #1677d2;
            font-size: 22px;
          }
          .fileInfo {
            min-width: 0;
            flex: 1;
            text-align: left;
            strong,
            em,
            small {
              display: block;
            }
            strong {
              overflow: hidden;
              color: #303133;
              font-size: 14px;
              font-weight: 600;
              line-height: 20px;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            em {
              margin-top: 2px;
              color: #409eff;
              font-size: 11px;
              font-style: normal;
              line-height: 16px;
            }
            small {
              margin-top: 2px;
              color: #909399;
              font-size: 12px;
              line-height: 16px;
            }
          }
          .fileDownloadIcon {
            display: inline-flex;
            width: 32px;
            height: 32px;
            flex: 0 0 32px;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: #ecf5ff;
            color: #409eff;
            font-size: 17px;
          }
        }
        .messageFile:hover {
          border-color: #409eff;
          box-shadow: 0 4px 12px rgba(64, 158, 255, 0.18);
          transform: translateY(-1px);
        }
        .messageFile[aria-busy="true"] {
          pointer-events: none;
          opacity: 0.76;
        }
        .emailMessage {
          min-width: 260px;
          text-align: left;
          line-height: 1.55;
          white-space: normal;
          p,
          ul,
          ol,
          pre,
          h1,
          h2,
          h3,
          blockquote {
            margin: 0 0 8px;
          }
          p:last-child,
          ul:last-child,
          ol:last-child,
          pre:last-child {
            margin-bottom: 0;
          }
          ul,
          ol {
            padding-left: 20px;
          }
          h1 {
            font-size: 20px;
          }
          h2 {
            font-size: 18px;
          }
          h3 {
            font-size: 16px;
          }
          blockquote {
            padding-left: 10px;
            border-left: 3px solid #c0c4cc;
            color: #606266;
          }
          code {
            padding: 1px 4px;
            border-radius: 3px;
            background: rgba(0, 0, 0, 0.06);
            font-family: Consolas, Monaco, monospace;
            font-size: 12px;
          }
          pre {
            overflow-x: auto;
            padding: 8px;
            border-radius: 4px;
            background: rgba(0, 0, 0, 0.06);
          }
          a {
            color: #409eff;
          }
        }
        .messageReactionBadges {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: -2px;
        }
        .messageReactionBadge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          height: 22px;
          padding: 0 7px;
          border: 1px solid #dcdfe6;
          border-radius: 11px;
          background: #fff;
          color: #606266;
          cursor: pointer;
          font-size: 12px;
          line-height: 1;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }
        .messageReactionBadge.active {
          border-color: #409eff;
          background: #ecf5ff;
          color: #409eff;
        }
        .messageReceipt {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 3px;
          margin-top: 5px;
          color: #a8abb2;
          font-size: 11px;
          line-height: 16px;
        }
        .messageReceipt.isRead {
          color: #409eff;
        }
      }
      .contentItem.textRight {
        text-align: right;
        .showtime {
          justify-content: flex-end;
        }
        .groupMessageMeta {
          .messageTime {
            order: 1;
          }
          .messageSenderIdentity {
            order: 2;
            text-align: right;
            strong {
              text-align: right;
            }
          }
          .messageSenderAvatar {
            order: 3;
          }
        }
        .messageBody {
          flex-direction: row-reverse;
          justify-content: flex-start;
        }
        .messageContentStack {
          align-items: flex-end;
        }
        .messageReactionBadges {
          justify-content: flex-end;
        }
        .contentItem1 {
          background: rgb(201, 231, 255);
          color: #000;
          padding: 10px 16px;
          border-radius: 4px;
          display: inline-block;
          max-width: 100%;
          word-wrap: break-word;
        }
        .messageFile {
          border-color: #9bcfff;
          border-left-color: #1677d2;
          background: #f2f8ff;
          box-shadow: 0 2px 8px rgba(22, 119, 210, 0.16);
        }
        .emailMessage {
          background: rgb(201, 231, 255);
        }
      }
    }
    @keyframes quotedMessageFocus {
      0%, 100% {
        background: transparent;
      }
      12%, 72% {
        background: rgba(64, 158, 255, 0.14);
      }
    }
    .multiSelectionPanel {
      display: flex;
      flex: 0 0 48px;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 0 16px;
      border-top: 1px solid #d9ecff;
      background: #f7fbff;
      color: #606266;
      font-size: 13px;
      .multiSelectionActions {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      button {
        display: inline-flex;
        height: 28px;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 0 9px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: #fff;
        color: #606266;
        cursor: pointer;
        font-size: 12px;
      }
      button.primary {
        border-color: #409eff;
        background: #409eff;
        color: #fff;
      }
      button:disabled {
        border-color: #dcdfe6;
        background: #f5f7fa;
        color: #c0c4cc;
        cursor: not-allowed;
      }
      button.close {
        width: 28px;
        padding: 0;
      }
    }
    .aiThinkingBar {
      display: flex;
      flex: 0 0 30px;
      align-items: center;
      gap: 8px;
      padding: 0 16px;
      border-top: 1px solid #e6e0ff;
      background: #f7f5ff;
      color: #6f55e8;
      font-size: 12px;
      i {
        font-size: 14px;
      }
    }
    .inputContainer {
      flex: 0 0 160px;
      background: #fff;
      width: 100%;
      height: 160px;
      border-top: 1px solid #eee;
      box-sizing: border-box;
      .toolInput {
        width: 100%;
        height: 40px;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        padding: 4px;
        .toolItem {
          cursor: pointer;
          margin-right: 10px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          border: 0;
          background: transparent;
          color: #606266;
          font-size: 22px;
        }
        .toolItem:hover {
          background: #efefef;
        }
        .toolItem.disabled {
          color: #c0c4cc;
          cursor: not-allowed;
        }
        .toolItem.voiceTool.active {
          background: #ece9ff;
          color: #6f55e8;
        }
        .composeModeSwitch {
          display: inline-flex;
          flex-shrink: 0;
          overflow: hidden;
          margin-right: 10px;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          button {
            height: 28px;
            padding: 0 10px;
            border: 0;
            border-right: 1px solid #dcdfe6;
            background: #fff;
            color: #606266;
            cursor: pointer;
            font-size: 12px;
          }
          button:last-child {
            border-right: 0;
          }
          button.active {
            background: #409eff;
            color: #fff;
          }
        }
        .editorModeSwitch {
          display: inline-flex;
          flex-shrink: 0;
          overflow: hidden;
          margin-left: 6px;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          button {
            height: 28px;
            padding: 0 10px;
            border: 0;
            border-right: 1px solid #dcdfe6;
            background: #fff;
            color: #606266;
            cursor: pointer;
            font-size: 12px;
          }
          button:last-child {
            border-right: 0;
          }
          button.active {
            background: #303133;
            color: #fff;
          }
        }
        .notificationTool.enabled {
          color: #409eff;
        }
        .attachmentInput {
          display: none;
        }
        .uploadStatus {
          min-width: 0;
          overflow: hidden;
          color: #909399;
          font-size: 12px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
      .inputContent {
        height: 120px;
        width: 100%;
        position: relative;
        &.isDragOver::before {
          content: '';
          position: absolute;
          z-index: 8;
          inset: 6px 12px;
          border: 2px dashed #409eff;
          border-radius: 6px;
          background: rgba(236, 245, 255, 0.82);
          pointer-events: none;
        }
        .composerDropOverlay {
          position: absolute;
          z-index: 9;
          inset: 6px 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #409eff;
          font-size: 14px;
          font-weight: 600;
          pointer-events: none;
          i {
            font-size: 22px;
          }
        }
        .attachmentDraftList {
          position: absolute;
          z-index: 2;
          top: 8px;
          left: 12px;
          right: 12px;
          display: flex;
          align-items: flex-start;
          max-width: calc(100% - 24px);
          overflow-x: auto;
          gap: 8px;
          padding: 0 8px 10px 0;
        }
        .imageDraftList {
          display: flex;
          flex: 0 0 auto;
          gap: 8px;
        }
        .imageDraft {
          position: relative;
          flex: 0 0 auto;
          width: 82px;
          height: 88px;
          padding: 5px;
          box-sizing: border-box;
          border: 1px solid #dcdfe6;
          border-radius: 6px;
          background: #f5f7fa;
          img {
            display: block;
            width: 70px;
            height: 62px;
            border-radius: 3px;
            object-fit: cover;
          }
          span {
            display: block;
            margin-top: 4px;
            overflow: hidden;
            color: #909399;
            font-size: 11px;
            line-height: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .removeImageDraft {
            position: absolute;
            top: -8px;
            right: -8px;
            width: 20px;
            height: 20px;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #606266;
            color: #fff;
            cursor: pointer;
            line-height: 20px;
          }
        }
        .fileDraft {
          position: relative;
          display: flex;
          flex: 0 0 220px;
          align-items: center;
          width: 220px;
          height: 64px;
          padding: 10px 12px;
          box-sizing: border-box;
          border: 1px solid #dcdfe6;
          border-radius: 6px;
          background: #f5f7fa;
          .fileDraftIcon {
            flex: 0 0 auto;
            margin-right: 10px;
            color: #409eff;
            font-size: 28px;
          }
          .fileDraftInfo {
            min-width: 0;
            strong,
            small {
              display: block;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            strong {
              color: #303133;
              font-size: 13px;
              font-weight: 500;
              line-height: 20px;
            }
            small {
              color: #909399;
              font-size: 11px;
              line-height: 18px;
            }
          }
          .removeFileDraft {
            position: absolute;
            top: -8px;
            right: -8px;
            width: 20px;
            height: 20px;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #606266;
            color: #fff;
            cursor: pointer;
            line-height: 20px;
          }
        }
        .voiceInputBar {
          position: absolute;
          z-index: 3;
          top: 8px;
          left: 12px;
          right: 12px;
          display: flex;
          align-items: center;
          min-height: 42px;
          padding: 4px 34px 4px 10px;
          border: 1px solid #d3cdfa;
          border-radius: 5px;
          box-sizing: border-box;
          background: #f7f5ff;
          color: #606266;
          gap: 10px;
          .voiceInputIcon {
            display: flex;
            flex: 0 0 auto;
            align-items: center;
            color: #6f55e8;
            font-size: 18px;
            line-height: 1;
          }
          .voiceTranslate {
            flex: 0 0 auto;
            flex-direction: row;
            align-items: center;
            min-height: 0;
            gap: 0;
          }
          .voiceInputHint {
            min-width: 0;
            flex: 1 1 auto;
            overflow: hidden;
            color: #909399;
            font-size: 12px;
            line-height: 32px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .removeVoiceInput {
            position: absolute;
            top: -8px;
            right: -8px;
            width: 20px;
            height: 20px;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #606266;
            color: #fff;
            cursor: pointer;
            line-height: 20px;
          }
        }
        &.hasVoiceInput .quoteDraft {
          top: 58px;
        }
        .quoteDraft {
          position: absolute;
          z-index: 3;
          top: 8px;
          left: 12px;
          right: 12px;
          display: flex;
          align-items: center;
          min-height: 42px;
          padding: 7px 34px 7px 10px;
          border: 1px solid #dcdfe6;
          border-radius: 5px;
          box-sizing: border-box;
          background: #f5f7fa;
          color: #606266;
          gap: 8px;
          .quoteDraftBar {
            align-self: stretch;
            flex: 0 0 3px;
            border-radius: 3px;
            background: #409eff;
          }
          .quoteDraftInfo {
            min-width: 0;
            strong,
            small {
              display: block;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            strong {
              color: #303133;
              font-size: 12px;
              line-height: 16px;
            }
            small {
              margin-top: 2px;
              color: #909399;
              font-size: 12px;
              line-height: 16px;
            }
          }
          .removeQuoteDraft {
            position: absolute;
            top: 8px;
            right: 8px;
            width: 24px;
            height: 24px;
            padding: 0;
            border: 0;
            border-radius: 4px;
            background: transparent;
            color: #909399;
            cursor: pointer;
            line-height: 24px;
          }
          .removeQuoteDraft:hover {
            background: #ebeef5;
            color: #303133;
          }
        }
        .wechatEmailEditor {
          height: 100%;
        }
        .mentionMenu {
          position: absolute;
          z-index: 8;
          left: 12px;
          bottom: 48px;
          width: 260px;
          max-height: 238px;
          overflow-y: auto;
          padding: 6px;
          border: 1px solid #dcdfe6;
          border-radius: 6px;
          box-sizing: border-box;
          background: #fff;
          box-shadow: 0 8px 24px rgba(31, 45, 61, 0.14);
        }
        .mentionCandidate {
          display: flex;
          align-items: center;
          width: 100%;
          min-height: 42px;
          padding: 6px 8px;
          border: 0;
          border-radius: 4px;
          background: transparent;
          cursor: pointer;
          text-align: left;
          gap: 9px;
        }
        .mentionCandidate:hover,
        .mentionCandidate.active {
          background: #ecf5ff;
        }
        .mentionAvatar {
          position: relative;
          flex: 0 0 28px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #eef6ff;
          color: #409eff;
          font-size: 13px;
          font-weight: 600;
          line-height: 28px;
          text-align: center;
          .el-icon-user-solid {
            font-size: 15px;
          }
        }
        .mentionAllCandidate .mentionAvatar {
          background: #ecf5ff;
          color: #409eff;
        }
        .mentionAiCandidate .mentionAvatar {
          background: #ece9ff;
          color: #6f55e8;
        }
        .mentionAiCandidate:hover,
        .mentionAiCandidate.active {
          background: #f3f0ff;
        }
        .mentionAiAvatar {
          display: flex;
          width: 100%;
          height: 100%;
          align-items: center;
          justify-content: center;
          i {
            font-size: 15px;
          }
        }
        .mentionPresenceDot {
          position: absolute;
          right: -2px;
          bottom: -2px;
          width: 8px;
          height: 8px;
          border: 2px solid #fff;
          border-radius: 50%;
          background: #dcdfe6;
          box-sizing: content-box;
        }
        .mentionPresenceDot.is-online {
          background: #67c23a;
        }
        .mentionPresenceDot.is-offline {
          background: #c0c4cc;
        }
        .mentionInfo {
          min-width: 0;
          strong,
          small {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          strong {
            color: #303133;
            font-size: 13px;
            line-height: 18px;
          }
          small {
            color: #909399;
            font-size: 11px;
            line-height: 15px;
          }
        }
        .mentionEmpty {
          padding: 10px 8px;
          color: #909399;
          font-size: 12px;
          text-align: center;
        }
        .sendBtn {
          position: absolute;
          right: 30px;
          bottom: 20px;
          cursor: pointer;
          color: #666;
          font-size: 16px;
        }
        .composerHint {
          position: absolute;
          left: 12px;
          bottom: 22px;
          color: #909399;
          font-size: 12px;
          pointer-events: none;
        }
        .sendBtn:hover {
          color: #999;
        }
        .sendBtn.disabled {
          color: #ddd !important;
          cursor: not-allowed;
        }
      }
    }
  }
  .historySearchDrawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 2100;
    display: flex;
    width: #{"min(420px, calc(100vw - 32px))"};
    flex-direction: column;
    background: #fff;
    box-shadow: -8px 0 28px rgba(0, 0, 0, 0.14);
    header {
      display: flex;
      flex: 0 0 72px;
      align-items: center;
      gap: 8px;
      padding: 16px 14px 16px 16px;
      border-bottom: 1px solid #ebeef5;
      box-sizing: border-box;
    }
    .historySearchDrawerField {
      display: inline-flex;
      min-width: 0;
      flex: 1 1 auto;
      align-items: center;
      height: 36px;
      padding: 0 6px 0 11px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      background: #fff;
      color: #909399;
      box-sizing: border-box;
      font-weight: normal;
      i {
        flex: 0 0 auto;
        font-size: 14px;
      }
      input {
        flex: 1 1 auto;
        min-width: 0;
        height: 34px;
        padding: 0 8px;
        border: 0;
        outline: none;
        background: transparent;
        color: #303133;
        font-size: 13px;
        font-family: inherit;
      }
      input::placeholder {
        color: #c0c4cc;
      }
      input:disabled {
        cursor: not-allowed;
      }
    }
    .historySearchDrawerField:focus-within {
      border-color: #409eff;
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
    }
    .historySearchDrawerClose {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #909399;
      cursor: pointer;
    }
    .historySearchDrawerClose {
      flex: 0 0 32px;
      width: 32px;
      height: 32px;
      font-size: 18px;
    }
    .historySearchDrawerClose:hover {
      background: #f2f6fc;
      color: #606266;
    }
    .historySearchSubmitButton {
      flex: 0 0 auto;
      height: 36px;
      padding: 0 12px;
      border: 1px solid #409eff;
      border-radius: 4px;
      background: #409eff;
      color: #fff;
      cursor: pointer;
      font-size: 13px;
      font-family: inherit;
    }
    .historySearchSubmitButton:disabled {
      border-color: #a0cfff;
      background: #a0cfff;
      cursor: not-allowed;
    }
    .historySearchDrawerSummary {
      display: flex;
      flex: 0 0 42px;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 0 20px;
      border-bottom: 1px solid #f0f2f5;
      box-sizing: border-box;
      strong {
        color: #303133;
        font-size: 14px;
      }
      small {
        color: #909399;
        font-size: 12px;
        font-weight: normal;
      }
    }
    .historySearchPanelBody {
      flex: 1 1 auto;
      min-height: 0;
      padding: 12px;
      overflow-y: auto;
      box-sizing: border-box;
    }
    .historySearchState {
      min-height: 160px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      color: #909399;
      font-size: 13px;
      line-height: 1.5;
      text-align: center;
    }
    .historySearchResult {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: 8px;
      margin: 0 0 8px;
      padding: 10px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
      color: #303133;
      cursor: pointer;
      text-align: left;
      font-family: inherit;
      box-sizing: border-box;
    }
    .historySearchResult:hover {
      border-color: #b3d8ff;
      background: #f5faff;
    }
    .historySearchResultMeta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      min-width: 0;
      strong {
        min-width: 0;
        overflow: hidden;
        color: #303133;
        font-size: 13px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      small {
        flex: 0 0 auto;
        color: #909399;
        font-size: 11px;
        font-weight: normal;
      }
    }
    .historySearchResultSnippet {
      color: #606266;
      font-size: 13px;
      font-weight: normal;
      line-height: 1.45;
      overflow-wrap: anywhere;
      mark {
        padding: 0 2px;
        border-radius: 2px;
        background: #fff1b8;
        color: #303133;
      }
    }
  }
  .historySearchDrawerFade-enter-active,
  .historySearchDrawerFade-leave-active {
    transition: opacity 0.18s ease;
    .historySearchDrawer {
      transition: transform 0.18s ease;
    }
  }
  .historySearchDrawerFade-enter,
  .historySearchDrawerFade-leave-to {
    opacity: 0;
    .historySearchDrawer {
      transform: translateX(28px);
    }
  }
  .groupPanelBackdrop {
    position: absolute;
    z-index: 100;
    inset: 0;
    display: flex;
    justify-content: flex-end;
    background: rgba(31, 45, 61, 0.2);
  }
  .groupPanel {
    display: flex;
    flex-direction: column;
    width: #{"min(380px, calc(100vw - 40px))"};
    height: 100%;
    background: #fff;
    box-shadow: -8px 0 24px rgba(31, 45, 61, 0.14);
    .groupPanelHeader {
      flex: 0 0 72px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 0 20px;
      border-bottom: 1px solid #ebeef5;
      box-sizing: border-box;
      > span {
        min-width: 0;
      }
      strong,
      small {
        display: block;
      }
      strong {
        color: #303133;
        font-size: 16px;
      }
      small {
        margin-top: 5px;
        overflow: hidden;
        color: #909399;
        font-size: 12px;
        font-weight: normal;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      button {
        flex: 0 0 auto;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 4px;
        background: transparent;
        color: #909399;
        cursor: pointer;
        font-size: 18px;
      }
      button:hover {
        background: #f5f7fa;
        color: #303133;
      }
    }
    .groupPanelBody {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      padding: 18px 20px 0;
      box-sizing: border-box;
    }
    .groupNameField {
      display: block;
      margin-bottom: 14px;
      > span {
        display: block;
        margin-bottom: 7px;
        color: #606266;
        font-size: 12px;
      }
      input {
        width: 100%;
        height: 36px;
        padding: 0 11px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        outline: none;
        box-sizing: border-box;
        color: #303133;
        font-size: 14px;
      }
      input:focus {
        border-color: #409eff;
      }
    }
    .groupSearchField {
      display: flex;
      align-items: center;
      height: 36px;
      padding: 0 10px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      box-sizing: border-box;
      color: #c0c4cc;
      input {
        flex: 1;
        min-width: 0;
        height: 34px;
        padding: 0 0 0 8px;
        border: 0;
        outline: none;
        color: #303133;
        font-size: 14px;
      }
    }
    .groupSearchField:focus-within {
      border-color: #409eff;
    }
    .groupSelectionSummary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 38px;
      color: #909399;
      font-size: 12px;
      span:first-child {
        color: #409eff;
      }
    }
    .groupCandidateList {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      margin: 0 -6px;
      padding: 0 6px 14px;
    }
    .groupCandidate {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-height: 58px;
      margin-bottom: 4px;
      padding: 8px 10px;
      border: 0;
      border-radius: 5px;
      box-sizing: border-box;
      background: transparent;
      color: inherit;
      cursor: pointer;
      font: inherit;
      text-align: left;
      .groupCandidateAvatar {
        position: relative;
        flex: 0 0 34px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: #ecf5ff;
        color: #409eff;
        font-size: 13px;
        font-weight: 600;
        line-height: 34px;
        text-align: center;
        .groupCandidatePresenceDot {
          right: -2px;
          bottom: -2px;
        }
      }
      .groupCandidateInfo {
        flex: 1;
        min-width: 0;
        strong,
        small {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        strong {
          color: #303133;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 7px;
        }
        small {
          margin-top: 4px;
          color: #909399;
          font-size: 11px;
        }
      }
      .groupMemberPresenceText {
        flex: 0 0 auto;
        color: #909399;
        font-size: 11px;
        font-weight: normal;
      }
      .groupMemberPresenceText.is-online {
        color: #67c23a;
      }
      .groupMemberPresenceText.is-offline {
        color: #909399;
      }
      .presenceDot {
        position: absolute;
        width: 8px;
        height: 8px;
        border: 2px solid #fff;
        border-radius: 50%;
        background: #dcdfe6;
        box-sizing: content-box;
      }
      .presenceDot.is-online {
        background: #67c23a;
      }
      .presenceDot.is-offline {
        background: #c0c4cc;
      }
      .groupCandidateCheck {
        flex: 0 0 auto;
        color: transparent;
        font-size: 14px;
      }
    }
    .groupCandidate:hover {
      background: #f5f7fa;
    }
    .groupCandidate:focus-visible {
      outline: 2px solid #409eff;
      outline-offset: -2px;
    }
    .groupCandidate.selected {
      background: #ecf5ff;
      .groupCandidateCheck {
        color: #409eff;
      }
    }
    .groupMemberReadOnly {
      cursor: default;
    }
    .groupMemberReadOnly:hover {
      background: #f8f9fb;
    }
    .groupMemberRole {
      flex: 0 0 auto;
      padding: 2px 6px;
      border-radius: 3px;
      background: #ecf5ff;
      color: #409eff;
      font-size: 10px;
      line-height: 18px;
      white-space: nowrap;
    }
    .groupCandidateEmpty {
      padding: 60px 0;
      color: #c0c4cc;
      font-size: 13px;
      text-align: center;
    }
    .groupPanelFooter {
      flex: 0 0 64px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      padding: 0 20px;
      border-top: 1px solid #ebeef5;
      box-sizing: border-box;
      button {
        height: 34px;
        padding: 0 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
      }
      .secondary {
        border: 1px solid #dcdfe6;
        background: #fff;
        color: #606266;
      }
      .primary {
        border: 1px solid #409eff;
        background: #409eff;
        color: #fff;
      }
      .primary:disabled {
        border-color: #a0cfff;
        background: #a0cfff;
        cursor: not-allowed;
      }
    }
  }
  .groupPanelFade-enter-active,
  .groupPanelFade-leave-active {
    transition: opacity 0.18s ease;
    .groupPanel {
      transition: transform 0.18s ease;
    }
  }
  .groupPanelFade-enter,
  .groupPanelFade-leave-to {
    opacity: 0;
    .groupPanel {
      transform: translateX(24px);
    }
  }
  .imagePreviewOverlay {
    position: fixed;
    z-index: 3000;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.82);
    cursor: zoom-out;
    img {
      display: block;
      max-width: calc(100vw - 80px);
      max-height: calc(100vh - 80px);
      border-radius: 6px;
      object-fit: contain;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
      cursor: default;
      user-select: none;
    }
    .imagePreviewClose {
      position: fixed;
      top: 20px;
      right: 24px;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.16);
      color: #fff;
      cursor: pointer;
      font-size: 22px;
      line-height: 40px;
    }
    .imagePreviewClose:hover {
      background: rgba(255, 255, 255, 0.28);
    }
  }
  .imagePreviewFade-enter-active,
  .imagePreviewFade-leave-active {
    transition: opacity 0.18s ease;
  }
  .imagePreviewFade-enter,
  .imagePreviewFade-leave-to {
    opacity: 0;
  }
  .todoDefaultListName {
    color: #606266;
    font-size: 13px;
  }
  &.emailComposerMode {
    .right {
      .inputContainer {
        flex-basis: clamp(280px, 35vh, 400px);
        height: clamp(280px, 35vh, 400px);
      }
      .inputContent {
        height: calc(100% - 40px);
      }
    }
  }
}
</style>
<style lang="scss">
.messageQuoteTooltip {
  max-width: #{"min(520px, calc(100vw - 32px))"};
  .messageQuoteTooltipContent {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 240px;
    overflow-y: auto;
    strong {
      color: #d9ecff;
      font-size: 12px;
      font-weight: 600;
    }
    span {
      color: #fff;
      font-size: 13px;
      line-height: 1.5;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }
  }
}

.messageReceiptTooltip {
  max-width: #{"min(420px, calc(100vw - 32px))"};
  .messageReceiptTooltipContent {
    display: flex;
    flex-direction: column;
    gap: 8px;
    line-height: 1.5;
    section {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    strong {
      color: #d9ecff;
      font-size: 12px;
    }
    span {
      color: #fff;
      font-size: 12px;
      overflow-wrap: anywhere;
    }
    small {
      color: #a8abb2;
      font-size: 11px;
    }
  }
}
.linkedTodoDialog {
  .el-dialog__body {
    padding: 16px 24px 8px;
  }
  .linkedTodoLoading,
  .linkedTodoMissing {
    min-height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 14px;
  }
  .linkedTodoDetails {
    position: relative;
    .linkedTodoStatusRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin: 0 0 14px;
    }
    .linkedTodoStatus {
      flex: 0 0 auto;
      padding: 3px 8px;
      border: 1px solid #b7eb8f;
      border-radius: 3px;
      background: #f6ffed;
      color: #52c41a;
      font-size: 12px;
      &.isComplete {
        border-color: #dcdfe6;
        background: #f4f4f5;
        color: #909399;
      }
    }
    .linkedTodoStatusButton {
      display: inline-flex;
      height: 30px;
      align-items: center;
      justify-content: center;
      gap: 5px;
      padding: 0 10px;
      border: 1px solid #b3d8ff;
      border-radius: 4px;
      background: #ecf5ff;
      color: #409eff;
      cursor: pointer;
      font-family: inherit;
      font-size: 12px;
    }
    .linkedTodoStatusButton:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    .linkedTodoContent {
      min-height: 48px;
      margin: 0 0 20px;
      color: #303133;
      font-size: 16px;
      line-height: 1.6;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    dl {
      display: grid;
      grid-template-columns: 76px minmax(0, 1fr);
      gap: 12px 8px;
      margin: 0;
      color: #606266;
      font-size: 13px;
      line-height: 1.45;
    }
    dt { color: #909399; }
    dd { min-width: 0; margin: 0; overflow-wrap: anywhere; }
    .linkedTodoLabels { display: flex; flex-wrap: wrap; gap: 5px; }
    .linkedTodoLabels span {
      padding: 2px 6px;
      border-radius: 3px;
      background: #ecf5ff;
      color: #409eff;
    }
    .linkedTodoId { color: #909399; font-family: monospace; }
  }
}
.conversationTodoDrawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2100;
  display: flex;
  width: #{"min(480px, calc(100vw - 32px))"};
  flex-direction: column;
  background: #fff;
  box-shadow: -8px 0 28px rgba(0, 0, 0, 0.14);
  .conversationTodoDrawerBody {
    flex: 1 1 auto;
    min-height: 0;
    padding: 16px 20px 24px;
    overflow-y: auto;
  }
  header {
    display: flex;
    flex: 0 0 72px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0 20px;
    border-bottom: 1px solid #ebeef5;
  }
  header span {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
  }
  header strong {
    color: #303133;
    font-size: 18px;
    line-height: 1.2;
  }
  header small {
    overflow: hidden;
    color: #909399;
    font-size: 12px;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  header button {
    display: inline-flex;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #909399;
    cursor: pointer;
    font-size: 20px;
  }
  header button:hover {
    background: #f5f7fa;
    color: #606266;
  }
  .conversationTodosLoading,
  .conversationTodosEmpty {
    min-height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 14px;
  }
  .conversationTodoList {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .conversationTodoItem {
    padding: 12px 14px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fff;
  }
  .conversationTodoItemHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    color: #909399;
    font-size: 12px;
  }
  .conversationTodoHeaderActions {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
  }
  .conversationTodoStatus {
    padding: 3px 7px;
    border: 1px solid #b7eb8f;
    border-radius: 3px;
    background: #f6ffed;
    color: #52c41a;
    &.isComplete {
      border-color: #dcdfe6;
      background: #f4f4f5;
      color: #909399;
    }
  }
  .conversationTodoStatusButton {
    display: inline-flex;
    height: 26px;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 8px;
    border: 1px solid #b3d8ff;
    border-radius: 4px;
    background: #ecf5ff;
    color: #409eff;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
  }
  .conversationTodoStatusButton:hover:not(:disabled) {
    border-color: #409eff;
    background: #d9ecff;
  }
  .conversationTodoStatusButton:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  .conversationTodoItem p {
    margin: 9px 0 7px;
    color: #303133;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .conversationTodoMeta {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 12px;
    color: #909399;
    font-size: 12px;
    line-height: 1.4;
  }
}
.conversationTodoDrawerFade-enter-active,
.conversationTodoDrawerFade-leave-active {
  transition: opacity 0.18s ease;
  .conversationTodoDrawer { transition: transform 0.18s ease; }
}
.conversationTodoDrawerFade-enter,
.conversationTodoDrawerFade-leave-to {
  opacity: 0;
  .conversationTodoDrawer { transform: translateX(28px); }
}
.conversationFileDrawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2100;
  display: flex;
  width: #{"min(460px, calc(100vw - 32px))"};
  flex-direction: column;
  background: #fff;
  box-shadow: -8px 0 28px rgba(0, 0, 0, 0.14);
  .conversationFileDrawerBody {
    flex: 1 1 auto;
    min-height: 0;
    padding: 16px 18px 24px;
    overflow-y: auto;
  }
  header {
    display: flex;
    flex: 0 0 72px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0 20px;
    border-bottom: 1px solid #ebeef5;
  }
  header span {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
  }
  header strong {
    color: #303133;
    font-size: 18px;
    line-height: 1.2;
  }
  header small {
    overflow: hidden;
    color: #909399;
    font-size: 12px;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  header button {
    display: inline-flex;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #909399;
    cursor: pointer;
    font-size: 20px;
  }
  header button:hover {
    background: #f5f7fa;
    color: #606266;
  }
  .conversationFilesLoading,
  .conversationFilesEmpty {
    min-height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 14px;
  }
  .conversationFileList {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .conversationFileItem {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border: 1px solid #d8e5f4;
    border-left: 4px solid #409eff;
    border-radius: 8px;
    background: #f8fbff;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.08);
  }
  .conversationFileIcon {
    display: inline-flex;
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #e8f3ff;
    color: #1677d2;
    font-size: 22px;
  }
  .conversationFileInfo {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 4px;
    strong {
      overflow: hidden;
      color: #303133;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    small {
      overflow: hidden;
      color: #909399;
      font-size: 12px;
      line-height: 18px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .conversationFileActions {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    button {
      display: inline-flex;
      width: 32px;
      height: 32px;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 1px solid #b3d8ff;
      border-radius: 50%;
      background: #ecf5ff;
      color: #409eff;
      cursor: pointer;
      font-size: 16px;
    }
    button:hover:not(:disabled) {
      border-color: #409eff;
      background: #d9ecff;
    }
    button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
}
.conversationFileDrawerFade-enter-active,
.conversationFileDrawerFade-leave-active {
  transition: opacity 0.18s ease;
  .conversationFileDrawer { transition: transform 0.18s ease; }
}
.conversationFileDrawerFade-enter,
.conversationFileDrawerFade-leave-to {
  opacity: 0;
  .conversationFileDrawer { transform: translateX(28px); }
}
.chatReauthDialog {
  .el-dialog__body {
    padding: 18px 24px 8px;
  }
  .reauthDescription,
  .reauthMobileHint {
    margin: 0 0 18px;
    color: #606266;
    font-size: 13px;
    line-height: 1.55;
  }
  .reauthError {
    margin: 0;
    color: #f56c6c;
    font-size: 13px;
    line-height: 1.5;
  }
}
.chatContentContainer {
  .el-textarea__inner {
    height: 116px !important;
    border: none;
    padding-bottom: 34px;
  }
  &.emailComposerMode {
    .wechatEmailEditor {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .wechatEmailEditor .ql-toolbar {
      flex: 0 0 auto;
      border-color: #ebeef5;
      text-align: left;
    }
    .wechatEmailEditor .ql-container {
      flex: 1 1 auto;
      min-height: 0;
      height: auto !important;
      border: 0;
      font-family: inherit;
    }
    .wechatEmailEditor .ql-editor {
      padding: 16px 18px 54px;
      color: #303133;
      font-size: 16px;
      line-height: 1.6;
    }
    .wechatEmailEditor .ql-editor.ql-blank::before {
      right: 18px;
      left: 18px;
      color: #c0c4cc;
      font-style: normal;
    }
    .inputContent.hasAttachmentDraft .ql-editor {
      padding-top: 116px;
    }
    &:not(.emailMarkdownMode) {
      .inputContent.hasAttachmentDraft .attachmentDraftList {
        top: 52px;
      }
    }
  }
  &.emailMarkdownMode .wechatEmailEditor .ql-toolbar {
    display: none;
  }
  .inputContent.hasAttachmentDraft .el-textarea__inner {
    padding-top: 104px;
  }
  .inputContent.hasQuoteDraft .el-textarea__inner {
    padding-top: 58px;
  }
  .inputContent.hasQuoteDraft.hasAttachmentDraft .el-textarea__inner {
    padding-top: 104px;
  }
  .inputContent.hasVoiceInput .el-textarea__inner {
    padding-top: 58px;
  }
  .inputContent.hasVoiceInput.hasQuoteDraft .el-textarea__inner {
    padding-top: 104px;
  }
  .inputContent.hasVoiceInput .attachmentDraftList {
    top: 58px;
  }
  .inputContent.hasQuoteDraft .attachmentDraftList {
    top: 58px;
  }
  .inputContent.hasQuoteDraft .ql-editor {
    padding-top: 66px;
  }
  .inputContent.hasQuoteDraft.hasAttachmentDraft .ql-editor {
    padding-top: 122px;
  }
  .messageTodoDialog {
    .todoContentInput .el-textarea__inner {
      height: auto !important;
      min-height: 150px;
      padding: 10px 12px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      color: #303133;
      font-size: 14px;
      line-height: 1.5;
      background: #fff;
    }
    .todoContentInput .el-textarea__inner:focus {
      border-color: #409eff;
    }
  }
  .messageEditDialog {
    .el-textarea__inner {
      min-height: 150px;
      padding: 10px 12px;
      line-height: 1.55;
    }
    .messageEditHint {
      margin: 10px 0 0;
      color: #909399;
      font-size: 12px;
      line-height: 1.5;
    }
  }
  .userProfileDialog {
    .el-dialog__body {
      padding-top: 10px;
    }    .userProfileCard {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .userProfileHeader {
      display: flex;
      align-items: center;
      gap: 12px;
      strong,
      small {
        display: block;
      }
      strong {
        color: #303133;
        font-size: 18px;
        font-weight: 600;
      }
      small {
        margin-top: 4px;
        color: #909399;
        font-size: 13px;
      }
    }
    .userProfileAvatar {
      flex: 0 0 44px;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #ecf5ff;
      color: #409eff;
      font-size: 18px;
      font-weight: 600;
      line-height: 44px;
      text-align: center;
    }
    .userProfileDetails {
      display: grid;
      grid-template-columns: 76px minmax(0, 1fr);
      gap: 10px 14px;
      margin: 0;
      dt {
        color: #909399;
        font-size: 13px;
      }
      dd {
        min-width: 0;
        margin: 0;
        color: #303133;
        font-size: 13px;
        overflow-wrap: anywhere;
      }
    }
    .userProfileEmpty {
      color: #909399;
      font-size: 13px;
    }
  }
  .chatAskAIDialog {
    .el-dialog__body {
      padding: 18px 24px 8px;
    }
    .askAIQuote {
      display: flex;
      align-items: stretch;
      margin-bottom: 16px;
      padding: 8px 10px;
      border: 1px solid #ebeef5;
      border-radius: 5px;
      background: #f5f7fa;
      gap: 8px;
      .askAIQuoteBar {
        flex: 0 0 3px;
        border-radius: 3px;
        background: #6f55e8;
      }
      .askAIQuoteInfo {
        min-width: 0;
        strong,
        small {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        strong {
          color: #6f55e8;
          font-size: 12px;
          font-weight: 600;
          line-height: 18px;
          white-space: nowrap;
        }
        small {
          color: #606266;
          font-size: 12px;
          line-height: 18px;
          max-height: 54px;
          white-space: normal;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }
      }
    }
    .askAIHint {
      margin: 4px 0 0;
      color: #909399;
      font-size: 12px;
      line-height: 1.5;
    }
  }
}
</style>
